/* ============================================
   Team Portal — Shared JS
   Auth gate, API utilities, shared state
   ============================================ */

(function () {
  'use strict';

  const WORKER_BASE = 'https://rfq-worker.ryan-29b.workers.dev';

  /* ------------------------------------------
     Auth Manager (JWT tokens + legacy fallback)
     ------------------------------------------ */
  var _refreshPromise = null; // singleton to avoid concurrent refresh races

  const RepAuth = {
    SESSION_KEY: 'rep_portal_session',

    /** Get stored session.
        CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-12) — when BOTH stores hold a
        session for the SAME rep, prefer the one with the FRESHER tokens.
        Refresh tokens are single-use (rotated + deleted server-side), so with
        several portal tabs open, the first tab to refresh kills every other
        tab's snapshot — the new pair lives in localStorage. The old
        sessionStorage-always preference stranded long-lived tabs on dead
        tokens ("Save failed: Not authenticated" in the QW, RW-1178-A).
        Different-rep / impersonation snapshots keep the tab's own copy. */
    getSession() {
      try {
        let rawS = sessionStorage.getItem(this.SESSION_KEY);
        let rawL = localStorage.getItem(this.SESSION_KEY);
        let chosen = null;
        if (rawS && rawL && rawS !== rawL) {
          try {
            var sS = JSON.parse(rawS), sL = JSON.parse(rawL);
            if (sS && sL && sS.code === sL.code && !sS.impersonating && !sL.impersonating) {
              chosen = ((sL.tokenExpiresAt || 0) > (sS.tokenExpiresAt || 0)) ? rawL : rawS;
            } else {
              chosen = rawS; // different rep or impersonating — keep this tab's identity
            }
          } catch (e) { chosen = rawS; }
        } else {
          chosen = rawS || rawL;
        }
        if (!chosen) return null;
        sessionStorage.setItem(this.SESSION_KEY, chosen);
        if (!localStorage.getItem(this.SESSION_KEY)) localStorage.setItem(this.SESSION_KEY, chosen);
        return JSON.parse(chosen);
      } catch {
        return null;
      }
    },

    /** Save session after successful auth.
        Detects login / rep change (vs a plain token refresh where session.code
        is unchanged) and resets the drawer's open/closed state so every fresh
        login starts with the drawer closed. */
    saveSession(data) {
      try {
        var prior = sessionStorage.getItem(this.SESSION_KEY) || localStorage.getItem(this.SESSION_KEY);
        var priorCode = prior ? (JSON.parse(prior) || {}).code : null;
        if (!priorCode || priorCode !== (data && data.code)) {
          localStorage.removeItem('rep_portal_drawer_open');
        }
      } catch (e) {}
      const json = JSON.stringify(data);
      this._setGuarded(sessionStorage, this.SESSION_KEY, json);
      this._setGuarded(localStorage, this.SESSION_KEY, json);
    },

    /** setItem that survives a FULL storage. The shared portal origin can be maxed out
        by the Room Designer autosave (it stores whole designs incl. base64 images under
        rd_autosave*), so a plain setItem of even the tiny session blob can throw
        QuotaExceededError — which previously broke login/impersonation (impersonation
        writes the session AND stashes the admin session). The session is critical, so on
        a quota error we evict large, server-recoverable keys and retry; if it still fails
        we keep the per-tab sessionStorage copy rather than throwing.
        CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-15). */
    _setGuarded(store, key, val) {
      try { store.setItem(key, val); return true; }
      catch (e) {
        // Pass 1 — evict the big, server-recoverable hogs (RD autosave designs, etc.).
        try {
          var kill = [];
          for (var i = 0; i < store.length; i++) {
            var k = store.key(i);
            if (k && k !== key && (k.indexOf('rd_autosave') !== -1 || k === 'rep_workstation_import' || k === 'custom_quote_submission')) kill.push(k);
          }
          kill.forEach(function (kk) { try { store.removeItem(kk); } catch (_) {} });
          store.setItem(key, val);
          return true;
        } catch (e2) { /* still full — fall through */ }
        // Pass 2 — storage is still full; the session is critical, so clear EVERYTHING
        // except the session keys and retry. Other entries (drafts, drawer state, RD
        // autosave) are local conveniences and recoverable from the server.
        try {
          var keep = {}; keep[key] = 1; keep[this.SESSION_KEY] = 1; keep['rep_portal_admin_session'] = 1;
          var kill2 = [];
          for (var j = 0; j < store.length; j++) { var k2 = store.key(j); if (k2 && !keep[k2]) kill2.push(k2); }
          kill2.forEach(function (kk) { try { store.removeItem(kk); } catch (_) {} });
          store.setItem(key, val);
          return true;
        } catch (e3) { return false; }
      }
    },

    /** Clear session (logout) — revokes refresh token server-side */
    clearSession() {
      var session = this.getSession();
      if (session && session.refreshToken) {
        fetch(WORKER_BASE + '/rep-logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: session.refreshToken })
        }).catch(function() {});
      }
      sessionStorage.removeItem(this.SESSION_KEY);
      localStorage.removeItem(this.SESSION_KEY);
      localStorage.removeItem('rep_portal_admin_session');
    },

    /** Admin impersonation: stash current session, swap to target rep's JWT */
    async startImpersonation(targetCode) {
      var current = this.getSession();
      if (!current || !current.accessToken) throw new Error('Not authenticated');
      // Stash original admin session so Stop button can restore it. Skip if
      // already impersonating — preserves the original admin session, not
      // the current impersonation.
      if (!current.impersonating) {
        this._setGuarded(localStorage, 'rep_portal_admin_session', JSON.stringify(current));
      }
      var resp = await fetch(WORKER_BASE + '/admin/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + current.accessToken,
        },
        body: JSON.stringify({ code: targetCode }),
      });
      if (!resp.ok) {
        var err = await resp.json().catch(function() { return {}; });
        throw new Error(err.error || ('Impersonation failed (' + resp.status + ')'));
      }
      var data = await resp.json();
      this.saveSession({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        refreshToken: null,
        name: data.rep.name,
        fullName: data.rep.fullName,
        email: data.rep.email,
        phone: data.rep.phone || '', // CUSTOM_RFQPORTAL_BY_CLAUDE — worker returns it; without this the Account Info phone shows blank while impersonating
        code: data.rep.code,
        initials: data.rep.initials,
        role: data.rep.role,
        admin: data.rep.admin,
        headshotUrl: data.rep.headshotUrl || '',
        impersonating: true,
        impersonatedBy: data.impersonatedBy,
      });
      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-11) — agency targets land on
         their own surface. Reloading the current page (hub, dashboard…) as
         an agency session fires that page's data loaders, which the worker
         fail-closes with 401 → forced logout. The showPortalContent guard
         is the safety net; routing directly avoids the double navigation. */
      if ((data.rep.role || '') === 'agency') {
        window.location.href = externalHome(RepAuth.getSession()); // CUSTOM_PORTALACCESS_BY_CLAUDE — their first granted page
        return;
      }
      /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */
      window.location.reload();
    },

    /** Stop impersonation: restore the stashed admin session and reload. */
    stopImpersonation() {
      var stash = localStorage.getItem('rep_portal_admin_session');
      if (!stash) {
        // Lost the stash somehow — full logout to recover.
        this.clearSession();
        window.location.href = '/pages/sales-team-portal';
        return;
      }
      this._setGuarded(sessionStorage, this.SESSION_KEY, stash);
      this._setGuarded(localStorage, this.SESSION_KEY, stash);
      localStorage.removeItem('rep_portal_admin_session');
      window.location.reload();
    },

    /** Solve a proof-of-work challenge (find nonce where SHA-256(challenge+nonce) has N leading zero bits) */
    async _solvePow(challenge, difficulty) {
      var target = difficulty || 16; // 16 bits = 2 leading zero bytes
      for (var nonce = 0; nonce < 1000000; nonce++) {
        var input = challenge + String(nonce);
        var buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
        var bytes = new Uint8Array(buf);
        if (target >= 16 && bytes[0] === 0 && bytes[1] === 0) return nonce;
        if (target >= 8 && target < 16 && bytes[0] === 0) return nonce;
      }
      throw new Error('Could not solve challenge');
    },

    /** Validate email + password against the worker. Returns session data with tokens. */
    async validate(email, password) {
      var loginBody = { email: email.trim().toLowerCase(), password: password.trim() };
      var resp = await fetch(WORKER_BASE + '/rep-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginBody)
      });
      // Handle proof-of-work challenge (too many failed attempts)
      if (resp.status === 429) {
        var powData = await resp.json().catch(function() { return {}; });
        if (powData.pow && powData.pow.challenge) {
          var nonce = await this._solvePow(powData.pow.challenge, powData.pow.difficulty);
          loginBody.pow_nonce = nonce;
          resp = await fetch(WORKER_BASE + '/rep-auth', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(loginBody)
          });
        }
      }
      if (!resp.ok) {
        var err = await resp.json().catch(function() { return { error: 'Invalid credentials' }; });
        throw new Error(err.error || 'Invalid email or password');
      }
      var data = await resp.json();
      // 2FA required — return the response for the auth gate to handle
      if (data.requires2fa) {
        return { requires2fa: true, tempToken: data.tempToken };
      }
      // JWT response: { accessToken, refreshToken, expiresIn, rep: {...} }
      if (data.accessToken && data.rep) {
        return {
          ...data.rep,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          tokenExpiresAt: Date.now() + (data.expiresIn * 1000),
        };
      }
      return data;
    },

    /** Verify 2FA code (TOTP or recovery) and get full session tokens */
    async verify2fa(tempToken, code, recoveryCode) {
      var body = { tempToken: tempToken };
      if (recoveryCode) { body.recoveryCode = recoveryCode; }
      else { body.code = code; }
      var resp = await fetch(WORKER_BASE + '/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        var err = await resp.json().catch(function() { return { error: 'Verification failed' }; });
        throw new Error(err.error || 'Invalid verification code');
      }
      var data = await resp.json();
      if (data.accessToken && data.rep) {
        return {
          ...data.rep,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          tokenExpiresAt: Date.now() + (data.expiresIn * 1000),
        };
      }
      return data;
    },

    /** Get the rep code for display / legacy use */
    getCode() {
      const session = this.getSession();
      return session ? session.code : null;
    },

    /** Get a valid access token, auto-refreshing if within 2 min of expiry */
    async getAccessToken() {
      var session = this.getSession();
      if (!session) return null;
      if (!session.accessToken) return null;
      // If token expires in more than 2 minutes, use it
      if (session.tokenExpiresAt && (session.tokenExpiresAt - Date.now()) > 120000) {
        return session.accessToken;
      }
      // Token is expiring soon or already expired — refresh
      if (session.refreshToken) {
        try {
          return await this.refreshTokens();
        } catch (e) {
          return session.accessToken; // try the current token anyway
        }
      }
      return session.accessToken;
    },

    /** Exchange refresh token for new access + refresh tokens */
    async refreshTokens() {
      // Deduplicate concurrent refresh calls
      if (_refreshPromise) return _refreshPromise;
      var self = this;
      _refreshPromise = (async function() {
        try {
          var session = self.getSession();
          if (!session || !session.refreshToken) throw new Error('No refresh token');
          var resp = await fetch(WORKER_BASE + '/rep-refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: session.refreshToken })
          });
          if (!resp.ok) throw new Error('Refresh failed');
          var data = await resp.json();
          session.accessToken = data.accessToken;
          session.refreshToken = data.refreshToken;
          session.tokenExpiresAt = Date.now() + (data.expiresIn * 1000);
          self.saveSession(session);
          return data.accessToken;
        } finally {
          _refreshPromise = null;
        }
      })();
      return _refreshPromise;
    }
  };

  /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-11) — agency surface allowlist +
     synchronous early redirect. Agency tokens are fail-closed server-side
     (401 from every rfq-worker endpoint outside the auth/profile allowlist),
     so an agency session on any data page (hub, dashboard, workstation…)
     means that page's loaders 401 → logout cascade. This runs at SCRIPT-EVAL
     time — before any deferred page script registers its DOMContentLoaded
     loaders — so the redirect always wins the race. */
  /* CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-09-26) — THE AGENCY ALLOWLIST IS NOW PER PERSON.
     An external user (role 'agency': Malt Media, or a freelancer) may open a page only when the
     MODULE it belongs to is switched on for THAT person in Settings > Admin: Module Visibility.
     The answer rides on the access token as the `mods` claim (rfq-worker lib/external-access.js),
     so this check is synchronous and needs no fetch — it still runs at SCRIPT-EVAL time, before any
     page loader, which is the whole reason this guard exists (see the history below).
     A token with NO `mods` claim was minted before this shipped and gets exactly the four modules
     agency users always had. Every worker enforces the same claim; this is presentation.
     ⚠ A NEW PAGE AN EXTERNAL USER MAY SEE: add it to EXTERNAL_PAGE_MODULE under its module key.
     (History: the old hard-coded AGENCY_PAGES bounced Gus off Budget Plan, Blog, Social and Agency
     Comp in turn, each time a page was built without being added here.) */
  var EXTERNAL_PAGE_MODULE = {
    '/pages/rep-paid-ads': 'marketingops',
    '/pages/rep-marketing-budget': 'marketingops',
    '/pages/rep-agency-comp': 'marketingops',
    '/pages/rep-ads-review': 'adsreview',
    '/pages/blog-bot': 'blogbot',            // the handle, NOT the section name rep-quote-blog-bot
    '/pages/rep-social-bot': 'socialbot',
    '/pages/rep-content-library': 'contentlibrary',
    '/pages/rep-gym-designer': 'gymlayout',   // Ryan 2026-10-02: a freelancer designs + saves his own plans
    '/pages/rep-weight-designer': 'weightdesigner', // Ryan 2026-10-30: his own weight designs + their pictures
  };
  var EXTERNAL_OPEN_PAGES = ['/pages/rep-quote-settings'];   // their own account page, always
  var LEGACY_AGENCY_MODS = ['marketingops', 'blogbot', 'socialbot', 'contentlibrary'];
  function externalModsFor(sess) {
    try {
      var part = String((sess && sess.accessToken) || '').split('.')[1];
      if (part) {
        part = part.replace(/-/g, '+').replace(/_/g, '/');
        while (part.length % 4) part += '=';
        var claims = JSON.parse(atob(part));
        if (Array.isArray(claims.mods)) return claims.mods;
      }
    } catch (e) {}
    return LEGACY_AGENCY_MODS.slice();
  }
  function agencyPageAllowed(pathname, sess) {
    var p = String(pathname || '').replace(/\/$/, '');
    if (EXTERNAL_OPEN_PAGES.indexOf(p) !== -1) return true;
    var mod = EXTERNAL_PAGE_MODULE[p];
    return !!mod && externalModsFor(sess || RepAuth.getSession()).indexOf(mod) !== -1;
  }
  /* Where an external user lands: the first page of theirs, in the drawer's own order, else their
     account page (a user with nothing switched on yet still has somewhere to stand). */
  function externalHome(sess) {
    var mods = externalModsFor(sess || RepAuth.getSession());
    var order = ['/pages/rep-paid-ads', '/pages/blog-bot', '/pages/rep-social-bot', '/pages/rep-content-library', '/pages/rep-ads-review', '/pages/rep-gym-designer', '/pages/rep-weight-designer'];
    for (var i = 0; i < order.length; i++) {
      if (mods.indexOf(EXTERNAL_PAGE_MODULE[order[i]]) !== -1) return order[i];
    }
    return EXTERNAL_OPEN_PAGES[0];
  }
  (function earlyAgencyRedirect() {
    var sess = RepAuth.getSession();
    if (sess && (sess.role || '') === 'agency' && !agencyPageAllowed(window.location.pathname, sess)) {
      window.location.replace(externalHome(sess));
    }
  })();
  /* END CUSTOM_PORTALACCESS_BY_CLAUDE */
  /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

  /* ------------------------------------------
     API Helper (JWT Bearer auth with refresh retry)
     ------------------------------------------ */
  const DEAL_LIST_IDB_MAX = 8; // CUSTOM_DDKEEPPLACE_BY_CLAUDE - board views kept in IndexedDB (each up to ~10 MB)
  const SAVED_COPY_MAX = 30; // CUSTOM_MARKETINGOPS_BY_CLAUDE - page saved copies (savedCopyGet/Put), each a few hundred KB

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-11-02) — a 401 is not always an expired session.
     Settings loads ~60 admin-only switch panels for every user, and a few admin routes refuse a
     non-admin with 401 rather than 403. RepAPI read every 401 as "session dead": a CFA (Phill) was
     logged out the moment he opened Settings, and an admin viewing as him was thrown back to their
     own account — then, with several 401s in flight, the NEXT one found the restored admin session
     and logged the admin out entirely (the race Ryan hit). Now: a 401 that survives a successful
     refresh, or arrives while an impersonation token is still unexpired, is a REFUSAL — it throws
     like any other error and never ends a session. Only a token that is genuinely expired and cannot
     be refreshed ends it, and once one bail has started, later 401s cannot stack a second one. */
  var _sessionBailStarted = false;
  function _jwtStillLive(token) {
    try {
      var part = String(token || '').split('.')[1];
      if (!part) return false;
      var payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return !!payload.exp && (payload.exp * 1000) > (Date.now() + 5000);
    } catch (e) { return false; }
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  const RepAPI = {
    async _request(fullUrl, options = {}) {
      var token = await RepAuth.getAccessToken();
      if (!token) throw new Error('Not authenticated');

      var headers = { ...(options.headers || {}) };
      // Default JSON Content-Type except for FormData bodies — the browser
      // sets the multipart boundary automatically and an explicit
      // 'multipart/form-data' header without the boundary breaks the upload.
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      headers['Authorization'] = 'Bearer ' + token;

      var resp = await fetch(fullUrl, { ...options, headers: headers });

      /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-11) — 401 while impersonating:
         restore the stashed admin session instead of a full logout.
         Impersonation tokens carry no refresh token, and clearSession()
         deletes the admin stash too — so before this, ANY 401 during
         impersonation (agency fail-closed endpoints, 30-min token expiry)
         logged the ADMIN out entirely. stopImpersonation restores the
         stash + reloads as the admin. */
      function bailFromExpiredSession() {
        if (_sessionBailStarted) throw new Error('Session ending — reloading'); // CUSTOM_RFQPORTAL_BY_CLAUDE (2026-11-02) — one bail per page, never a second on top
        _sessionBailStarted = true;
        var sess = RepAuth.getSession();
        if (sess && sess.impersonating && localStorage.getItem('rep_portal_admin_session')) {
          RepAuth.stopImpersonation();
          throw new Error('Impersonation ended — returning to your account');
        }
        // Agency 401 off their own pages = the server's fail-closed
        // authorization, NOT an expired session. Redirect to their surface
        // without clearing — nuking here is what force-logged agency users
        // out. On an agency-allowed page, a 401 is a genuinely dead session
        // and falls through to the normal clear.
        if (sess && (sess.role || '') === 'agency' && !agencyPageAllowed(window.location.pathname, sess)) {
          window.location.replace(externalHome(sess));
          throw new Error('Not available for agency accounts');
        }
        RepAuth.clearSession();
        window.location.reload();
        throw new Error('Session expired');
      }
      /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

      // On 401 with JWT: try one refresh + retry
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (2026-11-02) — see _sessionBailStarted above. An impersonation token
         has no refresh token, so while it is unexpired a 401 can only be a refusal: skip straight to the
         ordinary error. After a SUCCESSFUL refresh the retry carries a brand-new token, so a 401 on it is
         a refusal too. Only "expired and could not refresh" ends the session. */
      var _sessNow = RepAuth.getSession();
      if (resp.status === 401 && token && !(_sessNow && _sessNow.impersonating && _jwtStillLive(token))) {
        var _refreshed = false;
        try {
          var newToken = await RepAuth.refreshTokens();
          _refreshed = true;
          headers['Authorization'] = 'Bearer ' + newToken;
          resp = await fetch(fullUrl, { ...options, headers: headers });
        } catch (e) {
          if (!_refreshed) bailFromExpiredSession();
          throw e; // the retry fetch itself failed (network) — an ordinary error, not a dead session
        }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

      var data = await resp.json().catch(function() { return { error: 'Request failed' }; });

      if (!resp.ok) {
        var err = new Error(data.error || 'Request failed (' + resp.status + ')');
        err.status = resp.status;
        err.data = data;
        throw err;
      }

      return data;
    },

    fetch(path, options = {}) {
      return this._request(WORKER_BASE + path, options);
    },

    /** Call a different worker using the same auth/refresh flow. */
    fetchExternal(baseUrl, path, options = {}) {
      return this._request(baseUrl + path, options);
    },

    get(path) {
      return this.fetch(path, { method: 'GET' });
    },

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-26) — drop the Deal Dashboard's cached deal
       LISTS. Lives here, next to the `rqd_mutated_at` stamp below, because the two are halves
       of one idea ("what I just did makes the cached board wrong") and because the caller is
       not always the DD: the Quote Workstation can mint a pending and the rep then NAVIGATES to
       the dashboard, where a fresh page load reads a sessionStorage entry written before that
       deal existed. sessionStorage is per-tab and survives navigation, so the staleness follows
       them across the portal.

       ⚠️ ONE DEFINITION OF THE KEY PREFIX. `rqd-swr:` is written in two places in
       dashboard-main.js (the fetch and the sibling-tab prewarm); a second hand-rolled sweep in
       another file would be a third opinion about what the cache is called.

       ⛔ Call it when the set of ROWS changes — a create, a delete, a conversion. Not on every
       POST: a note or a status flip patches its row in place and already feels instant, so
       clearing there would make the common action slower to fix the rare one. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-28 PM11) — ⛔ THE SWR CACHE HAD NO EVICTION, SO
       THE FIRST VIEW LOADED WON THE SPACE FOREVER.
       ─────────────────────────────────────────────────────────────────────────────────────
       Both write sites in dashboard-main.js called `sessionStorage.setItem` inside a bare
       `catch (_) {}`. sessionStorage is ONE ~5 MB budget for the whole origin shared across
       every cached view (offline, online, per-rep, per-filter), and the RAM layer next to it
       caps at 16 and evicts the oldest (`_memQuotesSet`) — this layer evicted nothing. So
       whichever view happened to load first kept its slot and every later view silently failed
       to cache for the rest of the tab's life.

       ⚠️ MEASURED ON RYAN'S BOARD, and it is not theoretical: quota 5,114 KB, the ONLINE entry
       holding 1,687 KB of it, leaving 3,424 KB — and the OFFLINE payload needs 4,909 KB. Online
       had simply been fetched first. The offline tab, the one reps actually live in, had never
       once been cached, so every offline reload started cold. Evicting the other `rqd-swr:`
       keys restores 5,111 KB of headroom and it fits.

       ⛔ THE PAYLOAD DIET WAS A PREREQUISITE, NOT AN ALTERNATIVE. At its previous 5,907 KB the
       offline payload exceeded the ENTIRE 5,114 KB quota — eviction alone could never have made
       it fit, and a diet alone could not either while the online entry squatted on the budget.
       Neither change works without the other; margin today is 202 KB.

       Evicts OLDEST-FIRST by the stored `at`, retrying after each removal, so a busy rep loses
       the view they visited longest ago rather than the one in front of them. Own stale copy
       goes first — replacing a key still needs room for both values briefly.

       ⚠️ A GENUINE FAILURE IS REPORTED, NOT SWALLOWED. The bare catch is why this went unseen
       for months: a cache that never retains anything looks exactly like one that is merely
       cold. Returns a status and warns when even an empty store cannot hold the payload, which
       is the signal that the payload has outgrown the budget again. */
    /* CUSTOM_DDKEEPPLACE_BY_CLAUDE (Ryan 2026-10-22: "every time I jump into a quote in the QW, when I return to the
       DD it's slow to load again, like I wasn't just in there") - ⛔ THE BOARD COPY LIVES IN INDEXEDDB NOW.
       ─────────────────────────────────────────────────────────────────────────────────────
       sessionStorage is ~5 MB for the whole origin and per-tab. The offline board passed that (4.9 MB at 420 deals in
       August, ~728 deals now), so the write below answered 'quota' on every load and each return from the Workstation
       started cold; a second tab never had a copy at all. IndexedDB (db `rqd-board`, store `swr`) holds hundreds of MB,
       is shared by every tab and survives closing one. Same `rqd-swr:` keys, same JSON text, same 6-hour age limit.
       At most DEAL_LIST_IDB_MAX views, the oldest dropped. No IndexedDB (private mode, an error) = the sessionStorage
       path below, exactly as before. */
    _dlDbPromise: null,
    _dlDb() {
      if (this._dlDbPromise) return this._dlDbPromise;
      var self = this;
      this._dlDbPromise = new Promise(function (resolve, reject) {
        try {
          if (!window.indexedDB) { reject(new Error('no-indexeddb')); return; }
          var req = window.indexedDB.open('rqd-board', 1);
          req.onupgradeneeded = function () {
            var st = req.result.createObjectStore('swr', { keyPath: 'k' });
            st.createIndex('at', 'at');
          };
          req.onsuccess = function () { resolve(req.result); };
          req.onerror = function () { reject(req.error || new Error('idb-open')); };
          req.onblocked = function () { reject(new Error('idb-blocked')); };
        } catch (e) { reject(e); }
      });
      this._dlDbPromise.catch(function () { self._dlNoIdb = true; });
      return this._dlDbPromise;
    },
    /* ⛔ A CLEAR MUST HOLD EVEN IF THE PAGE LEAVES MID-DELETE. The Workstation clears and then navigates; an IndexedDB
       delete still running at unload can be dropped. So the clear also stamps localStorage (synchronous), and a copy
       written before that stamp is never served. */
    _dlClearedAt() {
      try { return parseInt(localStorage.getItem('rqd-swr-cleared-at') || '0', 10) || 0; } catch (_) { return 0; }
    },
    _dlUsable(at) {
      return !!at && (Date.now() - at < 21600000) && at > this._dlClearedAt();
    },
    /** The saved copy of one board view, `{at, data}`, or null. IndexedDB first, then a sessionStorage copy. Never throws. */
    async dealListGet(key) {
      if (!this._dlNoIdb) {
        try {
          var db = await this._dlDb();
          var rec = await new Promise(function (resolve, reject) {
            var r = db.transaction('swr', 'readonly').objectStore('swr').get(key);
            r.onsuccess = function () { resolve(r.result || null); };
            r.onerror = function () { reject(r.error); };
          });
          if (rec && this._dlUsable(rec.at)) {
            var hit = JSON.parse(rec.v);
            if (hit && hit.data) return hit;
          }
        } catch (_) { /* fall through to the sessionStorage copy */ }
      }
      try {
        var s = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (s && this._dlUsable(s.at) && s.data) return s;
      } catch (_) {}
      return null;
    },
    /** Rewrite every saved view in place (a row patch), keeping each one's age. Best effort, never throws. */
    async dealListPatchAll(apply) {
      if (this._dlNoIdb) return;
      try {
        var db = await this._dlDb();
        await new Promise(function (resolve) {
          var tx = db.transaction('swr', 'readwrite');
          var cur = tx.objectStore('swr').openCursor();
          cur.onsuccess = function () {
            var c = cur.result;
            if (!c) return;
            try {
              var entry = JSON.parse(c.value.v);
              if (entry && entry.data) { apply(entry.data.quotes); c.update({ k: c.value.k, at: c.value.at, v: JSON.stringify(entry) }); }
            } catch (_) { /* a corrupt entry is left for its own age to expire */ }
            c.continue();
          };
          tx.oncomplete = resolve; tx.onerror = resolve; tx.onabort = resolve;
        });
      } catch (_) {}
    },
    cacheDealList(key, value) {
      if (this._dlNoIdb) return this._cacheDealListSession(key, value);
      var self = this;
      var at = Date.now();
      this._dlDb().then(function (db) {
        return new Promise(function (resolve, reject) {
          var tx = db.transaction('swr', 'readwrite');
          var st = tx.objectStore('swr');
          st.put({ k: key, at: at, v: value });
          /* Oldest views out once there are more than the cap. A key cursor on the `at` index reads no values. */
          var cnt = st.count();
          cnt.onsuccess = function () {
            var extra = cnt.result - DEAL_LIST_IDB_MAX;
            if (extra <= 0) return;
            var kc = st.index('at').openKeyCursor();
            kc.onsuccess = function () {
              var c = kc.result;
              if (!c || extra <= 0) return;
              if (c.primaryKey !== key) { st.delete(c.primaryKey); extra--; }
              c.continue();
            };
          };
          tx.oncomplete = function () { resolve(); };
          tx.onerror = function () { reject(tx.error); };
          tx.onabort = function () { reject(tx.error || new Error('idb-abort')); };
        });
      }).then(function () {
        self.dealListCacheStatus = 'idb-stored';
        /* The old sessionStorage copy of this view is now the staler one; drop it so the fallback read cannot prefer it. */
        try { sessionStorage.removeItem(key); } catch (_) {}
      }, function () {
        self._dlNoIdb = true;
        self._cacheDealListSession(key, value);
      });
      return 'idb-pending';
    },
    /* END CUSTOM_DDKEEPPLACE_BY_CLAUDE */

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-10-23, Marketing Ops speed step 4) - A PAGE'S SAVED COPY.
       The DD board's pattern above for any page: the last answer is painted at once and the page asks the server
       again behind it. Paid Ads and Budget Plan use it. Its own IndexedDB db (`rp-saved`, store `copies`), so a
       page copy can never push a DD board view out of DEAL_LIST_IDB_MAX; at most SAVED_COPY_MAX copies, oldest
       dropped. Stored as JSON TEXT, like the DD board: Ryan's first live read of a structured-clone Paid Ads
       bundle took 1.5 s, and a string comes back from IndexedDB far faster than a deep object graph.
       ⛔ Keyed by the signed-in identity (code + admin + impersonation), so a shared computer never paints one
       person's numbers for another. No IndexedDB (private mode, an error) = no saved copy: the page loads as it
       always did. Never throws. */
    _scDbPromise: null,
    _scDb() {
      if (this._scDbPromise) return this._scDbPromise;
      this._scDbPromise = new Promise(function (resolve, reject) {
        try {
          if (!window.indexedDB) { reject(new Error('no-indexeddb')); return; }
          var req = window.indexedDB.open('rp-saved', 1);
          req.onupgradeneeded = function () {
            var st = req.result.createObjectStore('copies', { keyPath: 'k' });
            st.createIndex('at', 'at');
          };
          req.onsuccess = function () { resolve(req.result); };
          req.onerror = function () { reject(req.error || new Error('idb-open')); };
          req.onblocked = function () { reject(new Error('idb-blocked')); };
        } catch (e) { reject(e); }
      });
      return this._scDbPromise;
    },
    _scKey(key) {
      var s = RepAuth.getSession() || {};
      return [s.code || '?', s.admin ? 'a' : 'u', s.impersonating ? 'i' : '', key].join('|');
    },
    /** The saved copy `{at, data}` for `key`, or null when absent or older than `maxAgeMs`. Never throws.
        Settles within 10 s whatever the database does. ⛔ Callers must NOT await it ahead of their fresh
        request: paint from it when it arrives, if the fresh answer has not already. */
    savedCopyGet(key, maxAgeMs) {
      var self = this;
      return Promise.race([
        self._savedCopyRead(key, maxAgeMs),
        new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 10000); }),
      ]);
    },
    async _savedCopyRead(key, maxAgeMs) {
      try {
        var db = await this._scDb();
        var k = this._scKey(key);
        var rec = await new Promise(function (resolve, reject) {
          var r = db.transaction('copies', 'readonly').objectStore('copies').get(k);
          r.onsuccess = function () { resolve(r.result || null); };
          r.onerror = function () { reject(r.error); };
        });
        if (!rec || !rec.at || !(Date.now() - rec.at < maxAgeMs)) return null;
        var data = (typeof rec.v === 'string') ? JSON.parse(rec.v) : rec.data; // rec.data: the first day's structured copies
        return data == null ? null : { at: rec.at, data: data };
      } catch (_) { return null; }
    },
    /** Save `data` as the copy for `key` (best effort, never throws, returns nothing). The text is made
        once the database is open, so it never sits in front of the caller's own render. */
    savedCopyPut(key, data) {
      var k = this._scKey(key);
      var at = Date.now();
      this._scDb().then(function (db) {
        var v = JSON.stringify(data);
        var tx = db.transaction('copies', 'readwrite');
        var st = tx.objectStore('copies');
        st.put({ k: k, at: at, v: v });
        var cnt = st.count();
        cnt.onsuccess = function () {
          var extra = cnt.result - SAVED_COPY_MAX;
          if (extra <= 0) return;
          var kc = st.index('at').openKeyCursor();
          kc.onsuccess = function () {
            var c = kc.result;
            if (!c || extra <= 0) return;
            if (c.primaryKey !== k) { st.delete(c.primaryKey); extra--; }
            c.continue();
          };
        };
      }).catch(function () { /* no saved copy this time */ });
    },
    /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

    _cacheDealListSession(key, value) {
      var _set = function () { sessionStorage.setItem(key, value); };
      try { _set(); this.dealListCacheStatus = 'stored'; return 'stored'; } catch (e) {}
      /* ⛔ EVICTION MUST BE REVERSIBLE (Ryan 2026-08-28 PM12). The first cut evicted eagerly and
         only then discovered the value still would not fit — so a view too big to EVER cache
         (measured live: a 10,207 KB board against a 5,114 KB quota) destroyed the perfectly good
         entries it displaced and cached nothing in their place. Net effect: strictly worse than
         no eviction at all, which is what shipped and what Ryan hit within minutes.
         Everything removed is held and PUT BACK if the write ultimately fails. Freeing space we
         then do not use is pure loss, and "it can't fit" is only knowable by trying. */
      var removed = [];
      /* CUSTOM_DDREFRESHSTALE_BY_CLAUDE (Ryan 2026-10-01, RW-1469-A) — ⛔ NEVER PUT BACK THIS KEY'S
         OWN OLD COPY. The value we failed to write is the FRESHER one; restoring the old one made
         every reload paint a snapshot that could never be replaced, for up to 6 hours. A view that
         does not fit starts cold instead, which is slower but true. Other views are still restored. */
      var _restore = function () {
        for (var r = 0; r < removed.length; r++) {
          if (removed[r].k === key) continue;
          try { sessionStorage.setItem(removed[r].k, removed[r].v); } catch (_) { /* space we just freed */ }
        }
      };
      try {
        // our own stale copy first — replacing a key still needs room for both values briefly
        try {
          var mine = sessionStorage.getItem(key);
          if (mine !== null) { removed.push({ k: key, v: mine }); sessionStorage.removeItem(key); }
          _set(); this.dealListCacheStatus = 'stored'; return 'stored';
        } catch (e) {}
        var others = [];
        for (var i = 0; i < sessionStorage.length; i++) {
          var k = sessionStorage.key(i);
          if (!k || k.indexOf('rqd-swr:') !== 0 || k === key) continue;
          var at = 0;
          try { at = (JSON.parse(sessionStorage.getItem(k)) || {}).at || 0; } catch (_) {}
          others.push({ k: k, at: at });
        }
        others.sort(function (a, b) { return a.at - b.at; });   // oldest first
        for (var j = 0; j < others.length; j++) {
          try {
            var ov = sessionStorage.getItem(others[j].k);
            if (ov === null) continue;
            removed.push({ k: others[j].k, v: ov });
            sessionStorage.removeItem(others[j].k);
          } catch (_) { continue; }
          try { _set(); this.dealListCacheStatus = 'stored-after-evict'; return 'stored-after-evict'; } catch (_) {}
        }
      } catch (_) { /* private mode — fall through */ }
      _restore();
      this.dealListCacheStatus = 'quota';
      try {
        console.warn('[RepPortal] deal-list cache: ' + Math.round((value || '').length / 1024) +
                     ' KB does not fit sessionStorage even with every other view evicted — ' +
                     'its stale copy was dropped, reloads of this view start cold. key=' + key);
      } catch (_) {}
      return 'quota';
    },

    invalidateDealList() {
      /* CUSTOM_DDKEEPPLACE_BY_CLAUDE - the IndexedDB copies go too: the stamp first (synchronous, survives a navigation
         that cuts the delete short), then the delete itself. */
      try { localStorage.setItem('rqd-swr-cleared-at', String(Date.now())); } catch (_) {}
      try {
        this._dlDb().then(function (db) { db.transaction('swr', 'readwrite').objectStore('swr').clear(); }).catch(function () {});
      } catch (_) {}
      try {
        const doomed = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.indexOf('rqd-swr:') === 0) doomed.push(k);
        }
        doomed.forEach((k) => sessionStorage.removeItem(k));
      } catch (e) { /* private mode / quota — the caller's in-page cache clear still applies */ }
    },

    post(path, body) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-22) — stamp a "data just changed"
         marker (survives page navigation via localStorage) so the Deal Dashboard's
         next load bypasses the short-TTL server cache and shows fresh data — e.g.
         after saving a quote in the Workstation and returning to the DD. */
      try { localStorage.setItem('rqd_mutated_at', String(Date.now())); } catch (e) {}
      return this.fetch(path, {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    /** Multipart POST — pass a FormData. _request leaves Content-Type
        unset so the browser fills in the multipart boundary. */
    postForm(path, formData) {
      try { localStorage.setItem('rqd_mutated_at', String(Date.now())); } catch (e) {}
      return this.fetch(path, {
        method: 'POST',
        body: formData
      });
    }
  };

  /* ------------------------------------------
     Theme Toggle (Dark / Light Mode)
     ------------------------------------------ */
  const RepTheme = {
    STORAGE_KEY: 'rep_portal_theme',
    DARK_CLASS: 'rep-quote-dark',

    /** Get the wrapper element */
    getWrap() {
      return document.querySelector('.rep-quote-portal-wrap');
    },

    /** Check if dark mode is active */
    isDark() {
      const wrap = this.getWrap();
      return wrap ? wrap.classList.contains(this.DARK_CLASS) : false;
    },

    /** Sync parent element backgrounds with dark/light state */
    syncParents(dark) {
      var bg = dark ? '#000F1C' : '#FFFFFF';
      var imp = 'important';
      var wrap = this.getWrap();
      if (wrap) {
        wrap.style.setProperty('background', bg, imp);
        wrap.style.colorScheme = dark ? 'dark' : '';
      }
      var main = document.getElementById('main-content');
      if (main) main.style.setProperty('background', bg, imp);
      document.body.style.setProperty('background', bg, imp);
      document.documentElement.style.setProperty('background', bg, imp);
      // Shopify section wrapper around the portal
      if (wrap) {
        var allSections = document.querySelectorAll('.shopify-section');
        for (var i = 0; i < allSections.length; i++) {
          if (allSections[i].contains(wrap)) { allSections[i].style.setProperty('background', bg, imp); break; }
        }
      }
    },

    /** Apply theme on page load — dark is now the only mode. */
    apply() {
      const wrap = this.getWrap();
      if (!wrap) return;
      // Force dark always. Persist for any code that still reads the storage
      // key (Gym Designer URL params, room-designer instance, etc.).
      wrap.classList.add(this.DARK_CLASS);
      localStorage.setItem(this.STORAGE_KEY, 'dark');
      this.syncParents(true);
      this.updateIcons();
    },

    /** Toggle is retained only for the Gym Layout Designer page (its own
        theme-btn controls call into this). Elsewhere the toggle button is
        gone, so this won't fire. */
    toggle() {
      const wrap = this.getWrap();
      if (!wrap) return;
      wrap.classList.toggle(this.DARK_CLASS);
      const dark = this.isDark();
      localStorage.setItem(this.STORAGE_KEY, dark ? 'dark' : 'light');
      var ep = document.getElementById('rep-portal-early-paint');
      if (ep) ep.remove();
      this.syncParents(dark);
      this.updateIcons();
      var rd = document.querySelector('.room-designer');
      if (rd) {
        rd.classList.toggle('dark-mode', dark);
        localStorage.setItem('rd_darkMode', dark ? '1' : '0');
        var themeBtns = rd.querySelectorAll('.theme-btn');
        for (var i = 0; i < themeBtns.length; i++) {
          themeBtns[i].classList.toggle('active', (themeBtns[i].dataset.value === 'dark') === dark);
        }
        if (window.roomDesigner) {
          window.roomDesigner.darkMode = dark;
          if (window.roomDesigner._rebuildEquipmentTheme) window.roomDesigner._rebuildEquipmentTheme();
          if (window.roomDesigner.updateMeasurements) window.roomDesigner.updateMeasurements();
          if (window.roomDesigner.render) window.roomDesigner.render();
        }
      }
    },

    /** Update all toggle button icons to reflect current state */
    updateIcons() {
      const dark = this.isDark();
      var btns = document.querySelectorAll('.rep-quote-theme-toggle');
      for (var i = 0; i < btns.length; i++) {
        btns[i].innerHTML = dark ? '&#9728;' : '&#9790;';
        btns[i].setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
      }
    },

    /** Bind click handlers to all toggle buttons */
    init() {
      this.apply();
      var self = this;
      var btns = document.querySelectorAll('.rep-quote-theme-toggle');
      for (var i = 0; i < btns.length; i++) {
        btns[i].addEventListener('click', function () { self.toggle(); });
      }
    }
  };

  /* ------------------------------------------
     Auth Gate UI Controller
     ------------------------------------------ */
  function initAuthGate() {
    // Skip auth gate wiring if this is a password reset landing
    if (window.location.search.indexOf('reset=') !== -1) return;
    // CUSTOM_INVITELINK_BY_CLAUDE — nor on the sign-up page an invite link opens (rep-quote-portal.liquid).
    if (window.location.search.indexOf('invite=') !== -1) return;

    const gate = document.querySelector('.rep-quote-auth-gate:not(.rep-quote-reset-landing)');
    const content = document.querySelector('.rep-quote-portal-content');
    if (!gate || !content) return;

    // Already authenticated?
    const session = RepAuth.getSession();
    if (session) {
      // If the access token expired while the tab was away, refresh it
      // BEFORE rendering portal content. Otherwise pages that make bare
      // fetch() calls (e.g. the reviews moderation panel hits a different
      // worker without RepAPI's 401-retry) silently fail with empty
      // responses. Drawer's _silentFetch also swallows 401s, so the only
      // visible symptom is "the page loaded but no data" — exactly the
      // 2026-05-13 reviews regression after the drawer rollout.
      const stale = session.tokenExpiresAt && session.tokenExpiresAt < Date.now();
      if (stale && session.refreshToken) {
        RepAuth.refreshTokens()
          .then(function() { showPortalContent(gate, content, RepAuth.getSession()); })
          .catch(function() { RepAuth.clearSession(); window.location.reload(); });
        return;
      }
      if (stale) {
        // Expired with no refresh token — force re-login.
        RepAuth.clearSession();
        // Fall through to wire up the login form below.
      } else {
        showPortalContent(gate, content, session);
        return;
      }
    }

    // Wire up login form
    const emailInput = gate.querySelector('.rep-quote-auth-gate__input--email');
    const passwordInput = gate.querySelector('.rep-quote-auth-gate__input--password');
    const btn = gate.querySelector('.rep-quote-auth-gate__btn');
    const errorEl = gate.querySelector('.rep-quote-auth-gate__error');

    if (!emailInput || !passwordInput || !btn) return;

    async function handleLogin() {
      const email = emailInput.value.trim();
      const password = passwordInput.value.trim();
      if (!email || !password) return;

      btn.disabled = true;
      btn.textContent = 'Verifying...';
      errorEl.classList.remove('rep-quote-auth-gate__error--visible');
      emailInput.classList.remove('rep-quote-auth-gate__input--error');
      passwordInput.classList.remove('rep-quote-auth-gate__input--error');

      try {
        const data = await RepAuth.validate(email, password);
        if (data.requires2fa) {
          show2faView(data.tempToken);
          return;
        }
        RepAuth.saveSession(data);
        showPortalContent(gate, content, data);
      } catch (err) {
        emailInput.classList.add('rep-quote-auth-gate__input--error');
        passwordInput.classList.add('rep-quote-auth-gate__input--error');
        errorEl.textContent = err.message || 'Invalid email or password';
        errorEl.classList.add('rep-quote-auth-gate__error--visible');
        btn.disabled = false;
        btn.textContent = 'Sign In';
        emailInput.focus();
      }
    }

    // --- 2FA Verification View ---
    var _2faTempToken = null;

    function show2faView(tempToken) {
      _2faTempToken = tempToken;
      // Hide login elements
      for (var i = 0; i < loginEls.length; i++) loginEls[i].style.display = 'none';
      var titles = gate.querySelectorAll('.rep-quote-auth-gate__title, .rep-quote-auth-gate__subtitle');
      for (var j = 0; j < titles.length; j++) {
        if (!forgotView || !forgotView.contains(titles[j])) titles[j].style.display = 'none';
      }
      if (forgotView) forgotView.style.display = 'none';

      // Create 2FA view if it doesn't exist
      var tfaView = gate.querySelector('.rep-quote-auth-gate__2fa-view');
      if (!tfaView) {
        tfaView = document.createElement('div');
        tfaView.className = 'rep-quote-auth-gate__2fa-view';
        tfaView.innerHTML = '<p class="rep-quote-auth-gate__title">Two-Factor Authentication</p>'
          + '<p class="rep-quote-auth-gate__subtitle">Enter the 6-digit code from your authenticator app</p>'
          + '<input type="text" class="rep-quote-auth-gate__input rep-quote-auth-gate__input--totp" placeholder="000000" maxlength="6" inputmode="numeric" pattern="[0-9]*" autocomplete="one-time-code" spellcheck="false">'
          + '<button class="rep-quote-auth-gate__btn rep-quote-auth-gate__2fa-btn" type="button">Verify</button>'
          + '<a href="#" class="rep-quote-auth-gate__2fa-recovery-link" style="display:block;text-align:center;margin-top:12px;font-size:13px;color:var(--rq-text-muted,#667085);">Use a recovery code</a>'
          + '<div class="rep-quote-auth-gate__2fa-recovery" style="display:none;">'
          + '<input type="text" class="rep-quote-auth-gate__input rep-quote-auth-gate__input--recovery" placeholder="Recovery code" maxlength="20" autocomplete="off" spellcheck="false">'
          + '<button class="rep-quote-auth-gate__btn rep-quote-auth-gate__2fa-recovery-btn" type="button">Verify Recovery Code</button>'
          + '<a href="#" class="rep-quote-auth-gate__2fa-totp-link" style="display:block;text-align:center;margin-top:12px;font-size:13px;color:var(--rq-text-muted,#667085);">Use authenticator code</a>'
          + '</div>'
          + '<p class="rep-quote-auth-gate__error rep-quote-auth-gate__2fa-error"></p>'
          + '<a href="#" class="rep-quote-auth-gate__2fa-back" style="display:block;text-align:center;margin-top:16px;font-size:13px;color:var(--rq-text-muted,#667085);">&larr; Back to login</a>';
        gate.appendChild(tfaView);

        // Wire events
        var totpInput = tfaView.querySelector('.rep-quote-auth-gate__input--totp');
        var totpBtn = tfaView.querySelector('.rep-quote-auth-gate__2fa-btn');
        var recoveryInput = tfaView.querySelector('.rep-quote-auth-gate__input--recovery');
        var recoveryBtn = tfaView.querySelector('.rep-quote-auth-gate__2fa-recovery-btn');
        var recoveryLink = tfaView.querySelector('.rep-quote-auth-gate__2fa-recovery-link');
        var totpLink = tfaView.querySelector('.rep-quote-auth-gate__2fa-totp-link');
        var recoveryDiv = tfaView.querySelector('.rep-quote-auth-gate__2fa-recovery');
        var tfaError = tfaView.querySelector('.rep-quote-auth-gate__2fa-error');
        var backLink = tfaView.querySelector('.rep-quote-auth-gate__2fa-back');

        async function verify2faCode() {
          var code = (totpInput.value || '').trim();
          if (!code || code.length !== 6) { totpInput.focus(); return; }
          totpBtn.disabled = true; totpBtn.textContent = 'Verifying...';
          tfaError.classList.remove('rep-quote-auth-gate__error--visible');
          try {
            var data = await RepAuth.verify2fa(_2faTempToken, code, null);
            RepAuth.saveSession(data);
            showPortalContent(gate, content, data);
          } catch (err) {
            tfaError.textContent = err.message || 'Invalid code';
            tfaError.classList.add('rep-quote-auth-gate__error--visible');
            totpBtn.disabled = false; totpBtn.textContent = 'Verify';
            totpInput.value = ''; totpInput.focus();
          }
        }

        async function verify2faRecovery() {
          var rc = (recoveryInput.value || '').trim();
          if (!rc) { recoveryInput.focus(); return; }
          recoveryBtn.disabled = true; recoveryBtn.textContent = 'Verifying...';
          tfaError.classList.remove('rep-quote-auth-gate__error--visible');
          try {
            var data = await RepAuth.verify2fa(_2faTempToken, null, rc);
            RepAuth.saveSession(data);
            showPortalContent(gate, content, data);
          } catch (err) {
            tfaError.textContent = err.message || 'Invalid recovery code';
            tfaError.classList.add('rep-quote-auth-gate__error--visible');
            recoveryBtn.disabled = false; recoveryBtn.textContent = 'Verify Recovery Code';
            recoveryInput.value = ''; recoveryInput.focus();
          }
        }

        totpBtn.addEventListener('click', verify2faCode);
        totpInput.addEventListener('keydown', function(e) { if (e.key === 'Enter') verify2faCode(); });
        recoveryBtn.addEventListener('click', verify2faRecovery);
        recoveryInput.addEventListener('keydown', function(e) { if (e.key === 'Enter') verify2faRecovery(); });

        recoveryLink.addEventListener('click', function(e) {
          e.preventDefault();
          totpInput.parentElement.querySelector('.rep-quote-auth-gate__input--totp').style.display = 'none';
          totpBtn.style.display = 'none';
          recoveryLink.style.display = 'none';
          recoveryDiv.style.display = 'block';
          recoveryInput.focus();
        });

        totpLink.addEventListener('click', function(e) {
          e.preventDefault();
          totpInput.style.display = '';
          totpBtn.style.display = '';
          recoveryLink.style.display = '';
          recoveryDiv.style.display = 'none';
          totpInput.focus();
        });

        backLink.addEventListener('click', function(e) {
          e.preventDefault();
          hide2faView();
        });
      }

      tfaView.style.display = 'block';
      // Reset state
      var ti = tfaView.querySelector('.rep-quote-auth-gate__input--totp');
      var tb = tfaView.querySelector('.rep-quote-auth-gate__2fa-btn');
      if (ti) { ti.value = ''; ti.style.display = ''; }
      if (tb) { tb.disabled = false; tb.textContent = 'Verify'; tb.style.display = ''; }
      var rl = tfaView.querySelector('.rep-quote-auth-gate__2fa-recovery-link');
      if (rl) rl.style.display = '';
      var rd = tfaView.querySelector('.rep-quote-auth-gate__2fa-recovery');
      if (rd) rd.style.display = 'none';
      var te = tfaView.querySelector('.rep-quote-auth-gate__2fa-error');
      if (te) te.classList.remove('rep-quote-auth-gate__error--visible');
      if (ti) ti.focus();
    }

    function hide2faView() {
      _2faTempToken = null;
      var tfaView = gate.querySelector('.rep-quote-auth-gate__2fa-view');
      if (tfaView) tfaView.style.display = 'none';
      // Show login elements
      for (var i = 0; i < loginEls.length; i++) loginEls[i].style.display = '';
      var titles = gate.querySelectorAll('.rep-quote-auth-gate__title, .rep-quote-auth-gate__subtitle');
      for (var j = 0; j < titles.length; j++) {
        if (!forgotView || !forgotView.contains(titles[j])) titles[j].style.display = '';
      }
      btn.disabled = false;
      btn.textContent = 'Sign In';
      emailInput.focus();
    }

    btn.addEventListener('click', handleLogin);
    passwordInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') handleLogin();
    });
    emailInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') passwordInput.focus();
    });

    // --- Forgot Password Toggle ---
    var forgotLink = gate.querySelector('.rep-quote-auth-gate__forgot');
    var forgotView = gate.querySelector('.rep-quote-auth-gate__forgot-view');
    var forgotBack = gate.querySelector('.rep-quote-auth-gate__forgot-back');
    var forgotBtn = gate.querySelector('.rep-quote-auth-gate__forgot-btn');
    var forgotEmail = gate.querySelector('.rep-quote-auth-gate__forgot-email');
    var forgotSuccess = gate.querySelector('.rep-quote-auth-gate__success');

    // Elements that form the "login view"
    var loginEls = [emailInput, passwordInput, btn, errorEl];
    if (forgotLink) loginEls.push(forgotLink);

    function showForgotView(e) {
      if (e) e.preventDefault();
      // Hide login elements
      for (var i = 0; i < loginEls.length; i++) loginEls[i].style.display = 'none';
      // Hide login title/subtitle
      var titles = gate.querySelectorAll('.rep-quote-auth-gate__title, .rep-quote-auth-gate__subtitle');
      for (var j = 0; j < titles.length; j++) {
        if (!forgotView.contains(titles[j])) titles[j].style.display = 'none';
      }
      // Show forgot view
      if (forgotView) forgotView.style.display = 'block';
      if (forgotSuccess) forgotSuccess.style.display = 'none';
      if (forgotEmail) { forgotEmail.value = emailInput.value || ''; forgotEmail.focus(); }
      if (forgotBtn) { forgotBtn.style.display = ''; forgotBtn.disabled = false; forgotBtn.textContent = 'Send Reset Link'; }
    }

    function showLoginView(e) {
      if (e) e.preventDefault();
      // Show login elements
      for (var i = 0; i < loginEls.length; i++) loginEls[i].style.display = '';
      // Show login title/subtitle
      var titles = gate.querySelectorAll('.rep-quote-auth-gate__title, .rep-quote-auth-gate__subtitle');
      for (var j = 0; j < titles.length; j++) {
        if (!forgotView.contains(titles[j])) titles[j].style.display = '';
      }
      // Hide forgot view
      if (forgotView) forgotView.style.display = '';
      emailInput.focus();
    }

    if (forgotLink) forgotLink.addEventListener('click', showForgotView);
    if (forgotBack) forgotBack.addEventListener('click', showLoginView);

    if (forgotBtn && forgotEmail) {
      async function handleForgot() {
        var email = (forgotEmail.value || '').trim();
        if (!email) { forgotEmail.focus(); return; }

        forgotBtn.disabled = true;
        forgotBtn.textContent = 'Sending...';

        try {
          await fetch(WORKER_BASE + '/rep-password-reset-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email })
          });
        } catch (e) { /* always show success to prevent enumeration */ }

        forgotBtn.style.display = 'none';
        forgotEmail.style.display = 'none';
        if (forgotSuccess) forgotSuccess.style.display = 'block';
      }

      forgotBtn.addEventListener('click', handleForgot);
      forgotEmail.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') handleForgot();
      });
    }

    // Auto-focus
    emailInput.focus();
  }

  /** Validate password strength (mirrors backend validatePasswordStrength) */
  function checkPasswordStrength(pw) {
    if (!pw || pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pw)) return 'Must contain at least one uppercase letter';
    if (!/[a-z]/.test(pw)) return 'Must contain at least one lowercase letter';
    if (!/[0-9]/.test(pw)) return 'Must contain at least one number';
    if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain at least one special character';
    return null;
  }

  /** Show forced password change screen (first login) */
  function showForcePasswordChange(gate, content, session) {
    var earlyPaint = document.getElementById('rep-portal-early-paint');
    if (earlyPaint) earlyPaint.remove();
    content.style.display = 'none';

    // Clear gate and build password change form
    gate.innerHTML = '';
    gate.style.display = 'block';

    var wrap = document.createElement('div');
    wrap.innerHTML =
      '<p class="rep-quote-auth-gate__title">Set Your Password</p>' +
      '<p class="rep-quote-auth-gate__subtitle">You must set a new password before continuing</p>' +
      '<input type="password" class="rep-quote-auth-gate__input" id="force-pw-current" placeholder="Current password" autocomplete="off">' +
      '<input type="password" class="rep-quote-auth-gate__input" id="force-pw-new" placeholder="New password" autocomplete="new-password">' +
      '<input type="password" class="rep-quote-auth-gate__input" id="force-pw-confirm" placeholder="Confirm new password" autocomplete="new-password">' +
      '<p class="rep-quote-auth-gate__requirements">Must be 8+ characters with at least one uppercase, one lowercase, one number, and one special character.</p>' +
      '<button type="button" class="rep-quote-auth-gate__btn" id="force-pw-btn">Set Password</button>' +
      '<p class="rep-quote-auth-gate__error" id="force-pw-error"></p>';
    gate.appendChild(wrap);

    var currentInput = document.getElementById('force-pw-current');
    var newInput = document.getElementById('force-pw-new');
    var confirmInput = document.getElementById('force-pw-confirm');
    var btn = document.getElementById('force-pw-btn');
    var errorEl = document.getElementById('force-pw-error');

    currentInput.focus();

    async function handleForceChange() {
      var cur = currentInput.value;
      var np = newInput.value;
      var cp = confirmInput.value;

      currentInput.classList.remove('rep-quote-auth-gate__input--error');
      newInput.classList.remove('rep-quote-auth-gate__input--error');
      confirmInput.classList.remove('rep-quote-auth-gate__input--error');
      errorEl.classList.remove('rep-quote-auth-gate__error--visible');

      if (!cur) {
        currentInput.classList.add('rep-quote-auth-gate__input--error');
        errorEl.textContent = 'Please enter your current password';
        errorEl.classList.add('rep-quote-auth-gate__error--visible');
        currentInput.focus();
        return;
      }
      var strengthErr = checkPasswordStrength(np);
      if (strengthErr) {
        newInput.classList.add('rep-quote-auth-gate__input--error');
        errorEl.textContent = strengthErr;
        errorEl.classList.add('rep-quote-auth-gate__error--visible');
        newInput.focus();
        return;
      }
      if (np !== cp) {
        confirmInput.classList.add('rep-quote-auth-gate__input--error');
        errorEl.textContent = 'Passwords do not match';
        errorEl.classList.add('rep-quote-auth-gate__error--visible');
        confirmInput.focus();
        return;
      }

      btn.disabled = true;
      btn.textContent = 'Setting password...';

      try {
        await RepAPI.post('/rep-password-change', { currentPassword: cur, newPassword: np });
        // Clear the flag from session and show portal
        delete session.mustChangePassword;
        RepAuth.saveSession(session);
        content.style.display = '';
        showPortalContent(gate, content, session);
      } catch (err) {
        errorEl.textContent = err.message || 'Failed to set password';
        errorEl.classList.add('rep-quote-auth-gate__error--visible');
        btn.disabled = false;
        btn.textContent = 'Set Password';
      }
    }

    btn.addEventListener('click', handleForceChange);
    confirmInput.addEventListener('keydown', function(e) { if (e.key === 'Enter') handleForceChange(); });
    newInput.addEventListener('keydown', function(e) { if (e.key === 'Enter') confirmInput.focus(); });
    currentInput.addEventListener('keydown', function(e) { if (e.key === 'Enter') newInput.focus(); });
  }

  /** Show portal content, hide auth gate, populate rep info */
  function showPortalContent(gate, content, session) {
    // Check for forced password change
    if (session.mustChangePassword) {
      showForcePasswordChange(gate, content, session);
      return;
    }

    /* CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-06-11) — agency page guard.
       Agency tokens are fail-closed server-side (401 from every rfq-worker
       endpoint outside a small allowlist), so letting an agency session land
       on a data page (hub, dashboard, workstation…) fires that page's
       loaders → 401 → RepAPI treats it as an expired session and force-logs
       out. Redirect agency sessions to their own surface BEFORE the
       rep-portal:authenticated event triggers any data loader. Covers real
       agency logins AND admin impersonation of an agency user. */
    if ((session.role || '') === 'agency' && !agencyPageAllowed(window.location.pathname, session)) {
      window.location.replace(externalHome(session));
      return;
    }
    /* END CUSTOM_MARKETINGOPS_BY_CLAUDE */

    /* CUSTOM_CHATBOT_BY_CLAUDE (2026-08-11) — mount the portal-wide chat dock.
       Skipped on the chatbot admin page, which has its own full Live Chat tab;
       two takeover sockets for the same rep on one page is just confusing. */
    try {
      if (!/rep-chatbot-admin/.test(window.location.pathname)) {
        RepChatDock.init(session);
      }
    } catch (e) { console.warn('Chat dock mount skipped:', e); }

    // Remove early paint CSS so !important rules don't override JS display changes
    var earlyPaint = document.getElementById('rep-portal-early-paint');
    if (earlyPaint) earlyPaint.remove();
    gate.style.display = 'none';
    content.classList.add('rep-quote-portal-content--visible');

    // Fill in rep name in header. For admins (or anyone currently
    // impersonating), turn the name into a dropdown that lets you "view as"
    // any other rep without logging out + back in.
    const nameEl = content.querySelector('.rep-quote-header__rep-name');
    if (nameEl) {
      if (session.admin || session.impersonating) {
        renderRepSwitcher(nameEl, session);
      } else {
        nameEl.textContent = session.name || session.code;
      }
    }
    // If we're impersonating, show a sticky banner at the very top of the
    // page so it's obvious the displayed view isn't the admin's real account.
    if (session.impersonating) {
      renderImpersonationBanner(session);
    }

    // Wire up logout
    const logoutBtn = content.querySelector('.rep-quote-header__logout');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', function () {
        RepAuth.clearSession();
        sessionStorage.removeItem('rep_portal_page_config');
        window.location.href = '/pages/sales-team-portal';
      });
    }

    // Notify page-specific scripts that auth is ready FIRST. The drawer must
    // never be able to block this — if init() throws, the page's own data
    // loaders (dashboard, workstation, etc.) still need to fire.
    window.dispatchEvent(new Event('rep-portal:authenticated'));

    // Inject the persistent left-edge module drawer (every portal page).
    // Wrapped in try/catch so any drawer bug is contained — page still works.
    try {
      RepDrawer.init();
    } catch (e) {
      if (window.console && console.error) console.error('[RepDrawer] init failed:', e);
    }
  }

  /* ------------------------------------------
     Admin impersonation — header dropdown + banner
     ------------------------------------------ */

  // Replace the static header rep-name with a clickable dropdown widget that
  // lists every other rep. Click a rep → impersonate. While impersonating,
  // a "Stop impersonating" option appears at the top of the menu.
  function renderRepSwitcher(nameEl, session) {
    nameEl.innerHTML = '';
    nameEl.classList.add('rep-quote-switcher');
    var label = document.createElement('span');
    label.className = 'rep-quote-switcher__label';
    label.textContent = session.name || session.code;
    var caret = document.createElement('span');
    caret.className = 'rep-quote-switcher__caret';
    caret.textContent = '▾';  // ▾
    nameEl.appendChild(label);
    nameEl.appendChild(caret);

    var menu = document.createElement('div');
    menu.className = 'rep-quote-switcher__menu';
    menu.style.display = 'none';
    nameEl.appendChild(menu);

    var loaded = false;
    function closeMenu() {
      menu.style.display = 'none';
      document.removeEventListener('click', onDocClick);
    }
    function onDocClick(e) {
      if (!nameEl.contains(e.target)) closeMenu();
    }
    async function openMenu() {
      menu.style.display = 'block';
      setTimeout(function() { document.addEventListener('click', onDocClick); }, 0);
      if (loaded) return;
      menu.innerHTML = '<div class="rep-quote-switcher__hint">Loading reps…</div>';
      try {
        var token = await RepAuth.getAccessToken();
        var resp = await fetch(WORKER_BASE + '/rep-list-basic', {
          headers: { 'Authorization': 'Bearer ' + token },
        });
        var data = await resp.json();
        var reps = (data && data.reps) || [];
        // Sort alphabetically by name for predictable scanning.
        reps.sort(function(a, b) { return (a.name || a.code).localeCompare(b.name || b.code); });

        /* CUSTOM_MARKETINGOPS_BY_CLAUDE (Ryan 2026-06-08) — agency "view as".
           Agency users (role='agency') are deliberately filtered out of
           /rep-list-basic so they don't pollute lead-routing / reassign /
           share pickers. But admins legitimately want to see what a Malt Media
           login sees on the Paid Ads dashboard, and /admin/impersonate has no
           role restriction. So for admins only, pull the agency roster from the
           admin-only /agency-user-list and render it as a second group. */
        var agencyUsers = [];
        if (session.admin) {
          try {
            var aResp = await fetch(WORKER_BASE + '/agency-user-list', {
              headers: { 'Authorization': 'Bearer ' + token },
            });
            if (aResp.ok) {
              var aData = await aResp.json();
              agencyUsers = ((aData && aData.users) || []).filter(function(u) { return u.code !== session.code; });
              agencyUsers.sort(function(a, b) {
                return (a.agencyName || a.name || a.code).localeCompare(b.agencyName || b.name || b.code) ||
                  (a.name || a.code).localeCompare(b.name || b.code);
              });
            }
          } catch (ae) { /* non-fatal — agency group just won't render */ }
        }

        /* CUSTOM_PORTALSECURITY_BY_CLAUDE — HTML-escape server-supplied rep/agency
           fields before building innerHTML. Same stored-XSS class as the admin
           Portal Users table: a malicious rep/agency name would otherwise run in
           the ADMIN's context when they open the "view as" switcher. */
        function esc(s) {
          return String(s == null ? '' : s).replace(/[&<>"']/g, function(c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
          });
        }
        var html = '';
        if (session.impersonating) {
          html += '<button type="button" class="rep-quote-switcher__stop" data-stop-impersonation>Stop impersonating &rarr;</button>';
          html += '<div class="rep-quote-switcher__divider"></div>';
        }
        if (reps.length === 0 && agencyUsers.length === 0) {
          html += '<div class="rep-quote-switcher__hint">No other accounts found.</div>';
        }
        if (reps.length > 0) {
          html += '<div class="rep-quote-switcher__hint">View as:</div>';
          html += reps.map(function(r) {
            return '<button type="button" class="rep-quote-switcher__item" data-impersonate="' + esc(r.code) + '">' +
              '<span class="rep-quote-switcher__item-name">' + esc(r.name || r.code) + '</span>' +
              '<span class="rep-quote-switcher__item-code">' + esc(r.code) + '</span>' +
              '</button>';
          }).join('');
        }
        if (agencyUsers.length > 0) {
          if (reps.length > 0) html += '<div class="rep-quote-switcher__divider"></div>';
          html += '<div class="rep-quote-switcher__hint">View as agency:</div>';
          html += agencyUsers.map(function(u) {
            var sub = u.agencyName || u.code;
            return '<button type="button" class="rep-quote-switcher__item" data-impersonate="' + esc(u.code) + '">' +
              '<span class="rep-quote-switcher__item-name">' + esc(u.name || u.fullName || u.code) + '</span>' +
              '<span class="rep-quote-switcher__item-code">' + esc(sub) + '</span>' +
              '</button>';
          }).join('');
        }
        menu.innerHTML = html;
        loaded = true;
        // Wire item clicks
        menu.querySelectorAll('[data-impersonate]').forEach(function(btn) {
          btn.addEventListener('click', async function() {
            var code = btn.getAttribute('data-impersonate');
            btn.disabled = true;
            btn.textContent = 'Switching…';
            try { await RepAuth.startImpersonation(code); }
            catch (e) {
              btn.disabled = false;
              menu.innerHTML = '<div class="rep-quote-switcher__hint" style="color:#dc2626;">Failed: ' + (e.message || e) + '</div>';
            }
          });
        });
        var stopBtn = menu.querySelector('[data-stop-impersonation]');
        if (stopBtn) stopBtn.addEventListener('click', function() { RepAuth.stopImpersonation(); });
      } catch (e) {
        menu.innerHTML = '<div class="rep-quote-switcher__hint" style="color:#dc2626;">Failed to load reps: ' + (e.message || e) + '</div>';
      }
    }
    nameEl.style.cursor = 'pointer';
    nameEl.addEventListener('click', function(e) {
      e.stopPropagation();
      if (menu.style.display === 'none') openMenu();
      else closeMenu();
    });
  }

  // Sticky red banner at the top of the page while impersonating. Click the
  // banner (or its stop button) to revert to the original admin session.
  function renderImpersonationBanner(session) {
    if (document.getElementById('rep-portal-impersonation-banner')) return;
    var bar = document.createElement('div');
    bar.id = 'rep-portal-impersonation-banner';
    bar.className = 'rep-portal-impersonation-banner';
    bar.innerHTML = 'Viewing as <strong>' + (session.name || session.code) + '</strong> (admin: ' + (session.impersonatedBy || '?') +
      ') &mdash; <button type="button" class="rep-portal-impersonation-banner__stop">Stop impersonating &rarr;</button>';
    document.body.insertBefore(bar, document.body.firstChild);
    bar.querySelector('.rep-portal-impersonation-banner__stop').addEventListener('click', function() {
      RepAuth.stopImpersonation();
    });
  }

  /* ------------------------------------------
     Init on DOM ready
     ------------------------------------------ */
  function initAll() {
    RepTheme.init();
    initAuthGate();
  }

  /* NOTE: the actual `initAll()` invocation is at the BOTTOM of this IIFE,
     after `RepDrawer` is declared. When a user already has a session at
     page-load, defer-loaded JS runs synchronously and `showPortalContent`
     fires immediately during IIFE evaluation — if we trigger `initAll`
     before `const RepDrawer = {...}` is initialized, calling
     `RepDrawer.init()` from `showPortalContent` throws a Temporal Dead
     Zone ReferenceError. (Symptom: drawer works on first login, then
     vanishes on every subsequent page navigation.) */

  /* ------------------------------------------
     Phone Formatting Utility
     ------------------------------------------ */
  /** Format a phone string as (XXX) XXX-XXXX or 1 (XXX) XXX-XXXX */
  function formatPhone(value) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-11) — split the number from any
       extension. The "main" number is the first 10 digits (or 11 when it starts
       with a country-code '1'); every digit beyond that is the extension, rendered
       after " ext. " so it survives round-trips (type → save → re-display) across
       the whole pipeline — a prospecting phone's extension follows the deal all the
       way through. Numbers of 10 digits or fewer format exactly as before. */
    var digits = (value || '').replace(/\D/g, '');
    var hasCountry = digits.length > 10 && digits.charAt(0) === '1';
    var mainLen = hasCountry ? 11 : 10;
    var main = digits.substring(0, mainLen);
    var ext = digits.substring(mainLen);
    var out;
    if (hasCountry) {
      var local = main.substring(1);
      if (local.length === 0) out = '1';
      else if (local.length <= 3) out = '1 (' + local;
      else if (local.length <= 6) out = '1 (' + local.substring(0, 3) + ') ' + local.substring(3);
      else out = '1 (' + local.substring(0, 3) + ') ' + local.substring(3, 6) + '-' + local.substring(6);
    } else {
      if (main.length === 0) out = '';
      else if (main.length <= 3) out = '(' + main;
      else if (main.length <= 6) out = '(' + main.substring(0, 3) + ') ' + main.substring(3);
      else out = '(' + main.substring(0, 3) + ') ' + main.substring(3, 6) + '-' + main.substring(6);
    }
    if (ext) out += ' ext. ' + ext;
    return out;
  }

  /** Bind auto-format to a phone input element */
  function bindPhoneInput(input) {
    if (!input) return;
    input.addEventListener('input', function() {
      var pos = this.selectionStart;
      var oldLen = this.value.length;
      this.value = formatPhone(this.value);
      var newLen = this.value.length;
      var newPos = pos + (newLen - oldLen);
      this.setSelectionRange(newPos, newPos);
    });
  }

  /* ------------------------------------------
     Persistent left-edge module drawer.
     Injected into <body> on every portal page once the user is
     authenticated. Lets reps jump between tools without going back
     to the hub. State (open/closed) persists in localStorage.
     ------------------------------------------ */
  const RepDrawer = {
    STORAGE_KEY: 'rep_portal_drawer_open',
    DRAWER_ID: 'repPortalDrawer',
    OVERLAY_ID: 'repPortalDrawerOverlay',

    // Module catalog — single source of truth. Categories render in order.
    // data-page keys mirror /rep-portal-config keys so visibility rules
    // (hidden / disabled) apply just like the old hub grid.
    MODULES: [
      { title: 'Sales', items: [
        { page: 'workstation', icon: '&#128187;', label: 'Quote Workstation', href: '/pages/rep-quote-workstation' },
        { page: 'dashboard',   icon: '&#128202;', label: 'Deal Dashboard',    href: '/pages/rep-quote-dashboard' },
        { page: 'projects',    icon: '&#128193;', label: 'Projects',          href: '/pages/rep-quote-projects' },
        { page: 'invoicegen',  icon: '&#129534;', label: 'Invoice Generator', href: '/pages/rep-invoice-generator' },
        { page: 'commissions', icon: '&#128184;', label: 'Commissions',       href: '/pages/rep-commissions' },
      ]},
      { title: 'Prospecting', items: [
        { page: 'leadgen',    icon: '&#128269;', label: 'Lead Generator', href: '/pages/rep-leadgen' },
        { page: 'dealfinder', icon: '&#128176;', label: 'Deal Finder',    href: '/pages/lead-finder', badgeId: 'lfHubBadge', visitKey: 'lf_hub_last_visit' },
        /* CUSTOM_CFALOGOREVIEW_BY_CLAUDE (Ryan 2026-11-02, CFA Campaign 1 Step 4) - the CFA approves each school's logo on a
           45 lb bumper before the school can be emailed. ⚠ `page: 'logoreview'` is the permanent visibility key; never rename it.
           `cfaOnly` hides it from everyone but the CFA and admins (applyPages). rfq-worker answers 403 to anyone else, which is
           the gate. */
        { page: 'logoreview', icon: '&#128737;&#65039;', label: 'Logo Review', href: '/pages/rep-logo-review', cfaOnly: true },
      ]},
      { title: 'Marketing & CX', items: [
        /* CUSTOM_MARKETINGOPS_BY_CLAUDE — `page: 'marketingops'` key kept
           (NOT renamed to 'paidads') so existing user-visibility prefs
           stored against this key in /admin/page-visibility KV continue
           to apply. Label + href reflect the 2026-05-14 rename. */
        { page: 'marketingops', icon: '&#128200;', label: 'Paid Ads', href: '/pages/rep-paid-ads' },
        /* CUSTOM_ADSREVIEW_BY_CLAUDE (2026-09-06) - the weekly search-term review, moved
           out of the Excel workbook on Ryan's laptop. Sits beside Paid Ads because it is
           the same account and the same worker.
           ⚠ `page: 'adsreview'` is the KV persistence key for module visibility and must
           never be renamed - a rename orphans every saved per-rep status. Reps should be
           Hidden (Settings > Admin: Module Visibility): marketing-worker admits only
           admins + agency on these routes, and every WRITE is admin-only. */
        { page: 'adsreview', icon: '&#128269;', label: 'Search Terms', href: '/pages/rep-ads-review' },
        { page: 'blogbot',      icon: '&#9997;',   label: 'Blog',          href: '/pages/blog-bot' },
        /* CUSTOM_SOCIALBOT_BY_CLAUDE (2026-08-19) — Social Bot, portal dashboard.
           ⚠ `page: 'socialbot'` is the KV persistence key for module visibility and must
           never be renamed; a rename orphans every saved per-rep status. The LABEL is free
           to change and did (2026-08-19, "Social Bot" -> "Social", Ryan's call) — the same
           split `marketingops` above made on its own rename. Reps are Hidden
           at launch (set it in Settings > Admin: Module Visibility) — and the page itself
           admits only admins + agency, mirroring social-worker's canRead(). */
        { page: 'socialbot',    icon: '&#128241;', label: 'Social',        href: '/pages/rep-social-bot' },
        /* CUSTOM_CONTENTLIBRARY_BY_CLAUDE (2026-09-26) - Content Library: search every live product,
           open its page / collections, download its photos as JPGs. Open to reps, admins AND the
           agency. ⚠ `page: 'contentlibrary'` is the KV persistence key for module visibility and must
           never be renamed. An external user reaches it when their per-person config switches it
           on (EXTERNAL_PAGE_MODULE above + rfq-worker lib/external-access.js MODULE_PATHS). */
        { page: 'contentlibrary', icon: '&#128247;', label: 'Content Library', href: '/pages/rep-content-library' },
        { page: 'reviews',      icon: '&#11088;',  label: 'Reviews',       href: '/pages/rep-quote-reviews' },
        { page: 'chatbot',      icon: '&#128172;', label: 'Live Chat',     href: '/pages/rep-chatbot-admin' },
      ]},
      { title: 'Tools', items: [
        { page: 'gymlayout',   icon: '&#x1F4D0;', label: 'Gym Layout Designer', href: '/pages/rep-gym-designer', badgeId: 'glHubBadge', visitKey: 'gl_hub_last_visit' },
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — Weight Designer, portal edition (2026-07-29).
           ⚠ The `adminOnly: true` flag was REMOVED 2026-08-16 (Ryan): this module now
           obeys its Settings status like every other tool, so Enabled opens it to the
           whole team and Disabled shows them the "Soon" treatment. The flag itself and
           its machinery (data-admin-only + applyAdminOnly) are kept and are currently
           unused — they remain the way to lock a module for admins independently of the
           Settings status, which is a different question from "is this live yet". */
        { page: 'weightdesigner', icon: '&#127947;', label: 'Weight Designer', href: '/pages/rep-weight-designer' },
        { page: 'vendorguide', icon: '&#128218;', label: 'Vendor Guide',        href: '/pages/rep-vendor-guide' },
      ]},
      { title: 'Admin', items: [
        /* CUSTOM_TAXHUB_BY_CLAUDE (2026-08-22) — Tax Hub. Taxable sales by state.
           ⚠ `page: 'taxhub'` is the KV persistence key for module visibility and must never
           be renamed; a rename orphans every saved per-rep status. The label is free to change.

           `adminOnly: true` is used here on purpose, and this is the case the flag was kept
           for (see the note on weightdesigner below): "who may open this" is a genuinely
           different question from "is this live yet". Tax posture is finance, not a rep tool,
           so it stays admin-locked however its Settings status is set — and applyAdminOnly
           asserts that synchronously, without waiting on /rep-portal-config. Reps see it
           greyed with "Soon"; set the status to Hidden in Settings > Admin: Module Visibility
           to remove it from their drawer entirely.

           ⛔ Neither is a security boundary. rfq-worker's /tax-hub/* handlers re-check
           rep.admin server-side and that is the gate that matters. */
        { page: 'taxhub', icon: '&#127963;', label: 'Tax Hub', href: '/pages/rep-tax-hub', adminOnly: true },
        /* CUSTOM_PRODUCTRULES_BY_CLAUDE (Ryan 2026-10-04) - Product Rules: every product's rules and gaps.
           ⚠ `page: 'productrules'` is the permanent visibility key; never rename it. `adminOnly` because it
           carries costs and supplier rates; rfq-worker's /product-rules routes re-check admin, which is the gate. */
        { page: 'productrules', icon: '&#128203;', label: 'Product Rules', href: '/pages/rep-product-rules', adminOnly: true },
        /* CUSTOM_FREIGHTHUB_BY_CLAUDE (Ryan 2026-10-14, Freight Hub H1) - Freight Hub: every freight number in one place.
           ⚠ `page: 'freighthub'` is the permanent visibility key; never rename it. `adminOnly` because it carries costs
           and broker rates; rfq-worker's /admin/freight-hub/summary re-checks admin, which is the gate. */
        { page: 'freighthub', icon: '&#128666;', label: 'Freight Hub', href: '/pages/rep-freight-hub', adminOnly: true },
        /* CUSTOM_AGENTACCESS_BY_CLAUDE (Ryan 2026-08-29) — AI Agents: the held-send queue and
           the per-agent capability matrix.
           ⚠ `page: 'aiagents'` is the KV persistence key for module visibility and must never be
           renamed; a rename orphans every saved per-rep status. The label is free to change.
           `adminOnly: true` for the same reason Tax Hub uses it — releasing a held supplier email
           and widening a bot's capabilities are not rep work, whatever the Settings status says.
           ⛔ Neither flag is a security boundary. /agent-holds, /agent-hold-release,
           /agent-hold-cancel, /agent-list, /agent-capabilities and /agent-kill-switch all re-check
           rep.admin server-side, and none of them appear in the agent capability registry — so an
           agent holding every grant cannot reach them either. That is the gate that matters. */
        /* CUSTOM_AGENTSUGGEST_BY_CLAUDE (Ryan 2026-08-29) — visitKey added for the SUGGESTION
           half of this badge only. The approvals half deliberately ignores it; see loadBadges. */
        { page: 'aiagents', icon: '&#129302;', label: 'AI Agents', href: '/pages/rep-agent-approvals', adminOnly: true, badgeId: 'aiHoldsBadge', visitKey: 'ai_suggestions_last_visit' },
        /* CUSTOM_ABBYCATALOG_BY_CLAUDE (AIOPS suggestion 80758facfe84, Ryan 2026-08-30) — every
           email Abby can send, code-derived from src/email/abby-catalog.js in rfq-worker.
           ⚠ `page: 'abby'` is the KV persistence key for module visibility and must never be
           renamed; a rename orphans every saved per-rep status. The label is free to change.
           `adminOnly: true` — the page paints only for admins, and /abby-catalog re-checks
           server-side. ⛔ Neither flag is a security boundary, and neither needs to be a hard
           one here: the route is a READ with no send, no edit and no toggle behind it. */
        /* CUSTOM_AICOMPOSER_BY_CLAUDE (Ryan 2026-10-18) — renamed "Emails": Abby keeps her tab and each employee gets a
           voice tab. `voiceOpen` lets a rep whom Ryan switched on for Draft it open the page for their OWN voice tab
           (see openVoiceForRep); everyone else still sees it locked. The key stays 'abby'. */
        { page: 'abby', icon: '&#9993;', label: 'Emails', href: '/pages/rep-abby', adminOnly: true, voiceOpen: true },
        // recovery's display starts hidden in markup; applyPages reveals for admins per config.
        { page: 'recovery', icon: '&#128737;', label: 'Data Recovery', href: '/pages/rep-quote-admin-recovery', startHidden: true },
      ]},
    ],

    SETTINGS_ITEM: { icon: '&#9881;', label: 'Settings', href: '/pages/rep-quote-settings' },

    _injected: false,
    _open: false,
    _hoverExpanded: false, // true when drawer is open via hover (not click)

    /**
     * Decorative background fetch — does NOT use RepAPI._request because
     * RepAPI.clearSession + window.location.reload on 401 (line ~280) would
     * kill the user's session and break the page they're currently loading.
     * The drawer's badge counts and config refresh are non-critical; if they
     * fail, we silently swallow the error and leave the cached state in place.
     */
    _silentFetch(url) {
      var session = RepAuth.getSession();
      if (!session || !session.accessToken) return Promise.reject(new Error('no session'));
      return fetch(url, {
        method: 'GET',
        headers: { 'Authorization': 'Bearer ' + session.accessToken }
      }).then(function(r) {
        if (!r.ok) return Promise.reject(new Error('http ' + r.status));
        return r.json();
      });
    },

    init() {
      // Reset on every page load so BFCache restoration can't leave us in a
      // half-initialized state where _injected is true but the DOM elements
      // were never created (or were cleared by a prior reload).
      if (!document.getElementById(this.DRAWER_ID)) this._injected = false;
      if (this._injected) return;
      if (!RepAuth.getSession()) return;
      // Each step wrapped so a downstream bug never prevents the drawer
      // itself from rendering. We want the menu visible at all costs; badges
      // and config refresh are bonus.
      try { this._render();         this._injected = true; }
      catch (e) { console.error('[RepDrawer] render failed:', e); return; }
      try { this._bindEvents(); }   catch (e) { console.error('[RepDrawer] bind failed:', e); }
      try { this.markActive(); }    catch (e) { console.error('[RepDrawer] markActive failed:', e); }
      try { this.applyPageConfig(); } catch (e) { console.error('[RepDrawer] applyPageConfig failed:', e); }
      var self = this;
      setTimeout(function() {
        try { self.loadBadges(); } catch (e) { console.error('[RepDrawer] loadBadges failed:', e); }
      }, 1500);
    },

    _render() {
      // Top-level Home item (above categories). Links to the portal hub.
      // Standalone — no data-page so it can't be hidden by /rep-portal-config;
      // active-state highlight still works via markActive's path match.
      var html = '';
      html += '<div class="rep-portal-drawer__top">';
      html += '<a href="/pages/sales-team-portal" class="rep-portal-drawer__item" data-tooltip="Home">';
      html += '<span class="rep-portal-drawer__item-icon" aria-hidden="true">&#127968;</span>';
      html += '<span class="rep-portal-drawer__item-label">Home</span>';
      html += '</a>';
      html += '</div>';
      for (var i = 0; i < this.MODULES.length; i++) {
        var cat = this.MODULES[i];
        var catKey = cat.title.toLowerCase().replace(/[^a-z]+/g, '-');
        html += '<div class="rep-portal-drawer__category" data-category="' + catKey + '">';
        html += '<div class="rep-portal-drawer__category-title">' + cat.title + '</div>';
        for (var j = 0; j < cat.items.length; j++) {
          var it = cat.items[j];
          var attrs = ' data-page="' + it.page + '" data-tooltip="' + it.label + '"';
          if (it.adminOnly) attrs += ' data-admin-only="1"';
          if (it.cfaOnly) attrs += ' data-cfa-only="1"'; // CUSTOM_CFALOGOREVIEW_BY_CLAUDE
          if (it.voiceOpen) attrs += ' data-voice-openable="1"'; // CUSTOM_AICOMPOSER_BY_CLAUDE
          if (it.visitKey) attrs += ' onclick="localStorage.setItem(\'' + it.visitKey + '\',Date.now().toString())"';
          var styleAttr = it.startHidden ? ' style="display:none;"' : '';
          html += '<a href="' + it.href + '" class="rep-portal-drawer__item"' + attrs + styleAttr + '>';
          html += '<span class="rep-portal-drawer__item-icon" aria-hidden="true">' + it.icon + '</span>';
          html += '<span class="rep-portal-drawer__item-label">' + it.label + '</span>';
          if (it.badgeId) html += '<span id="' + it.badgeId + '" class="rep-portal-drawer__item-badge" style="display:none;"></span>';
          html += '<span class="rep-portal-drawer__item-status"></span>';
          html += '</a>';
        }
        html += '</div>';
      }

      // Top control: a single toggle button. Chevron rotates 180° between
      // collapsed and expanded states (CSS handles the rotation).
      var head = '<div class="rep-portal-drawer__head">'
               + '<button type="button" class="rep-portal-drawer__toggle" aria-label="Toggle menu" data-tooltip="Expand menu">'
               +   '<span class="rep-portal-drawer__toggle-icon" aria-hidden="true">&#8250;</span>'
               + '</button>'
               + '<span class="rep-portal-drawer__brand">Modules</span>'
               + '</div>';
      var nav  = '<nav class="rep-portal-drawer__nav" aria-label="Portal modules">' + html + '</nav>';
      var foot = '<div class="rep-portal-drawer__footer">'
               + '<a href="' + this.SETTINGS_ITEM.href + '" class="rep-portal-drawer__item" data-tooltip="' + this.SETTINGS_ITEM.label + '">'
               + '<span class="rep-portal-drawer__item-icon" aria-hidden="true">' + this.SETTINGS_ITEM.icon + '</span>'
               + '<span class="rep-portal-drawer__item-label">' + this.SETTINGS_ITEM.label + '</span>'
               + '</a></div>';

      var drawer = document.createElement('aside');
      drawer.id = this.DRAWER_ID;
      drawer.className = 'rep-portal-drawer';
      drawer.setAttribute('aria-label', 'Portal modules');
      drawer.innerHTML = head + nav + foot;
      document.body.appendChild(drawer);

      var overlay = document.createElement('div');
      overlay.id = this.OVERLAY_ID;
      overlay.className = 'rep-portal-drawer-overlay';
      overlay.hidden = true;
      document.body.appendChild(overlay);

      // Mobile open button — only visible on small viewports (CSS-controlled)
      // since the rail is hidden by default on mobile and the drawer head
      // toggle is therefore off-screen. Lives as a direct child of the page
      // header (.rep-quote-header), positioned via CSS grid on mobile so it
      // sits at the far right of the title row without disturbing the
      // rep-info row below.
      var mobileOpen = document.createElement('button');
      mobileOpen.id = 'repPortalDrawerMobileOpen';
      mobileOpen.className = 'rep-portal-drawer-mobile-open';
      mobileOpen.type = 'button';
      mobileOpen.setAttribute('aria-label', 'Open menu');
      mobileOpen.innerHTML = '<span aria-hidden="true">&#9776;</span>';
      var header = document.querySelector('.rep-quote-header');
      if (header) header.appendChild(mobileOpen);
      else document.body.appendChild(mobileOpen); // fallback for pages without the standard header

      // Saved state — default collapsed (rail mode).
      // On mobile (<=769px) we ignore the saved state and always start
      // collapsed, since the mobile drawer is a transient overlay
      // (open → tap item → navigate). Carrying "open" across page loads
      // would leave the menu covering content after every navigation.
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-15) — 600 → 769 to match the
         drawer's CSS breakpoint (custom-rep-portal.css, "@media (max-width:
         769px)"). These two MUST agree: this line is what stops a remembered
         "open" from being restored on a viewport where the drawer renders as a
         full-screen overlay rather than a rail. Left at 600, a rep on a 601–769
         screen who had opened the drawer on desktop would land on every page
         with the menu covering the content. */
      var saved = localStorage.getItem(this.STORAGE_KEY);
      var isMobile = window.matchMedia && window.matchMedia('(max-width: 769px)').matches;
      this.setOpen(!isMobile && saved === '1', false);
    },

    setOpen(open, persist) {
      var drawer = document.getElementById(this.DRAWER_ID);
      var overlay = document.getElementById(this.OVERLAY_ID);
      if (!drawer) return;
      drawer.classList.toggle('rep-portal-drawer--open', open);
      // Body class lets CSS shift portal content right on wide viewports
      // (see @media min-width:1500px in custom-rep-portal.css). Below that
      // breakpoint the class has no visual effect; the drawer overlays.
      document.body.classList.toggle('rep-portal-drawer-expanded', open);
      // Update toggle button accessibility + tooltip text.
      var toggleBtn = drawer.querySelector('.rep-portal-drawer__toggle');
      if (toggleBtn) {
        toggleBtn.setAttribute('aria-expanded', String(open));
        toggleBtn.setAttribute('data-tooltip', open ? 'Collapse menu' : 'Expand menu');
      }
      // On the hub, suppress the dim backdrop — main content stays bright
      // and interactive while the drawer expands beside it.
      var isHub = window.location.pathname.replace(/\/$/, '') === '/pages/sales-team-portal';
      if (overlay) overlay.hidden = !open || isHub;
      if (persist) {
        localStorage.setItem(this.STORAGE_KEY, open ? '1' : '0');
        // Explicit user action — clear the hover flag so this state sticks
        // (drawer won't auto-collapse on mouseleave even though it's open).
        this._hoverExpanded = false;
      }
      this._open = open;
    },

    _bindEvents() {
      var self = this;
      // The entire drawer head is the toggle target (cursor:pointer on the
      // head, hover state highlights the chevron). Forgiving click area —
      // hitting either the chevron button or empty head space toggles.
      var drawer = document.getElementById(this.DRAWER_ID);
      var head = drawer ? drawer.querySelector('.rep-portal-drawer__head') : null;
      var overlay = document.getElementById(this.OVERLAY_ID);
      var mobileOpen = document.getElementById('repPortalDrawerMobileOpen');
      if (head) head.addEventListener('click', function() { self.setOpen(!self._open, true); });
      if (overlay) overlay.addEventListener('click', function() { self.setOpen(false, true); });
      if (mobileOpen) mobileOpen.addEventListener('click', function() { self.setOpen(true, true); });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self._open) self.setOpen(false, true);
      });

      // Auto-expand on hover (Notion/Slack pattern) — only on devices with
      // real hover support, never on touch-only devices where it'd misfire.
      // Expand on mouseenter after a short delay (avoid expanding when the
      // cursor is just passing over the rail); collapse on mouseleave if
      // the expansion was triggered by hover (not by an explicit click).
      var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
      if (drawer && canHover) {
        var hoverOpenTimer = null;
        var hoverCloseTimer = null;
        var HOVER_OPEN_DELAY = 1200;  // 1.2s — deliberate hover, but not slow enough to feel laggy
        var HOVER_CLOSE_DELAY = 350;

        drawer.addEventListener('mouseenter', function() {
          if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); hoverCloseTimer = null; }
          if (self._open) return; // already open — nothing to do
          hoverOpenTimer = setTimeout(function() {
            // persist=false so a hover-driven expand doesn't overwrite the
            // user's last explicit choice in localStorage.
            self.setOpen(true, false);
            self._hoverExpanded = true;
          }, HOVER_OPEN_DELAY);
        });
        drawer.addEventListener('mouseleave', function() {
          if (hoverOpenTimer) { clearTimeout(hoverOpenTimer); hoverOpenTimer = null; }
          if (!self._open || !self._hoverExpanded) return;
          hoverCloseTimer = setTimeout(function() {
            self.setOpen(false, false);
            self._hoverExpanded = false;
          }, HOVER_CLOSE_DELAY);
        });
      }
    },

    /** Hide a category header when every item inside is hidden. */
    updateCategoryVisibility() {
      var cats = document.querySelectorAll('.rep-portal-drawer__category');
      cats.forEach(function(cat) {
        var items = cat.querySelectorAll('.rep-portal-drawer__item');
        var any = false;
        for (var i = 0; i < items.length; i++) {
          if (items[i].style.display !== 'none') { any = true; break; }
        }
        cat.classList.toggle('rep-portal-drawer__category--empty', !any);
      });
    },

    /** Grey a module out and make it inert. Idempotent: applyPages runs at least
        twice per load (cached config, then the fetched one), and an unguarded
        addEventListener would stack a fresh preventDefault on every pass. */
    _lockItem(item) {
      item.classList.add('rep-portal-drawer__item--disabled');
      if (item.dataset.lockBound) return;
      item.dataset.lockBound = '1';
      item.addEventListener('click', function(e) {
        if (item.dataset.voiceOpen === '1') return; // CUSTOM_AICOMPOSER_BY_CLAUDE — unlocked for this rep's own voice page
        e.preventDefault();
      });
    },

    /* CUSTOM_AICOMPOSER_BY_CLAUDE (Ryan 2026-10-18) — a rep Ryan switched on for Draft it may open the Emails page,
       which then shows only their own voice tab. One GET, cached for the session (the same key composer-ai.js
       writes). Not a security boundary: every voice route re-checks the person server-side. */
    openVoiceForRep() {
      var self = this;
      var unlock = function () {
        var items = document.querySelectorAll('.rep-portal-drawer__item[data-voice-openable="1"]');
        for (var i = 0; i < items.length; i++) {
          items[i].dataset.voiceOpen = '1';
          items[i].classList.remove('rep-portal-drawer__item--disabled');
          var st = items[i].querySelector('.rep-portal-drawer__item-status');
          if (st) st.textContent = '';
        }
      };
      try {
        var raw = sessionStorage.getItem('bsf_composer_ai_me_v1');
        var c = raw ? JSON.parse(raw) : null;
        if (c && c.me && Date.now() - c.at < 10 * 60 * 1000) { if (c.me.allowed === true) unlock(); return; }
      } catch (e) {}
      this._silentFetch(WORKER_BASE + '/composer-ai/me').then(function (me) {
        try { sessionStorage.setItem('bsf_composer_ai_me_v1', JSON.stringify({ code: me.code, at: Date.now(), me: me })); } catch (e) {}
        if (me && me.allowed === true) unlock();
      }).catch(function () {});
    },

    /** CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — lock every [data-admin-only] module for
        non-admins, WITHOUT waiting on /rep-portal-config.
        applyPages only ever runs off a cached config or a successful fetch, so on a
        first visit with the worker unreachable it never runs at all — and the lock
        would silently not be there. An access rule can't be contingent on a network
        call succeeding. This runs synchronously; applyPages then re-asserts the same
        state (see the isDisabled line) so the two can't disagree. */
    applyAdminOnly(isAdmin) {
      var locked = document.querySelectorAll('.rep-portal-drawer__item[data-admin-only]');
      for (var i = 0; i < locked.length; i++) {
        if (isAdmin) continue;
        this._lockItem(locked[i]);
        var st = locked[i].querySelector('.rep-portal-drawer__item-status');
        if (st) st.textContent = 'Soon';
      }
    },

    /** Apply /rep-portal-config visibility (hidden / disabled / agency role). */
    applyPageConfig() {
      var session = RepAuth.getSession();
      if (!session) return;
      var isAdmin = !!session.admin;
      var role = session.role || (isAdmin ? 'admin' : 'rep');
      var isExternal = role === 'agency' && !isAdmin; // CUSTOM_PORTALACCESS_BY_CLAUDE
      var self = this;

      this.applyAdminOnly(isAdmin);
      if (!isAdmin && !isExternal) this.openVoiceForRep(); // CUSTOM_AICOMPOSER_BY_CLAUDE

      function applyPages(pages) {
        var items = document.querySelectorAll('.rep-portal-drawer__item[data-page]');
        /* CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-09-26) — an external user (role 'agency') goes
           through the SAME per-person config as everybody else now; the hard-coded four-module
           filter that used to sit here is gone. The one difference: for them a module the config
           says nothing about is HIDDEN, never enabled — they never inherit the team default. */
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          var key = item.getAttribute('data-page');
          var val = pages[key];
          if (isExternal && val !== true && val !== 'enabled') val = 'hidden';
          var isHidden = val === 'hidden'
            /* CUSTOM_CFALOGOREVIEW_BY_CLAUDE (2026-11-02) - a CFA-only module (Logo Review) is simply absent for anyone who is
               neither the CFA nor an admin; its worker refuses them anyway, so a greyed "Soon" would promise something. */
            || (!isAdmin && role !== 'cfa' && item.getAttribute('data-cfa-only') === '1');
          /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — an adminOnly module reads as DISABLED to
             anyone who isn't an admin, whatever the config says. Folded into the existing
             branch rather than given one of its own so the drawer has exactly ONE locked
             look; a second would be a new state for the team to learn for no gain. */
          var isDisabled = val === false || val === 'disabled'
                        || (!isAdmin && item.getAttribute('data-admin-only') === '1' && item.dataset.voiceOpen !== '1'); // voiceOpen: CUSTOM_AICOMPOSER_BY_CLAUDE
          var status = item.querySelector('.rep-portal-drawer__item-status');

          if (isHidden) {
            if (isAdmin) { item.style.display = ''; if (status) status.textContent = 'Hidden'; }
            else { item.style.display = 'none'; }
          } else if (isDisabled) {
            item.style.display = '';
            if (isAdmin) { if (status) status.textContent = 'Soon'; }
            else {
              self._lockItem(item);
              if (status) status.textContent = 'Soon';
            }
          } else {
            item.style.display = '';
            if (status) status.textContent = '';
          }
        }
        self.updateCategoryVisibility();
      }

      /* CUSTOM_PORTALACCESS_BY_CLAUDE — an external user's drawer is drawn at once from the token's
         `mods` (the same claim every worker enforces), so nothing flashes before the fetch lands; the
         server's per-person config below then confirms it. */
      if (isExternal) {
        var _fromToken = {};
        var _mods = externalModsFor(session);
        for (var m = 0; m < _mods.length; m++) _fromToken[_mods[m]] = true;
        applyPages(_fromToken);
      }

      try {
        var cached = JSON.parse(sessionStorage.getItem('rep_portal_page_config'));
        if (cached) applyPages(cached);
      } catch (e) {}

      // Use _silentFetch — a 401 here must NOT clear the session or reload
      // the page, because that would kill any in-flight data load on the
      // page the user just opened (e.g., the dashboard's /rep-quotes call).
      this._silentFetch(WORKER_BASE + '/rep-portal-config').then(function(data) {
        var pages = data.effectivePages || data.pages || {};
        sessionStorage.setItem('rep_portal_page_config', JSON.stringify(pages));
        applyPages(pages);
        /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (2026-08-16) — announce the fresh config so a
           page that gates ITSELF on its module status doesn't have to fire a second
           request for the answer this one just fetched. Emitted on the fetch path only:
           the cache path is synchronous, and any listener reads the cache first. */
        try {
          window.dispatchEvent(new CustomEvent('rep-portal:page-config', { detail: pages }));
        } catch (e) {}
      }).catch(function() {});
    },

    /** Highlight the drawer item matching the current page. */
    markActive() {
      var path = window.location.pathname.replace(/\/$/, '');
      var items = document.querySelectorAll('.rep-portal-drawer__item');
      for (var i = 0; i < items.length; i++) {
        var href = (items[i].getAttribute('href') || '').replace(/\/$/, '');
        if (href && href === path) items[i].classList.add('rep-portal-drawer__item--active');
        else items[i].classList.remove('rep-portal-drawer__item--active');
      }
    },

    /** Deal Finder + Gym Layout new-items badge loaders.
        All requests use _silentFetch — 401s must not trigger the RepAPI
        reload-and-clear-session path because that would kill any in-flight
        data fetch on the page the user is currently looking at. */
    loadBadges() {
      var self = this;
      /* CUSTOM_PORTALACCESS_BY_CLAUDE — the badges count team data (leads, layouts). An external
         user never had the modules and leadfinder-worker now refuses their token outright. */
      var _bs = RepAuth.getSession();
      if (_bs && (_bs.role || '') === 'agency' && !_bs.admin) return;
      var lfLast = parseInt(localStorage.getItem('lf_hub_last_visit') || '0', 10);
      if (!lfLast) { localStorage.setItem('lf_hub_last_visit', Date.now().toString()); }
      else {
        self._silentFetch('https://leadfinder-worker.ryan-29b.workers.dev/leads').then(function(d) {
          var leads = (d && d.leads) || [];
          var n = 0;
          leads.forEach(function(l) { if (l.dateFound && new Date(l.dateFound).getTime() > lfLast) n++; });
          var b = document.getElementById('lfHubBadge');
          if (b && n > 0) { b.textContent = '+' + n; b.style.display = 'inline-flex'; }
        }).catch(function() {});
      }

      /* CUSTOM_AGENTACCESS_BY_CLAUDE (Ryan 2026-08-29) — agent sends awaiting approval.
         ⚠️ NOT a since-last-visit delta like the two around it, and deliberately has no
         visitKey. Those answer "what is new"; this answers "what is still blocked on you",
         which does not stop being true because you looked at it. Visiting the page must not
         clear it — only releasing or discarding the item does.
         Admin-gated at the worker, so it is only fetched for an admin session: reps would
         take a guaranteed 401 on every page load for a badge their drawer greys out anyway. */
      var _sess = null;
      try { _sess = RepAuth.getSession(); } catch (e) { _sess = null; }
      if (_sess && (_sess.admin || (_sess.rep && _sess.rep.admin))) {
        /* CUSTOM_AGENTSUGGEST_BY_CLAUDE (Ryan 2026-08-29) — ONE number on the robot, two sources,
           and they clear differently on purpose:
             · approvals   — still blocked on you. No visitKey; looking at the page does not
                             clear it, only releasing or discarding the item does.
             · suggestions — what is NEW since you last opened the page, the Deal Finder / Gym
                             Layout shape. Ryan: "at a glance when I login I can see any new
                             suggestions." Visiting clears this half.
           So after a visit the number drops to just the approvals, which reads correctly as
           "this many are still waiting on you".
           ⛔ ONE write to the badge, after BOTH settle. Two independent .then() blocks writing
           the same element is a race whose loser silently wins about half the time.
           ⛔ `hydrate=0` — this runs on every portal page load, and the count only needs each
           item's createdAt, which is already in KV metadata. Without it the badge would drag
           every suggestion BODY across the wire to render one digit.
           First run stamps the clock and counts nothing, matching the two loaders around it:
           otherwise every suggestion ever filed reads as new the first time you load a page. */
        /* CUSTOM_SUGBADGE_BY_CLAUDE (Ryan 2026-10-10) - the robot's number IS the Suggestions tab's number: every OPEN
           suggestion, both lanes, counted the way the tab counts it (status open). It used to add approvals waiting to
           suggestions filed since your last visit, so it never matched the tab and was always smaller. */
        self._silentFetch(WORKER_BASE + '/agent-suggestions?status=open&hydrate=0&limit=200').catch(function() { return null; })
          .then(function(r) {
            var items = (r && r.items) || [];
            var n = 0;
            items.forEach(function(i) { if (!i.status || i.status === 'open') n++; });
            /* The worker counts every matching open row before its 200-row page; prefer that. */
            if (r && r.counts && typeof r.counts.matching === 'number') n = r.counts.matching;
            var b = document.getElementById('aiHoldsBadge');
            if (!b) return;
            if (n > 0) {
              b.textContent = String(n);
              b.style.display = 'inline-flex';
              b.title = n + ' open suggestion' + (n === 1 ? '' : 's');
            } else {
              b.style.display = 'none';
            }
          }).catch(function() {});
      }

      var glLast = parseInt(localStorage.getItem('gl_hub_last_visit') || '0', 10);
      if (!glLast) { localStorage.setItem('gl_hub_last_visit', Date.now().toString()); }
      else {
        self._silentFetch(WORKER_BASE + '/design-list').then(function(d) {
          var designs = (d && d.designs) || [];
          var n = 0;
          designs.forEach(function(ds) { if (ds.sharedBy && ds.sharedAt && new Date(ds.sharedAt).getTime() > glLast) n++; });
          var b = document.getElementById('glHubBadge');
          if (b && n > 0) { b.textContent = '+' + n; b.style.display = 'inline-flex'; }
        }).catch(function() {});
      }
    },
  };

  /* ------------------------------------------
     Shared chip-style email input (Ryan 2026-06-19)
     ------------------------------------------
     Outlook-style recipient chips for ANY email field across the portal
     (SOLD TO popup, +New popups, Quote Workstation customer card). Ported
     from the email-modal's _installChipFields, generalized to a single
     standalone field with neutral class names so it's reusable everywhere.

     installChipField(opts):
       opts.replace : an <input> element to convert in place (we build the
                      chip field around it + reuse it as the typing input)
       opts.initial : seed value — string (comma/;/space separated) or array
       opts.placeholder, opts.ariaLabel, opts.onChange
     Returns { getChips, setChips, getValue, focus, element, primary }.
       getValue() commits any pending text then returns the comma-joined string
       (FIRST chip = primary — callers send chips[0] to Shopify / outbound To,
        and the full list for Outlook thread-matching).
     CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) — opts.named: each pill is a PERSON, {name, email}.
       A pasted or typed `Jill Smith <jill@x.org>` (Outlook's copy, `;` lists too) keeps the name; a pill
       shows the name with the address on hover; CLICKING a pill opens a one-line name box (Enter saves,
       Esc cancels) so a rep can name somebody, never has to. getPeople() / setPeople([{name,email}]).
       opts.noPrimary: no first-pill "primary" mark (the CC box). Address-only callers are unchanged. */
  var _CHIP_EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  function _chipEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function installChipField(opts) {
    opts = opts || {};
    var srcInput = opts.replace;
    if (!srcInput || !srcInput.parentNode) return null;
    var chips = [];
    var names = []; // CUSTOM_DEALPEOPLE_BY_CLAUDE - names[i] belongs to chips[i]; '' = no name known (named mode only)
    var named = !!opts.named;
    var seed = opts.initial != null ? opts.initial : srcInput.value;
    if (named && Array.isArray(seed) && seed.length && typeof seed[0] === 'object') {
      seed.forEach(function (p) { var e = String((p && p.email) || '').trim(); if (e) { chips.push(e); names.push(String((p && p.name) || '').trim()); } });
    } else {
      (Array.isArray(seed) ? seed : String(seed || '').split(/[,;\s]+/))
        .map(function (t) { return String(t || '').trim(); })
        .filter(Boolean)
        .forEach(function (t) { chips.push(t); names.push(''); });
    }
    var editing = -1; // the pill whose name box is open
    var onChange = typeof opts.onChange === 'function' ? opts.onChange : function () {};

    /* Robustness: keep srcInput as a HIDDEN MIRROR that preserves its id +
       holds the comma-joined value, so every existing `getElementById(id).value`
       read keeps working untouched. A NEW visible input does the typing. */
    var field = document.createElement('div');
    field.className = 'rqd-chipfield';
    if (srcInput.id) field.setAttribute('data-for', srcInput.id);
    srcInput.parentNode.replaceChild(field, srcInput);
    srcInput.type = 'hidden';
    function syncMirror() { srcInput.value = chips.join(', '); }
    syncMirror();

    var typeInput = document.createElement('input');
    typeInput.type = 'text';
    typeInput.className = 'rqd-chip-input';
    typeInput.autocomplete = 'off';
    typeInput.spellcheck = false;
    if (opts.placeholder != null) typeInput.placeholder = opts.placeholder;
    if (opts.ariaLabel) typeInput.setAttribute('aria-label', opts.ariaLabel);

    function render() {
      var html = chips.map(function (addr, i) {
        var valid = _CHIP_EMAIL_RX.test(addr);
        var primary = i === 0 && !opts.noPrimary;
        var nm = named ? (names[i] || '') : '';
        if (named && i === editing) {
          return '<span class="rqd-chip rqd-chip--editing" data-idx="' + i + '">'
            + '<input type="text" class="rqd-chip-name" data-idx="' + i + '" value="' + _chipEsc(nm) + '" placeholder="Name for ' + _chipEsc(addr) + '" aria-label="Name for ' + _chipEsc(addr) + '">'
            + '</span>';
        }
        var tip = valid ? (nm ? nm + ' <' + addr + '>' : addr) + (primary ? ' (primary)' : '') + (named ? ' — click to ' + (nm ? 'change the name' : 'add a name') : '') : 'Invalid: ' + addr;
        return '<span class="rqd-chip' + (valid ? '' : ' rqd-chip--invalid') + (primary ? ' rqd-chip--primary' : '') + (named ? ' rqd-chip--named' : '') + '" data-idx="' + i + '" title="' + _chipEsc(tip) + '">'
          + _chipEsc(nm || addr)
          + '<button type="button" class="rqd-chip-rm" data-idx="' + i + '" aria-label="Remove ' + _chipEsc(nm || addr) + '">×</button>'
          + '</span>';
      }).join('');
      while (field.firstChild) field.removeChild(field.firstChild);
      field.insertAdjacentHTML('beforeend', html);
      field.appendChild(typeInput);
      field.appendChild(srcInput); // hidden mirror stays in the DOM (keeps its id)
      syncMirror();
      if (named) typeInput.placeholder = chips.length ? '' : (opts.placeholder || ''); // CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan): the hint goes once there is a pill
      if (editing >= 0) {
        var box = field.querySelector('.rqd-chip-name');
        if (box) { try { box.focus(); box.select(); } catch (_) {} }
      }
    }
    function addPerson(email, name) {
      var e = String(email || '').trim();
      var k = e.toLowerCase();
      for (var i = 0; i < chips.length; i++) {
        if (String(chips[i]).toLowerCase() === k) { if (name && !names[i]) names[i] = name; return; }
      }
      chips.push(e); names.push(String(name || '').trim());
    }
    function closeNameBox(save) {
      if (editing < 0) return;
      var box = field.querySelector('.rqd-chip-name');
      if (save && box) names[editing] = String(box.value || '').trim().slice(0, 200);
      editing = -1;
      render();
      if (save) onChange();
    }
    function commit() {
      var val = String(typeInput.value || '').trim();
      if (!val) return false;
      if (named && RepContacts && RepContacts.parseAddressList) {
        var people = RepContacts.parseAddressList(val);
        if (!people.length) return false;
        people.forEach(function (p) { addPerson(p.email, p.name); });
        typeInput.value = '';
        render();
        return true;
      }
      var toks = val.split(/[,;\s]+/).map(function (t) { return t.trim(); }).filter(Boolean);
      var valid = toks.filter(function (t) { return _CHIP_EMAIL_RX.test(t); });
      var invalid = toks.filter(function (t) { return !_CHIP_EMAIL_RX.test(t); });
      if (valid.length === 0) return false;
      valid.forEach(function (v) { chips.push(v); names.push(''); });
      typeInput.value = invalid.join(', ');
      render();
      return true;
    }
    field.addEventListener('click', function (ev) {
      if (ev.target === field) { try { typeInput.focus(); } catch (_) {} return; }
      var rm = ev.target.closest('.rqd-chip-rm');
      if (rm) {
        ev.stopPropagation();
        var idx = parseInt(rm.dataset.idx, 10);
        if (!isNaN(idx) && idx >= 0 && idx < chips.length) {
          chips.splice(idx, 1);
          names.splice(idx, 1);
          editing = -1;
          render();
          try { typeInput.focus(); } catch (_) {}
          onChange();
        }
        return;
      }
      /* CUSTOM_DEALPEOPLE_BY_CLAUDE - named mode: a click on a pill opens its name box. */
      if (named && !ev.target.closest('.rqd-chip-name')) {
        var pill = ev.target.closest('.rqd-chip');
        if (pill) {
          var pi = parseInt(pill.dataset.idx, 10);
          if (!isNaN(pi) && pi !== editing) { ev.stopPropagation(); if (editing >= 0) closeNameBox(true); editing = pi; render(); }
        }
      }
    });
    if (named) {
      field.addEventListener('keydown', function (ev) {
        if (!ev.target.classList || !ev.target.classList.contains('rqd-chip-name')) return;
        if (ev.key === 'Enter') { ev.preventDefault(); ev.stopPropagation(); closeNameBox(true); try { typeInput.focus(); } catch (_) {} }
        else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); closeNameBox(false); }
      });
      field.addEventListener('focusout', function (ev) {
        if (ev.target.classList && ev.target.classList.contains('rqd-chip-name')) closeNameBox(true);
      });
    }
    typeInput.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ',' || ev.key === ';') {
        ev.preventDefault();
        if (commit()) onChange();
        return;
      }
      if (ev.key === 'Tab' && typeInput.value && typeInput.value.trim()) {
        if (commit()) onChange();
        return;
      }
      if (ev.key === 'Backspace' && typeInput.value === '' && chips.length > 0) {
        ev.preventDefault();
        chips.pop();
        names.pop();
        render();
        onChange();
      }
    });
    typeInput.addEventListener('paste', function (ev) {
      var pasted = (ev.clipboardData || window.clipboardData).getData('text');
      if (pasted && (/[,;\s]/.test(pasted) || (named && /</.test(pasted)))) {
        ev.preventDefault();
        typeInput.value = (typeInput.value || '') + pasted;
        if (commit()) onChange();
      }
    });
    typeInput.addEventListener('blur', function () { if (commit()) onChange(); });

    render();
    return {
      element: field,
      input: typeInput, // the visible typing input — for binding external autocomplete
      getChips: function () { return chips.slice(); },
      setChips: function (arr) { chips = (arr || []).map(function (t) { return String(t || '').trim(); }).filter(Boolean); names = chips.map(function () { return ''; }); editing = -1; render(); },
      /* CUSTOM_DEALPEOPLE_BY_CLAUDE - the pills as people. Commits pending text first; drops invalid addresses. */
      getPeople: function () {
        commit();
        if (editing >= 0) closeNameBox(true);
        var out = [];
        chips.forEach(function (e, i) { if (_CHIP_EMAIL_RX.test(e)) out.push({ name: names[i] || '', email: e }); });
        return out;
      },
      setPeople: function (arr) {
        chips = []; names = []; editing = -1;
        (arr || []).forEach(function (p) { if (p && p.email) addPerson(p.email, p.name); });
        render();
      },
      add: function (addr, name) {
        var v = String(addr || '').trim();
        if (!v || !_CHIP_EMAIL_RX.test(v)) return false;
        addPerson(v, name); typeInput.value = ''; render(); onChange(); return true;
      },
      getValue: function () { commit(); return chips.join(', '); },
      primary: function () { commit(); return chips[0] || ''; },
      focus: function () { try { typeInput.focus(); } catch (_) {} },
    };
  }

  /* ============================================================
     CUSTOM_CHATBOT_BY_CLAUDE (2026-08-11) — Portal-wide live chat dock
     ============================================================
     Lives here because this file is loaded by all 23 portal sections, so a
     customer chat reaches the rep on whatever tool they happen to be using.

     The problem it solves: an SMS arrives, the rep navigates to the chatbot
     page, finds the conversation, clicks in — and the customer is gone. Now
     the bubble goes red wherever they are, one click takes the chat over, and
     they keep working while they type.

     Alerting rides a single WebSocket to the AdminInbox Durable Object.
     Taking a chat over opens a second socket to that conversation's
     ConversationRoom, which is the same channel the full admin page uses.

     Nothing in here may throw into the portal — every entry point is guarded.
     A broken dock must never take out the Deal Dashboard. */
  const CHAT_WORKER = 'https://chatbot-worker.ryan-29b.workers.dev';

  /* ---------- RepChatAlert — "you are not looking at this" alerting ----------
     Shared by both admin chat surfaces: the portal-wide dock, and the Live Chat
     tab on /pages/rep-chatbot-admin (where the dock is deliberately not
     mounted). One implementation so the two cannot drift.

     ⛔ THE HELD-CHAT CASE IS SILENT BY DESIGN, SERVER-SIDE. AdminInbox only
     increments `unread` for a chat nobody has taken over ("an admin already
     holding the conversation is reading it live"), and the dock's whole alert
     chain keys off unread. That assumption holds right up until the rep
     switches tabs, at which point a live takeover goes completely quiet. So
     this hangs off the room socket's customer_message event instead of unread,
     and gates on whether the tab is actually being looked at.

     "Not looking at it" is deliberately BOTH checks: document.hidden alone is
     false when the tab is selected but the whole browser window is behind
     another app, which is half of what was asked for.

     ⛔ TWO RULES, NOT ONE — and the difference is whether a human has committed
     to the conversation (Ryan, 2026-08-17):

       newChat()     — before anyone picks up. One chime on the conversation's
                       FIRST message, then nothing; the red bubble carries the
                       rest. A rep who has not taken it by message two is busy,
                       and the bot is handling it fine. A chat that beeps on
                       every message just teaches people to ignore beeping.
       heldMessage() — after this rep has taken over. EVERY message announces
                       itself, because a missed one now means a customer waiting
                       on a human. Silent only while their cursor is in the
                       reply box.

     So "away" is the gate for the first, and "engaged" for the second. They are
     not the same question: a rep can be staring right at the tab with the
     conversation open and still want to be told, if they have clicked away from
     the box to do something else. */
  const RepChatAlert = {
    /* Built from a code point rather than pasted as a glyph. The rest of the
       non-ASCII in this file is BMP; the siren is astral-plane, and this is the
       one piece of user-visible text here where a mangled byte would be
       obvious to a customer-facing... well, to Ryan, every time he alt-tabs.
       Pure-ASCII source cannot be mangled by any step of the deploy. */
    get ALARM_TITLE() {
      var siren = String.fromCodePoint(0x1F6A8);
      return siren + ' NEW MESSAGE ' + siren;
    },
    resting: null,
    alarmed: false,
    bound: false,

    init() {
      if (this.bound) return;
      this.bound = true;
      if (this.resting === null) {
        try { this.resting = document.title; } catch (e) { this.resting = ''; }
      }
      var self = this;
      var back = function () {
        self.clear();
        /* Coming back to the tab with the popup already open is also "I am
           caught up" — the thread is on screen and they can see what arrived.
           Without this the count would sit there until they closed and
           reopened the popup. */
        if (self._dock && self._dock.watchingThread()) self._dock.clearMissedHeld();
      };
      try {
        document.addEventListener('visibilitychange', function () {
          if (!document.hidden) back();
        });
        window.addEventListener('focus', back);
      } catch (e) {}
      this.unlockAudio();
    },

    /* Embedded mode: the widget owns its window, so it tells us when it opens.
       Reopening the popup is the rep catching up, which clears the "missed
       while minimised" tally. Guarded by clearMissedHeld's own early return so
       this cannot bounce render -> bsdock:unread -> here -> render. */
    watchWidgetVisibility(dock) {
      this._dock = dock;   // also used by the focus handler in init()
      try {
        document.addEventListener('bschat:visibility', function (e) {
          var d = (e && e.detail) || {};
          /* Scoped 2026-08-25: reopening the widget onto a DIFFERENT
             conversation is not catching up on the held one, and clearing its
             tally there is how the count a rep was relying on disappeared. */
          if (d.open && dock.watchingThread()) dock.clearMissedHeld();
        });
      } catch (e) {}
    },

    away() {
      try {
        if (document.hidden) return true;
        // hasFocus() is what catches "tab is selected, but the window is behind
        // something else". Guarded because it is absent in some embedded views.
        if (typeof document.hasFocus === 'function' && !document.hasFocus()) return true;
      } catch (e) {}
      return false;
    },

    /* The normal title, owned by whoever renders it (the dock composes an
       unread count into it). Remembered either way, applied only while the
       alarm is down — otherwise the next re-render would quietly wipe it. */
    setResting(title) {
      this.init();
      this.resting = title;
      if (!this.alarmed) {
        try { document.title = title; } catch (e) {}
      }
    },

    /* ---------- who is engaged, and therefore does not need shouting at ----------
       Cursor in a reply box is the signal that the rep is actually present and
       working the conversation, as opposed to merely having the tab open behind
       whatever else they are doing. Ryan's rule, 2026-08-17.

       Note it requires the WINDOW to be focused too: activeElement happily
       reports the composer for a window that has been in the background for an
       hour, which would silence exactly the case this exists to catch. */
    composers: [],

    watchComposer(id) {
      this.init();
      if (!id || this.composers.indexOf(id) !== -1) return;
      this.composers.push(id);
      // Engaging with the box IS the acknowledgement. Without this, an alarm
      // raised while the tab was already visible would never clear, because no
      // visibilitychange or window focus event is coming.
      var self = this;
      try {
        var el = document.getElementById(id);
        if (!el) return;
        // Putting the cursor in the box, or typing in it, both mean "I have
        // this" — either way an alarm already on the tab comes down.
        var seen = function () { self.clear(); };
        el.addEventListener('focus', seen);
        el.addEventListener('keydown', seen);
      } catch (e) {}
    },

    engaged() {
      if (this.away()) return false;
      try {
        var ae = document.activeElement;
        if (!ae || !ae.id || this.composers.indexOf(ae.id) === -1) return false;
        /* The box has to be ON SCREEN. Minimising the popup normally blurs the
           composer, but "normally" is not a guarantee across browsers and the
           failure mode is the worst available: silence, on a chat the rep has
           taken responsibility for and can no longer see. offsetParent goes
           null when the element or any ancestor is display:none. */
        if (ae.offsetParent === null) return false;
        /* Cursor in the visible reply box IS engagement. Nothing more.
           ⛔ A 30-second "recent typing" requirement lived here briefly and was
           WRONG: it meant a rep sitting with the caret in the box, reading
           what the customer had written before answering, got chimed at
           anyway. Ryan, 2026-08-17, testing it: "when their mouse was in the
           chat text input box the noise kept coming through... this should not
           come through." Cursor in the box means leave them alone; anywhere
           else, or minimised, means tell them. */
        return true;
      } catch (e) { return false; }
    },

    /* ---------- the two announcements ----------
       They are deliberately different, because the two situations are:

       BEFORE anyone picks up — one chime on the FIRST message and nothing after
       it. The red bubble carries the rest. If a rep has not taken it by the
       second message they are busy, and the bot is handling it; a chat that
       beeps on every message trains people to ignore beeping.

       DURING a takeover — chime every message, because the rep has committed to
       this conversation and a missed message is now a customer waiting on a
       human. Silent only while their cursor is in the box. */

    /* Title only, no sound. This is the every-message half: while the bot has a
       conversation, the tab keeps saying so on every message, but it only
       CHIMES once (see newChat). Ryan, 2026-08-17: "notifications in top banner
       for all messages... noise on msg one." */
    alarmIfAway() {
      this.init();
      if (!this.away()) return false;
      this.raise();
      return true;
    },

    // The once-per-conversation chime. Beeps whether or not you are looking;
    // the title only alarms if you are not, since there is nothing to alert you
    // to on a tab you can already see.
    newChat() {
      this.init();
      this.beep();
      return this.alarmIfAway();
    },

    heldMessage() {
      this.init();
      if (this.engaged()) return false;
      this.beep();
      this.raise();
      return true;
    },

    raise() {
      if (this.alarmed) return;
      this.alarmed = true;
      try { document.title = this.ALARM_TITLE; } catch (e) {}
    },

    clear() {
      if (!this.alarmed) return;
      this.alarmed = false;
      // Back to the RESTING title, not the one captured at page load — the
      // dock's unread count is part of the resting state and has to survive.
      try { document.title = this.resting || document.title; } catch (e) {}
    },

    /* ---------- the chime ----------
       Synthesized rather than an audio asset: nothing to host, nothing to 404,
       and it is identical on both surfaces.

       ⛔ ONE SHARED CONTEXT, NEVER CLOSED, AND SCHEDULE ONLY AFTER IT IS
       RUNNING. This used to `new AudioContext()` per beep and close it after
       600ms, which produced Ryan's "muffled airy noise" (2026-08-17) rather
       than a tone:

         - A context created without a prior user gesture starts SUSPENDED. The
           envelope was scheduled against `ctx.currentTime` immediately, the
           context then auto-resumed some time later, and every scheduled time
           was already in the past — so the ramps collapsed and what came out
           was the attack smeared into a whoosh.
         - It bit hardest right after a portal page change, because a navigation
           is a full reload and the new page has had no gesture on it yet.
         - Browsers also cap live AudioContexts (~6 in Chrome); one per beep
           eventually returns a context that never starts at all.

       So: reuse one context, resume it first, and take `currentTime` only once
       it is actually running. */
    _ctx: null,

    audioCtx() {
      try {
        var Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        if (!this._ctx || this._ctx.state === 'closed') this._ctx = new Ctx();
        return this._ctx;
      } catch (e) { return null; }
    },

    /* Any real interaction is permission to make noise later. Cheap, passive,
       and it is what turns the first chime after a page load from a fragment
       into a tone. */
    unlockAudio() {
      var self = this;
      var go = function () {
        var ctx = self.audioCtx();
        if (ctx && ctx.state === 'suspended') { try { ctx.resume(); } catch (e) {} }
      };
      ['pointerdown', 'keydown', 'touchstart'].forEach(function (ev) {
        try { document.addEventListener(ev, go, { passive: true }); } catch (e) {}
      });
    },

    beep() {
      var ctx = this.audioCtx();
      if (!ctx) return;
      var play = function () {
        try {
          // A beat of lead-in so every ramp is scheduled in the FUTURE.
          var t = ctx.currentTime + 0.02;
          var osc = ctx.createOscillator();
          var gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(880, t);
          osc.frequency.setValueAtTime(1180, t + 0.11);
          gain.gain.setValueAtTime(0.0001, t);
          gain.gain.exponentialRampToValueAtTime(0.14, t + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.32);
          osc.start(t); osc.stop(t + 0.34);
        } catch (e) {}
      };
      if (ctx.state === 'suspended') {
        try { ctx.resume().then(play).catch(function () {}); } catch (e) { play(); }
      } else {
        play();
      }
    },
  };

  /* ---------- RepChatLearn — the end-of-conversation wrap-up panel ----------
     Extracted out of RepChatDock 2026-08-17 so it is not reachable from one
     place only. The dock opens it when a takeover finishes; the chatbot admin
     page's Review tab opens it on any past conversation a rep typed in. Same
     markup, same endpoints, same contract — only the mount point and where a
     success line goes differ.

     ⛔ APPROVE-THEN-SAVE. /learn/extract only ever PROPOSES. Nothing reaches the
     knowledge base until a human ticks it and presses save. A wrong KB entry is
     worse than a missing one: it is retrieved with the same confidence as a
     right one and nothing downstream tells them apart. */
  const RepChatLearn = {
    sid: null,
    data: null,
    note: '',
    inline: false,
    onStatus: null,

    el() { return document.getElementById('rqcdLearn'); },

    close() {
      var el = this.el();
      if (el) el.remove();
      this.sid = null;
      this.data = null;
      this.note = '';
      this.onStatus = null;
    },

    /* mount         — element the panel is placed in.
       opts.inline   — sit in normal flow (Review tab) instead of sliding up
                       over the dock panel. Carries .rqcd__learn--inline, which
                       is also what makes the --rqcd-* palette resolve off-dock.
       opts.onStatus — where the "saved N" line goes once the panel is gone. */
    open(sid, mount, opts) {
      if (!sid || !mount) return;
      this.close();
      opts = opts || {};
      this.sid = sid;
      this.inline = !!opts.inline;
      this.onStatus = opts.onStatus || null;

      var el = document.createElement('div');
      el.id = 'rqcdLearn';
      el.className = 'rqcd__learn' + (this.inline ? ' rqcd__learn--inline' : '');
      el.innerHTML =
        '<div class="rqcd__learn-head">Anything the bot should have known?</div>' +
        '<textarea id="rqcdLearnNote" class="rqcd__learn-note" rows="3" ' +
          'placeholder="Optional. What did you have to correct, or tell them, that the bot missed?"></textarea>' +
        '<div class="rqcd__learn-actions">' +
          '<button type="button" class="rqcd__btn" id="rqcdLearnSkip">Skip</button>' +
          '<button type="button" class="rqcd__btn rqcd__btn--end" id="rqcdLearnGo">Review what to save</button>' +
        '</div>' +
        '<div class="rqcd__learn-status" id="rqcdLearnStatus" hidden></div>';
      // Inline it goes at the top of the panel the rep is already looking at;
      // in the dock it slides up from the bottom over the transcript.
      if (this.inline) mount.insertBefore(el, mount.firstChild);
      else mount.appendChild(el);

      var self = this;
      document.getElementById('rqcdLearnSkip').addEventListener('click', function () { self.close(); });
      document.getElementById('rqcdLearnGo').addEventListener('click', function () { self.extract(); });
      try { document.getElementById('rqcdLearnNote').focus(); } catch (e) {}
    },

    setLearnStatus(text, busy) {
      var el = document.getElementById('rqcdLearnStatus');
      if (!el) return;
      el.hidden = !text;
      el.textContent = text || '';
      el.classList.toggle('rqcd__learn-status--busy', !!busy);
    },

    extract() {
      var self = this;
      var sid = this.sid;
      if (!sid) return;
      var note = (document.getElementById('rqcdLearnNote') || {}).value || '';
      var go = document.getElementById('rqcdLearnGo');
      if (go) go.disabled = true;
      this.setLearnStatus('Reading the conversation…', true);

      RepAPI.fetchExternal(CHAT_WORKER, '/learn/extract', {
        method: 'POST',
        body: JSON.stringify({ sessionId: sid, note: note }),
      }).then(function (data) {
        if (self.sid !== sid) return; // rep moved on while it ran
        if (!data || data.error) throw new Error((data && data.error) || 'no data');
        self.note = note;
        self.renderProposals(data);
      }).catch(function (e) {
        if (go) go.disabled = false;
        self.setLearnStatus('Could not read the conversation. ' + (e && e.message ? e.message : ''), false);
      });
    },

    renderProposals(data) {
      var el = this.el();
      if (!el) return;
      var kb = data.kbEntries || [];
      var corr = data.corrections || [];
      this.data = { kb: kb, corr: corr };

      if (!kb.length && !corr.length) {
        /* Nothing to learn is the common, correct outcome — say so plainly
           rather than dressing it up as a failure. */
        el.innerHTML =
          '<div class="rqcd__learn-head">Nothing new to save</div>' +
          '<div class="rqcd__learn-empty">The bot handled this one. Your note is on the transcript.</div>' +
          '<div class="rqcd__learn-actions"><button type="button" class="rqcd__btn" id="rqcdLearnDone">Close</button></div>' +
          '<div class="rqcd__learn-status" id="rqcdLearnStatus" hidden></div>';
        var self0 = this;
        document.getElementById('rqcdLearnDone').addEventListener('click', function () { self0.save(true); });
        return;
      }

      var html = '<div class="rqcd__learn-head">Tick what is right. Nothing saves until you do.</div>' +
        '<div class="rqcd__learn-list">';
      kb.forEach(function (e, i) {
        html += '<label class="rqcd__learn-item">' +
          '<input type="checkbox" data-kind="kb" data-i="' + i + '" checked>' +
          '<span class="rqcd__learn-body">' +
            '<span class="rqcd__tag">FACT</span> ' + escHtml(e.fact || '') +
            (e.why ? '<span class="rqcd__learn-why">' + escHtml(e.why) + '</span>' : '') +
          '</span></label>';
      });
      corr.forEach(function (c, i) {
        html += '<label class="rqcd__learn-item">' +
          '<input type="checkbox" data-kind="corr" data-i="' + i + '" checked>' +
          '<span class="rqcd__learn-body">' +
            '<span class="rqcd__tag rqcd__tag--hot">FIX</span> ' + escHtml(c.correction || '') +
            '<span class="rqcd__learn-why">Instead of: ' + escHtml((c.bot_answer || '').slice(0, 160)) + '</span>' +
          '</span></label>';
      });
      html += '</div>' +
        '<div class="rqcd__learn-actions">' +
          '<button type="button" class="rqcd__btn" id="rqcdLearnCancel">Discard</button>' +
          '<button type="button" class="rqcd__btn rqcd__btn--end" id="rqcdLearnSave">Save ticked</button>' +
        '</div>' +
        '<div class="rqcd__learn-status" id="rqcdLearnStatus" hidden></div>';
      el.innerHTML = html;
      var self = this;
      document.getElementById('rqcdLearnCancel').addEventListener('click', function () { self.close(); });
      document.getElementById('rqcdLearnSave').addEventListener('click', function () { self.save(false); });
    },

    save(noteOnly) {
      var self = this;
      var sid = this.sid;
      if (!sid) return;
      var kb = [], corr = [];
      if (!noteOnly && this.data) {
        var boxes = (this.el() || document).querySelectorAll('input[type="checkbox"]');
        Array.prototype.forEach.call(boxes, function (b) {
          if (!b.checked) return;
          var i = parseInt(b.dataset.i, 10);
          if (b.dataset.kind === 'kb') kb.push(self.data.kb[i]);
          else corr.push(self.data.corr[i]);
        });
        if (!kb.length && !corr.length) { this.close(); return; }
      }
      this.setLearnStatus('Saving…', true);
      RepAPI.fetchExternal(CHAT_WORKER, '/learn/apply', {
        method: 'POST',
        body: JSON.stringify({ sessionId: sid, note: this.note || '', kbEntries: kb, corrections: corr }),
      }).then(function (d) {
        if (!d || d.error) throw new Error((d && d.error) || 'save failed');
        var n = (d.kbSaved || 0) + (d.correctionsSaved || 0);
        var msg = n ? ('Saved ' + n + ' to the knowledge base.') : 'Note saved.';
        var sink = self.onStatus;
        self.close();
        if (sink) { try { sink(msg); } catch (e) {} }
      }).catch(function (e) {
        self.setLearnStatus('Could not save. ' + (e && e.message ? e.message : ''), false);
      });
    },
  };

  /* CUSTOM_CHATSCROLL_BY_CLAUDE (Ryan 2026-10-11) — A LIVE CHAT THAT LETS YOU READ BACK.
     ─────────────────────────────────────────────────────────────────────────────────────────
     Every new message, every typing-dots flicker and every transcript refresh used to set
     scrollTop to the bottom, so an admin scrolled up to catch up on the bot's conversation was
     yanked down again every few seconds. One rule for both live chat views (the dock and the
     Live Chat page): if the reader is AT THE BOTTOM, follow the conversation as before; if they
     have scrolled up, leave them exactly where they are, mark the first message they have not
     seen with the NEW divider, and float a "3 new messages ↓" pill over the bottom of the thread.
     The pill goes away once the NEW divider (or the bottom) comes into view; clicking it scrolls
     to the divider. The reader's own message always takes them to the bottom.
     ⛔ Decide "at the bottom" BEFORE the change (snapshot), never after: once a message is
     appended, everyone is "not at the bottom" by its height.
     The pill sits in a zero-height holder right after the scrolling element, so it overlays the
     thread without being one of its children (several callers dedupe on lastElementChild). */
  var RQCS_NEAR_PX = 48;
  function rqChatScrollFor(el, opts) {
    if (!el) return null;
    if (el._rqcs) { if (opts) el._rqcs.setOpts(opts); return el._rqcs; }
    var o = opts || {};
    var unseen = 0, holder = null, pill = null;
    function atBottom() { return el.scrollHeight - el.scrollTop - el.clientHeight <= RQCS_NEAR_PX; }
    function pin() { el.scrollTop = el.scrollHeight; }
    function divider() {
      if (o.divider) return o.divider();
      return el.querySelector('.rqcs__divider');
    }
    function placeDivider(first) {
      if (o.placeDivider) { o.placeDivider(first); return; }
      var old = el.querySelector('.rqcs__divider');
      if (old) old.remove();
      var d = document.createElement('div');
      d.className = 'rqcs__divider';
      d.innerHTML = '<span>New</span>';
      el.insertBefore(d, first);
    }
    function ensurePill() {
      if (holder && holder.parentNode && holder.previousElementSibling === el) return;
      if (!el.parentNode) return;
      if (holder && holder.parentNode) holder.remove();
      holder = document.createElement('div');
      holder.className = 'rqcs__holder';
      pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'rqcs__pill';
      pill.hidden = true;
      pill.addEventListener('click', jump);
      holder.appendChild(pill);
      el.parentNode.insertBefore(holder, el.nextSibling);
    }
    function paint() {
      ensurePill();
      if (!pill) return;
      if (unseen > 0) {
        pill.hidden = false;
        pill.innerHTML = '<span>' + unseen + ' new message' + (unseen === 1 ? '' : 's') + '</span>'
          + '<span class="rqcs__arrow" aria-hidden="true">&darr;</span>';
      } else {
        pill.hidden = true;
      }
    }
    function dividerInView() {
      var d = divider();
      if (!d || !d.parentNode || !el.contains(d)) return false;
      var er = el.getBoundingClientRect(), dr = d.getBoundingClientRect();
      return dr.top >= er.top && dr.bottom <= er.bottom;
    }
    function seen() {
      if (!unseen) return;
      unseen = 0;
      paint();
      if (o.onSeen) o.onSeen();
    }
    function check() { if (unseen && (atBottom() || dividerInView())) seen(); }
    function jump() {
      var d = divider();
      if (d && d.parentNode && el.contains(d)) {
        el.scrollTop += d.getBoundingClientRect().top - el.getBoundingClientRect().top - 8;
      } else {
        pin();
      }
      check();
    }
    el.addEventListener('scroll', check, { passive: true });
    var k = {
      atBottom: atBottom,
      pin: pin,
      unseen: function () { return unseen; },
      setOpts: function (next) { o = next || {}; },
      snapshot: function () { return { stick: atBottom(), top: el.scrollTop }; },
      /* info: {added: real messages added, firstNew: the first of them, own: the reader sent it,
         fresh: a different conversation was just drawn}. Returns true when it pinned. */
      settle: function (snap, info) {
        info = info || {};
        if (info.fresh || info.own) {
          if (info.own) seen(); else { unseen = 0; paint(); }
          pin();
          return true;
        }
        if (!snap || snap.stick) { pin(); check(); return true; }
        el.scrollTop = snap.top;
        if ((info.added || 0) > 0 && info.firstNew) {
          if (!unseen) placeDivider(info.firstNew);
          unseen += info.added;
          paint();
        }
        return false;
      },
      reset: function () { unseen = 0; paint(); },
    };
    el._rqcs = k;
    return k;
  }
  window.RqChatScroll = { for: rqChatScrollFor };

  const RepChatDock = {
    session: null,
    chats: {},            // sessionId → summary from the inbox
    inboxWs: null,
    inboxRetries: 0,
    inboxPing: null,
    everConnected: false, // the worker has accepted this rep at least once
    roomWs: null,         // socket for the conversation we are holding OR previewing
    roomSid: null,
    roomWatch: false,     // true = connected read-only, no admin_join sent
    holding: false,       // true once the worker confirms intercept_started
    /* Every conversation this rep has typed in since the page loaded, and every
       one they have already been offered the wrap-up for. Kept apart from
       `holding` on purpose — see offerWrapUp for what conflating them cost. */
    heldSids: {},
    /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-25) — the conversation this rep
       still holds while looking at a DIFFERENT one. There is a single room
       socket, so opening another conversation closes the held one's and
       `holding` goes false locally; the worker keeps the takeover alive for a
       90s grace and returning reclaims it. Without this the chip's live dot
       vanished the instant you switched away, which is the one moment you most
       need it: a customer is still waiting on a HUMAN in there. */
    heldAwaySid: null,
    wrapOffered: {},
    /* sessionId → we have already chimed for this conversation, so shut up
       about it. Cleared when a takeover ends, so the bot getting it back earns
       exactly one more chime. See announceFor. */
    announced: {},
    /* Messages that arrived in the conversation this rep is HOLDING while the
       popup was closed. The server cannot supply this: AdminInbox deliberately
       stops counting unread for a held chat ("the rep is reading it live"),
       which is right while the popup is open and wrong the moment they press
       the x. Ryan, 2026-08-17: closing the popup is a minimise, not an exit,
       and the count has to say what was missed. Cleared on reopening. */
    missedHeld: 0,
    activeSid: null,      // conversation shown in the thread pane
    thread: [],
    pendingSends: {},
    sentBubbles: {},   // server timestamp → bubble, for read receipts
    sendSeq: 0,
    mounted: false,
    baseTitle: null,
    /* Embedded mode: the portal chatbot widget is on the page, so it owns the
       bubble and the window and this dock is just the engine plus a panel it
       lends out. Standalone (own bubble) is the fallback for a portal page
       that somehow has no widget. */
    embedded: false,

    init(session) {
      try {
        if (this.mounted) return;
        if (!session || !session.code) return;
        if ((session.role || '') === 'agency') return; // agency users never see customer chats
        /* ADMIN ONLY. Customer chat alerts, and the ability to speak to a
           customer as the bot, are an admin capability — Ryan impersonated a
           non-admin and found the live-chat menu sitting there. Check
           session.admin, NOT session.role: role reads "rep" for admins. The
           worker enforces the same rule on /ws/inbox and /ws/admin; this just
           keeps the door from being drawn for people who cannot open it. */
        if (!session.admin) return;
        this.session = session;
        this.baseTitle = document.title;
        // Which conversations this TAB has already chimed for. Survives portal
        // navigations, which are full reloads; see loadAnnounced.
        this.announced = this.loadAnnounced();
        // Survives a portal navigation; see loadMissedHeld.
        this.missedHeld = this.loadMissedHeld();
        this.embedded = !!document.getElementById('bschat');
        RepChatAlert.watchWidgetVisibility(this);
        this.mount();
        this.mounted = true;
        this.restoreUiState();
        this.connectInbox();
      } catch (e) {
        console.warn('Chat dock init failed:', e);
      }
    },

    /* ---------- DOM ---------- */

    mount() {
      var host = document.createElement('div');
      host.id = 'rqChatDock';
      host.className = 'rqcd';
      host.innerHTML =
        '<button class="rqcd__bubble" id="rqcdBubble" type="button" aria-label="Live customer chats">' +
          '<span class="rqcd__bubble-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
              '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>' +
            '</svg>' +
          '</span>' +
          '<span class="rqcd__bubble-label" id="rqcdBubbleLabel">NEW CHAT</span>' +
          '<span class="rqcd__badge" id="rqcdBadge" hidden>0</span>' +
        '</button>' +
        '<section class="rqcd__panel" id="rqcdPanel" hidden aria-label="Live chats">' +
          '<header class="rqcd__head">' +
            '<span class="rqcd__head-title" id="rqcdHeadTitle">Live chats</span>' +
            '<span class="rqcd__conn" id="rqcdConn" title="Connecting"></span>' +
            '<button class="rqcd__icon-btn" id="rqcdBack" type="button" hidden title="Back to list">&larr;</button>' +
            '<button class="rqcd__icon-btn" id="rqcdClose" type="button" title="Close">&times;</button>' +
          '</header>' +
          '<div class="rqcd__list" id="rqcdList"></div>' +
          '<div class="rqcd__thread-wrap" id="rqcdThreadWrap" hidden>' +
            /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-25) — conversation switcher.
               With two customers live at once the list was the ONLY way between
               them, and the way back to it was broken (see mountInto), so a rep
               who entered one chat was stuck in it. One chip per live
               conversation, each carrying its own unread count, pinned above
               the transcript: switching is one click, and the other chat's
               messages stay visible while you type in this one. Hidden with a
               single conversation, where it would only be chrome. */
            '<div class="rqcd__switch" id="rqcdSwitch" hidden role="tablist" aria-label="Live conversations"></div>' +
            '<div class="rqcd__meta" id="rqcdMeta"></div>' +
            '<div class="rqcd__thread" id="rqcdThread"></div>' +
            /* Status and buttons share one row. As two stacked full-width
               blocks they ate a lot of the panel for very little information. */
            '<div class="rqcd__bar">' +
              '<span class="rqcd__status" id="rqcdStatus" hidden></span>' +
              '<div class="rqcd__actions" id="rqcdActions">' +
                '<button class="rqcd__btn rqcd__btn--join" id="rqcdTakeOver" type="button">Take Over</button>' +
                '<button class="rqcd__btn" id="rqcdHandBack" type="button" hidden>Hand Back</button>' +
                '<button class="rqcd__btn rqcd__btn--end" id="rqcdEnd" type="button" hidden>End Chat</button>' +
              '</div>' +
            '</div>' +
            '<div class="rqcd__compose" id="rqcdCompose" hidden>' +
              '<textarea class="rqcd__input" id="rqcdInput" rows="1" aria-label="Reply as the bot" autocomplete="off"></textarea>' +
              '<button class="rqcd__send" id="rqcdSend" type="button">Send</button>' +
            '</div>' +
          '</div>' +
        '</section>';
      document.body.appendChild(host);

      var self = this;
      var $ = function (id) { return document.getElementById(id); };
      $('rqcdBubble').addEventListener('click', function () { self.toggleDock(); });
      $('rqcdClose').addEventListener('click', function () { self.closeDock(); });
      $('rqcdBack').addEventListener('click', function () { self.showList(); });
      $('rqcdTakeOver').addEventListener('click', function () { self.takeOver(); });
      $('rqcdHandBack').addEventListener('click', function () { self.handBack(); });
      $('rqcdEnd').addEventListener('click', function () { self.endChat(); });
      $('rqcdSend').addEventListener('click', function () { self.send(); });
      /* Enter sends, Shift+Enter breaks the line. It was an <input>, so a
         multi-line reply was impossible — everything arrived as one paragraph. */
      var input = $('rqcdInput');
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); self.send(); }
      });
      input.addEventListener('input', function () {
        self.autoGrow(input);
        self.notifyTyping();
      });
      input.addEventListener('blur', function () { self.notifyTyping(false); });
      // Cursor in here == actively working the chat, so incoming messages stay
      // quiet; focusing it also clears a live alarm.
      RepChatAlert.watchComposer('rqcdInput');

      this.syncTheme();
      this.watchTheme();

      if (this.embedded) {
        // The widget supplies the bubble and the frame. Ours stay parked.
        $('rqcdBubble').hidden = true;
        $('rqcdClose').hidden = true;
        host.classList.add('rqcd--host-parked');
      }
    },

    /* ---- embedding API (used by the portal chatbot widget) ---- */

    /** Move the live panel into the widget's window. Moving the node rather
        than re-rendering keeps every listener and the current view intact. */
    /* Inside the widget the conversation must look like the widget's own
       conversation, not like a panel bolted into it. Rather than copy the
       bubble styling across (which drifts the moment either side changes), the
       thread borrows the widget's actual classes and is styled by the widget's
       actual CSS. Standalone mode keeps the .rqcd__* look. */
    adoptWidgetSkin(on) {
      var map = [
        ['rqcdThread', 'bschat__messages'],
        ['rqcdCompose', 'bschat__input-area'],
        ['rqcdInput', 'bschat__input'],
        ['rqcdSend', 'bschat__send'],
      ];
      map.forEach(function (pair) {
        var el = document.getElementById(pair[0]);
        if (el) el.classList[on ? 'add' : 'remove'](pair[1]);
      });
    },

    /** Class list for one message bubble, in whichever skin is active.
     *
     * Note the deliberate inversion when embedded, and do not "correct" it.
     * The widget's --user bubble is the right-hand purple one (the person
     * holding the window) and --bot is the left-hand grey one (whoever they
     * are talking to). A rep reading a customer conversation is on OUR side:
     * the bot's replies and their own replies both belong on the right, and
     * the CUSTOMER goes on the left. Mapping role names straight across put
     * the customer on the rep's own side of the thread.
     *
     * Bot and rep messages look identical on purpose — the customer cannot
     * tell them apart either, so neither should the transcript.
     */
    msgClass(kind) {
      if (this.embedded) {
        return 'bschat__msg bschat__msg--' + (kind === 'user' ? 'bot' : 'user');
      }
      return 'rqcd__msg ' + (kind === 'user' ? 'rqcd__msg--user' : 'rqcd__msg--bot') +
        (kind === 'human' ? ' rqcd__msg--human' : '');
    },

    mountInto(container) {
      var panel = document.getElementById('rqcdPanel');
      if (!panel || !container) return;
      container.classList.add('rqcd', 'rqcd--live', 'rqcd--embedded');
      this.syncTheme();
      container.appendChild(panel);
      panel.hidden = false;
      this.adoptWidgetSkin(true);
      this.render();
      // The panel only just gained layout — pin the thread now it can scroll.
      this.scrollThreadToBottom();
      if (!this.activeSid) {
        var ids = Object.keys(this.chats).filter(function (sid) {
          return !this.chats[sid].endedAt;
        }, this);
        if (ids.length === 1) this.selectChat(ids[0], true);
      }
      /* \u26d4 RE-ANNOUNCE THE LEVEL ON MOUNT \u2014 this is what broke the way back.
         (Ryan 2026-08-25.) The widget derives its back arrow from bsdock:view
         AND its own portalView: `hidden = !(onThread && portalView==='live')`.
         selectChat fires 'thread' from restoreUiState at PAGE LOAD, when
         portalView is still 'home', so the arrow was computed hidden \u2014 and
         nothing ever recomputed it, because showLive() sets portalView='live'
         and then calls mountInto, which rendered the thread and announced
         nothing. Result: open the widget, land in the conversation you were
         last in, and no control anywhere reaches the other one. */
      this.announceView(this.activeSid ? 'thread' : 'list');
      return panel;
    },

    /** Park the panel back on the hidden host so its state survives. */
    unmount() {
      var panel = document.getElementById('rqcdPanel');
      var host = document.getElementById('rqChatDock');
      if (!panel || !host) return;
      this.adoptWidgetSkin(false);
      panel.hidden = true;
      host.appendChild(panel);
    },

    unreadCount() { return this.totalUnread(); },

    /* The --rq-* palette is scoped to .rep-quote-portal-wrap, and this dock is
       a child of <body> so it can be position:fixed without inheriting some
       ancestor's transform. Scoped vars do not reach it, so the dock carries
       its own tokens and mirrors the portal's dark class onto itself. */
    /* Only the standalone host follows the portal's dark mode. The embedded
       container sits inside the chatbot widget's window, which is always
       white — dark tokens in there would paint dark text on a light card. */
    syncTheme() {
      var host = document.getElementById('rqChatDock');
      if (!host) return;
      var dark = !!document.querySelector('.rep-quote-portal-wrap.rep-quote-dark');
      host.classList.toggle('rqcd--dark', dark && !this.embedded);
    },

    watchTheme() {
      var self = this;
      var wrap = document.querySelector('.rep-quote-portal-wrap');
      if (!wrap || !window.MutationObserver) return;
      try {
        new MutationObserver(function () { self.syncTheme(); })
          .observe(wrap, { attributes: true, attributeFilter: ['class'] });
      } catch (e) {}
    },

    /* Dock open/closed and the selected conversation survive a page nav —
       portal pages are full page loads, so without this every hop between
       tools would dump the rep back to a closed bubble mid-conversation. */
    restoreUiState() {
      try {
        if (!this.embedded && sessionStorage.getItem('rqcd_open') === '1') this.openDock(true);
        var sid = sessionStorage.getItem('rqcd_sid');
        if (!sid) return;

        if (!this.embedded) { this.selectChat(sid, true); return; }

        /* Embedded: reclaim the conversation the rep was holding before the
           page changed. Previously this was skipped entirely on the theory the
           widget would drive it — nothing did, so a rep who clicked from the
           Deal Dashboard to the Workstation mid-chat came back to the menu and
           had to press Take Over again. The worker holds the intercept for a
           90s grace window, so re-joining reclaims it rather than starting a
           new takeover. */
        var wasHolding = sessionStorage.getItem('rqcd_holding') === '1';
        /* ⛔ Tell selectChat NOT to open its read-only preview socket here.
           Both would mint a ticket for the same sid, both would pass
           connectRoom's `roomSid !== sid` staleness check, and the second
           openRoom would overwrite this.roomWs while the first socket stayed
           open and unreferenced — still firing onRoomEvent, so every message
           would render twice. */
        this.selectChat(sid, true, wasHolding);
        if (wasHolding) this.connectRoom(sid);
      } catch (e) {}
    },

    /* ---------- inbox socket ---------- */

    /* A WebSocket URL is a public thing — it cannot carry an Authorization
       header, it lands in logs, and anyone can type one. So it no longer
       carries a rep code (two guessable letters that the worker used to trust
       outright); it carries a signed ticket, minted over authenticated HTTP
       with the portal JWT and good for two minutes. RepAPI attaches the token
       and handles refresh, so this inherits the session handling every other
       portal call already has. */
    wsTicket() {
      return RepAPI.fetchExternal(CHAT_WORKER, '/ws/ticket', { method: 'POST' })
        .then(function (d) { return (d && d.ticket) || null; })
        .catch(function () { return null; });
    },

    connectInbox() {
      var self = this;
      if (this.inboxWs && (this.inboxWs.readyState === WebSocket.OPEN || this.inboxWs.readyState === WebSocket.CONNECTING)) return;
      // Minting is async, so guard the window between asking and connecting —
      // without this, a retry timer and a page event can both be in flight and
      // open two sockets.
      if (this._inboxTicketPending) return;
      this._inboxTicketPending = true;
      this.wsTicket().then(function (ticket) {
        self._inboxTicketPending = false;
        if (!ticket) { self.scheduleInboxRetry(); return; }
        self.openInbox(ticket);
      });
    },

    openInbox(ticket) {
      var self = this;
      var url = CHAT_WORKER.replace('https://', 'wss://') + '/ws/inbox' +
        '?ticket=' + encodeURIComponent(ticket);
      try {
        this.inboxWs = new WebSocket(url);
      } catch (e) {
        this.scheduleInboxRetry();
        return;
      }

      this.inboxWs.onopen = function () {
        self.inboxRetries = 0;
        self.everConnected = true;
        // Only reveal the bubble once the worker has actually accepted us. The
        // chat rep roster is server-side (REP_CODES), so a portal user who is
        // not on it simply never sees a dock rather than a dead button.
        var host = document.getElementById('rqChatDock');
        if (host) host.classList.add('rqcd--live');
        self.setConn(true);
        /* The widget keeps its Live Customer Chats button hidden until this
           lands, so the entry point only ever appears for someone the worker
           has actually accepted as an admin. */
        try { document.dispatchEvent(new CustomEvent('bsdock:ready')); } catch (e) {}
        // Cheap keep-alive. The DO answers this without waking, so an all-day
        // socket costs nothing and is still alive when the first chat lands.
        clearInterval(self.inboxPing);
        self.inboxPing = setInterval(function () {
          try { self.inboxWs.send('ping'); } catch (e) {}
        }, 25000);
      };

      this.inboxWs.onmessage = function (evt) {
        if (evt.data === 'pong') return;
        var data;
        try { data = JSON.parse(evt.data); } catch (e) { return; }
        self.onInboxEvent(data);
      };

      this.inboxWs.onclose = function () {
        self.inboxWs = null;
        self.setConn(false);
        clearInterval(self.inboxPing);
        self.scheduleInboxRetry();
      };
    },

    scheduleInboxRetry() {
      var self = this;
      // Never connected and out of patience: this session's rep code is not on
      // the chat roster (the worker 401s it). Stop knocking.
      if (!this.everConnected && this.inboxRetries >= 5) {
        var host = document.getElementById('rqChatDock');
        if (host) host.classList.remove('rqcd--live');
        return;
      }
      var delay = Math.min(1000 * Math.pow(2, Math.min(this.inboxRetries, 5)), 30000);
      this.inboxRetries++;
      setTimeout(function () { self.connectInbox(); }, delay);
    },

    /* ⛔ ONE CHIME PER CONVERSATION, WHILE THE BOT HAS IT.
       Ryan, 2026-08-17: "it's really important the beeps don't continue for
       every message after the first one when the admin decides not to enter the
       chat." If nobody has picked up by message two, they are busy and the bot
       is handling it — the red bubble, the badge and the tab title carry it from
       there. A chat that beeps on every message teaches people to ignore
       beeping, which costs us the one that mattered.

       Kept as its own function, and given the `announced` map explicitly, so it
       can be tested without standing up a socket — this gate has now been wrong
       twice. Covered by chatbot-worker/test/rep-chat-announce.test.js.

       It RECORDS as well as decides (sets announced[sid] when it returns true).

       Three earlier attempts and why they were wrong:
         `unread === 1`  — marking a chat read then getting another message
                           re-fires it, which is the same nagging by another route.
         `!prev`         — right for the opening message, but a conversation stays
                           known forever, so after a rep hands back to the bot it
                           could never chime again. Ryan wants exactly one more.
         no gate at all  — the original: chimed on every single message. */
    /* ⛔ `announced` MUST SURVIVE A PORTAL PAGE CHANGE.
       Every portal navigation is a full reload, so an in-memory map means each
       page change re-arms the chime for every conversation already running —
       which is exactly the "it beeped again" Ryan saw after clicking through to
       another tool (2026-08-17). sessionStorage is per-tab and dies with it,
       which is the right lifetime: a genuinely new browser session should hear
       about conversations it has never been told about.

       Belt and braces with the snapshot seeding in onInboxEvent, which covers
       the first load of the tab. This covers every load after it, and closes
       the race where a message lands between the socket opening and the
       snapshot arriving. */
    ANNOUNCED_KEY: 'rqcd_announced',

    loadAnnounced() {
      try {
        return JSON.parse(sessionStorage.getItem(this.ANNOUNCED_KEY) || '{}') || {};
      } catch (e) { return {}; }
    },

    saveAnnounced() {
      // A bare setItem throws when storage is full, and an unguarded throw here
      // would take out the whole inbox event handler.
      try { sessionStorage.setItem(this.ANNOUNCED_KEY, JSON.stringify(this.announced)); } catch (e) {}
    },

    announceFor(prev, chat, announced) {
      var sid = chat.sessionId;
      /* A takeover just ended (hand back, idle timeout, dropped connection):
         the bot has it again, so this conversation is once more something
         nobody is watching, and earns exactly one more chime. */
      if (prev && prev.takenOverBy && !chat.takenOverBy) announced[sid] = false;

      var newlyUnread = (chat.unread || 0) > ((prev && prev.unread) || 0);
      if (!newlyUnread) return null;        // nothing new arrived
      if (chat.takenOverBy) return null;    // held: heldMessage() owns this, off the room socket

      /* \u26d4 A CONVERSATION BEHIND ANOTHER TAB IS NOT ONE YOU HAVE SEEN.
         (Ryan 2026-08-25.) The once-per-conversation rule assumes the rep can
         watch the chat climb \u2014 true on the list or on the bubble, false when
         they are inside a DIFFERENT conversation, where nothing but a chip
         badge moves. Two customers at once meant the second one's follow-ups
         were completely silent. So: still once-per-conversation at rest, but
         every message announces while the rep is reading somebody else. */
      if (announced[sid]) {
        return (sid !== this.activeSid && this.threadOnScreen()) ? 'chime' : 'title';
      }
      announced[sid] = true;
      return 'chime';                       // first message, or first since a hand-back
    },

    /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-26) — ⛔ "I HOLD THE CONVERSATION I AM LOOKING AT".
       ─────────────────────────────────────────────────────────────────────────────────────────
       `holding` alone is no longer enough to answer that. The room socket now STAYS on the
       conversation this rep took over even when they open another one to read it (see
       selectChat), so `holding` can be true while the transcript on screen belongs to somebody
       else entirely. Everything that types, sends, or offers to send has to ask this instead —
       ⛔ ESPECIALLY send(): firing over `roomWs` while the rep believes they are writing to the
       conversation in front of them would put a reply in a DIFFERENT customer's chat. */
    holdingActive() {
      return !!(this.holding && this.roomSid && this.roomSid === this.activeSid);
    },

    /* The socket is open on a conversation that is NOT the one on screen. Used to keep the
       transcript from being painted with the wrong chat's messages. */
    roomOffScreen() {
      return !!(this.roomSid && this.activeSid && this.roomSid !== this.activeSid);
    },

    /* True when the panel is open on a transcript (as opposed to the list, or
       closed altogether). Deliberately says nothing about WHICH transcript. */
    threadOnScreen() {
      try {
        var panel = document.getElementById('rqcdPanel');
        if (!panel || panel.hidden || panel.offsetParent === null) return false;
        var wrap = document.getElementById('rqcdThreadWrap');
        return !!(wrap && !wrap.hidden);
      } catch (e) { return false; }
    },

    onInboxEvent(data) {
      if (data.type === 'snapshot') {
        this.chats = {};
        (data.chats || []).forEach(function (c) {
          this.chats[c.sessionId] = c;
          /* Conversations already in flight when this page loaded are NOT new
             arrivals — announcing them would chime on every portal navigation. */
          this.announced[c.sessionId] = true;
        }, this);
        this.saveAnnounced();
      } else if (data.type === 'chat_update' && data.chat) {
        var prev = this.chats[data.chat.sessionId];
        this.chats[data.chat.sessionId] = data.chat;
        /* chime = sound + bubble ping + tab title (once per conversation).
           title = tab title only (every message, so the tab keeps saying the
           conversation is still running). The red bubble and the cumulative
           count are not decided here at all — render() derives them from
           unread, which is why they keep climbing on every message. */
        var announce = this.announceFor(prev, data.chat, this.announced);
        if (announce) this.saveAnnounced();   // survive the next portal navigation
        if (announce === 'chime') this.alert();
        else if (announce === 'title') RepChatAlert.alarmIfAway();
        if (this.activeSid === data.chat.sessionId) {
          this.renderMeta(); // keep "on <page>" current as they browse
          this.refreshThread();
          /* ⛔ READING A CHAT COUNTS AS READING IT, EVEN WITHOUT JOINING.
             selectChat marks read once, on the click. Anything arriving while
             the rep sits there reading then piled straight back onto the
             counter, because the server only stops counting for a chat someone
             has TAKEN OVER, and previewing deliberately does not do that. So a
             rep who read a conversation and closed the popup was left with a
             number on the light for messages they had watched arrive, and the
             next one started at 2. Ryan, 2026-08-17: it should be back to the
             green light, and the next message starts at 1.

             Zeroed locally as well as on the server so the light does not flash
             a number for one render while the round trip completes. */
          if (this.watchingThread(data.chat.sessionId) && (data.chat.unread || 0) > 0) {
            data.chat.unread = 0;   // same object as this.chats[sid]
            this.inboxSend({ type: 'mark_read', sessionId: data.chat.sessionId });
          }
        }
      } else if (data.type === 'chat_removed') {
        delete this.chats[data.sessionId];
        if (this.heldAwaySid === data.sessionId) this.heldAwaySid = null;
        // Pruned after 45 min cold or 60s after ending. If the same session
        // ever comes back it is a genuinely new arrival again.
        delete this.announced[data.sessionId];
        if (this.activeSid === data.sessionId) this.showList();
      } else {
        return;
      }
      this.render();
    },

    /* ---------- alerting ---------- */

    totalUnread() {
      var n = 0;
      for (var sid in this.chats) n += (this.chats[sid].unread || 0);
      return n;
    },

    /* Unread belonging to conversations OTHER than the one on screen, plus a
       minimised takeover's tally when the held chat is not the one on screen.
       This is the "somebody else is waiting" number the switcher chips and the
       widget's bubble both key off. */
    unreadElsewhere() {
      var n = 0;
      for (var sid in this.chats) {
        if (sid === this.activeSid) continue;
        n += (this.chats[sid].unread || 0);
      }
      if (this.roomSid && this.roomSid !== this.activeSid) n += (this.missedHeld || 0);
      return n;
    },

    /* True when this rep holds a conversation that is not the one on screen —
       the state the live dot on the switcher chip is drawing. */
    holdingElsewhere() {
      if (this.heldAwaySid && this.heldAwaySid !== this.activeSid) return true;
      return !!(this.holding && this.roomSid && this.roomSid !== this.activeSid);
    },

    /* The once-per-conversation announcement: sound + bubble ping, plus the
       tab title if the rep is away. Fired only when announceFor() says 'chime'
       — a conversation's first message, or its first since a hand-back.
       Every message after that gets the tab title alone, and the red bubble
       and cumulative count come from render() off `unread`, not from here. */
    alert() {
      RepChatAlert.newChat();
      var b = document.getElementById('rqcdBubble');
      if (b) { b.classList.remove('rqcd__bubble--ping'); void b.offsetWidth; b.classList.add('rqcd__bubble--ping'); }
    },

    // Kept as a method because call sites use this.beep(); the implementation
    // moved to RepChatAlert so the chatbot admin page can share it.
    beep() { RepChatAlert.beep(); },

    setConn(ok) {
      var el = document.getElementById('rqcdConn');
      if (el) {
        el.classList.toggle('rqcd__conn--on', !!ok);
        el.title = ok ? 'Connected' : 'Reconnecting…';
      }
    },

    /* ---------- rendering ---------- */

    render() {
      /* Unattended conversations are counted by the server; a minimised
         takeover is counted here. Adding them is what makes the badge the
         cumulative "things waiting on you" number Ryan asked for. */
      var unread = this.totalUnread() + (this.missedHeld || 0);
      var host = document.getElementById('rqChatDock');
      var badge = document.getElementById('rqcdBadge');
      var bubble = document.getElementById('rqcdBubble');
      if (!host || !badge || !bubble) return;

      var live = Object.keys(this.chats).length;
      host.classList.toggle('rqcd--has-chats', live > 0);
      bubble.classList.toggle('rqcd__bubble--alert', unread > 0);
      badge.hidden = unread === 0;
      badge.textContent = unread > 9 ? '9+' : String(unread);

      // Tab title carries the count so a backgrounded portal still shouts.
      // Routed through RepChatAlert rather than written directly: while the
      // NEW MESSAGE alarm is up, this must not quietly overwrite it, and when
      // the alarm clears this count is what it has to fall back to.
      RepChatAlert.setResting(unread > 0 ? '(' + unread + ') ' + this.baseTitle : this.baseTitle);

      // In embedded mode the chatbot widget owns the bubble, so tell it what to
      // show rather than reaching across files into its DOM.
      try {
        /* `holding` drives the third indicator state: an active takeover that
           has been minimised shows a RED light with no number, so the rep has a
           standing reminder that a customer is mid-conversation with them. The
           number is then laid on top as messages arrive. */
        document.dispatchEvent(new CustomEvent('bsdock:unread', {
          detail: {
            count: unread, chats: live, holding: !!this.holding,
            /* How much of that count is NOT the conversation on screen. The
               widget suppresses its red bubble while the live view is open;
               without this it suppressed it inside a conversation too, hiding
               the second customer entirely. (Ryan 2026-08-25.) */
            elsewhere: this.unreadElsewhere(),
          },
        }));
      } catch (e) {}

      this.renderList();
      this.renderSwitch();
      this.renderActions();
    },

    /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-25) — the tab strip above the
       transcript. Order comes from Object.keys(this.chats), which is the server
       snapshot's order and is STABLE: sorting by last activity would reshuffle
       the chips under the rep's cursor every time a message landed, which is
       the one thing a switcher must never do. Ended conversations drop out of
       the strip unless one is what you are currently reading. */
    renderSwitch() {
      var el = document.getElementById('rqcdSwitch');
      if (!el) return;
      var self = this;
      var sids = Object.keys(this.chats).filter(function (sid) {
        return !self.chats[sid].endedAt || sid === self.activeSid;
      });
      // One conversation needs no switcher; the header already names it.
      if (sids.length < 2) { el.hidden = true; el.innerHTML = ''; return; }
      el.hidden = false;
      var html = '';
      sids.forEach(function (sid) {
        var c = self.chats[sid];
        var active = sid === self.activeSid;
        /* The held conversation is the one with a customer waiting on a HUMAN.
           It has to be findable at a glance whichever chip is open. */
        var held = !!((self.holding && self.roomSid === sid) || self.heldAwaySid === sid);
        var unread = active ? 0 : (c.unread || 0) + (held ? (self.missedHeld || 0) : 0);
        var cls = 'rqcd__tab';
        if (active) cls += ' rqcd__tab--active';
        if (unread) cls += ' rqcd__tab--unread';
        if (held) cls += ' rqcd__tab--held';
        if (c.endedAt) cls += ' rqcd__tab--ended';
        var who = (c.contactInfo && c.contactInfo.name) ? c.contactInfo.name : 'Visitor';
        html += '<button type="button" class="' + cls + '" data-sid="' + escAttr(sid) + '"' +
          ' role="tab" aria-selected="' + (active ? 'true' : 'false') + '"' +
          ' title="' + escAttr(who + (held ? ' \u2014 you are holding this chat' : '')) + '">' +
          (held ? '<span class="rqcd__tab-live" aria-hidden="true"></span>' : '') +
          '<span class="rqcd__tab-who">' + escHtml(who) + '</span>' +
          (unread ? '<span class="rqcd__tab-count">' + (unread > 9 ? '9+' : unread) + '</span>' : '') +
        '</button>';
      });
      el.innerHTML = html;
      Array.prototype.forEach.call(el.querySelectorAll('.rqcd__tab'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.dataset.sid !== self.activeSid) self.selectChat(btn.dataset.sid);
        });
      });
    },

    renderList() {
      var el = document.getElementById('rqcdList');
      if (!el) return;
      var list = Object.values(this.chats).sort(function (a, b) {
        return (b.lastActivity || 0) - (a.lastActivity || 0);
      });
      if (list.length === 0) {
        el.innerHTML = '<div class="rqcd__empty">No live chats right now.<br>You will hear a chime the moment one starts.</div>';
        return;
      }
      var self = this;
      var html = '';
      list.forEach(function (c) {
        var cls = 'rqcd__row';
        if (c.unread) cls += ' rqcd__row--unread';
        if (c.endedAt) cls += ' rqcd__row--ended';
        if (self.activeSid === c.sessionId) cls += ' rqcd__row--active';
        var who = (c.contactInfo && c.contactInfo.name) ? c.contactInfo.name : 'Visitor';
        var where = c.productTitle || self.prettyLocation(c.pageUrl);
        html += '<button type="button" class="' + cls + '" data-sid="' + escAttr(c.sessionId) + '">' +
          '<span class="rqcd__row-top">' +
            '<span class="rqcd__row-who">' + escHtml(who) + '</span>' +
            (c.highValueLead ? '<span class="rqcd__tag rqcd__tag--hot">HOT</span>' : '') +
            (c.takenOverBy ? '<span class="rqcd__tag">' + escHtml(c.takenOverByName || c.takenOverBy) + '</span>' : '') +
            (c.endedAt ? '<span class="rqcd__tag">ended</span>' : '') +
            (c.unread ? '<span class="rqcd__row-count">' + c.unread + '</span>' : '') +
          '</span>' +
          '<span class="rqcd__row-msg">' + escHtml(c.lastMessage || '') + '</span>' +
          '<span class="rqcd__row-where">' + escHtml(where) + '</span>' +
        '</button>';
      });
      el.innerHTML = html;
      Array.prototype.forEach.call(el.querySelectorAll('.rqcd__row'), function (row) {
        row.addEventListener('click', function () { self.selectChat(row.dataset.sid); });
      });
    },

    shortPath(url) {
      if (!url) return '';
      try { return new URL(url, window.location.origin).pathname; } catch (e) { return String(url); }
    },

    renderActions() {
      var take = document.getElementById('rqcdTakeOver');
      var back = document.getElementById('rqcdHandBack');
      var end = document.getElementById('rqcdEnd');
      var compose = document.getElementById('rqcdCompose');
      if (!take) return;
      var chat = this.chats[this.activeSid];
      var heldByOther = chat && chat.takenOverBy && chat.takenOverBy !== this.session.code;
      /* ⛔ EVERY ONE OF THESE IS ABOUT THE CONVERSATION ON SCREEN, so none of them may read the
         bare `holding` any more — a rep previewing chat B while holding chat A would otherwise be
         shown a composer, a Hand Back and an End Chat that all operate on A. Take Over correctly
         reappears for B; taking it closes the parked socket and hands A back, which is the rep
         explicitly choosing (see takeOver). */
      var mine = this.holdingActive();
      take.hidden = mine || !!heldByOther;
      back.hidden = !mine;
      end.hidden = !mine;
      compose.hidden = !mine;
      if (heldByOther) this.setStatus('Held by ' + (chat.takenOverByName || chat.takenOverBy));
    },

    /* CUSTOM_CHATBOT_BY_CLAUDE (Ryan 2026-08-26) — a status line that BELONGS TO THE ROOM SOCKET.
       With the socket now parked on a held conversation while the rep reads another one, every
       'Live — bot paused' / 'Handed back to bot' coming off that socket describes a chat that is
       not on screen. Written into the shared status bar it would describe the WRONG conversation
       — so room-owned status is dropped while the room is off-screen, and repainted by
       renderActions/room_state the moment the rep switches back. */
    roomStatus(text) {
      if (this.roomOffScreen()) return;
      this.setStatus(text);
    },

    setStatus(text) {
      var el = document.getElementById('rqcdStatus');
      if (!el) return;
      if (!text) { el.hidden = true; el.textContent = ''; return; }
      el.hidden = false;
      el.textContent = text;
    },

    /* ---------- dock open/close ---------- */

    toggleDock() {
      if (this.embedded) return; // the widget owns open/close
      var panel = document.getElementById('rqcdPanel');
      if (panel && panel.hidden) this.openDock(); else this.closeDock();
    },

    openDock(skipPersist) {
      // Standalone only; the embedded case is cleared off bschat:visibility.
      if (!this.embedded) this.clearMissedHeld();
      if (this.embedded) return; // already on screen inside the widget window
      var panel = document.getElementById('rqcdPanel');
      if (!panel) return;
      panel.hidden = false;
      document.getElementById('rqChatDock').classList.add('rqcd--open');
      if (!skipPersist) { try { sessionStorage.setItem('rqcd_open', '1'); } catch (e) {} }
      this.render();

      // With exactly one live chat, the list is a pointless extra click — and
      // it reads as though there is no Take Over button at all. Drop straight
      // into the conversation. Two or more, the list earns its place.
      if (!this.activeSid) {
        var ids = Object.keys(this.chats).filter(function (sid) {
          return !this.chats[sid].endedAt;
        }, this);
        if (ids.length === 1) this.selectChat(ids[0], true);
      }
    },

    closeDock() {
      if (this.embedded) return;
      var panel = document.getElementById('rqcdPanel');
      if (!panel) return;
      panel.hidden = true;
      document.getElementById('rqChatDock').classList.remove('rqcd--open');
      try { sessionStorage.setItem('rqcd_open', '0'); } catch (e) {}
    },

    showList() {
      this.activeSid = null;
      try { sessionStorage.removeItem('rqcd_sid'); } catch (e) {}
      document.getElementById('rqcdThreadWrap').hidden = true;
      document.getElementById('rqcdList').hidden = false;
      document.getElementById('rqcdBack').hidden = true;
      document.getElementById('rqcdHeadTitle').textContent = 'Live chats';
      // No conversation open, no persona to wear — back to a plain ADMIN badge.
      this.announcePersona(null);
      this.announceView('list');
      this.render();
    },

    /* Embedded, the widget header owns the navigation buttons — it is the only
       place they can sit and match the rest of the chrome. Tell it which level
       we are on rather than reaching into its DOM. */
    announceView(view) {
      try {
        document.dispatchEvent(new CustomEvent('bsdock:view', { detail: { view: view } }));
      } catch (e) {}
    },

    /* ---------- one conversation ---------- */

    selectChat(sid, restoring, skipWatch) {
      if (!sid) return;
      this.activeSid = sid;
      try { sessionStorage.setItem('rqcd_sid', sid); } catch (e) {}
      if (!restoring) this.openDock();

      document.getElementById('rqcdList').hidden = true;
      document.getElementById('rqcdThreadWrap').hidden = false;
      document.getElementById('rqcdBack').hidden = false;
      var chat = this.chats[sid] || {};
      var who = (chat.contactInfo && chat.contactInfo.name) ? chat.contactInfo.name : 'Visitor';
      document.getElementById('rqcdHeadTitle').textContent = who;

      // Clear the unread badge for this conversation across every admin.
      this.inboxSend({ type: 'mark_read', sessionId: sid });
      /* Locally too, so the chip's count drops on the click rather than one
         round trip later \u2014 the switcher is read at a glance, and a stale number
         on the tab you are looking at reads as a message you have missed. */
      if (this.chats[sid]) this.chats[sid].unread = 0;

      /* Drop the last conversation's persona before rendering this one.
         loadThread is async, so without this the meta line (and now the header
         badge) shows the name from the chat you just left until the transcript
         arrives — and keeps showing it if that fetch fails. Nulling it falls
         back to the inbox's own botName for THIS session, which is right. */
      if (this.threadSid !== sid) {
        this.botName = null;
        /* ⛔ WIPE THE PANE BEFORE THE FETCH, NOT AFTER IT.
           renderThread only ever ran from loadThread's SUCCESS path, and
           nothing cleared the DOM on the way in. So any load that did not
           render — a brand-new conversation not yet persisted, a deferred
           re-fetch, a network error — left the PREVIOUS conversation sitting
           on screen looking like the one just clicked. With a room socket now
           open, refreshThread's HTTP fallback is skipped too, so it never
           self-corrected. That is how a rep ended up reading a finished chat
           while a new customer's messages counted up on the bubble. */
        this.thread = [];
        this.seenUpTo = '';
        this.sentBubbles = {};
        var pane = document.getElementById('rqcdThread');
        if (pane) pane.innerHTML = '<div class="rqcd__empty">Loading conversation…</div>';
      }
      this.threadSid = sid;

      // Reconnecting to the room we were already holding (page nav) reclaims
      // the takeover inside the worker's 90s grace window.
      /* ⛔ SWITCHING CONVERSATIONS IS NOT LEAVING ONE. (Ryan 2026-08-26.)
         ───────────────────────────────────────────────────────────────────────────────────────
         This used to closeRoom() on every switch. The worker cannot tell a deliberate tab-switch
         from a rep whose browser went away — `webSocketClose` stamps `disconnectedAt` either way
         — so opening the other chat started INTERCEPT_DISCONNECT_GRACE_MS, and ninety seconds
         later the bot quietly took the customer back from a rep who was sitting right there.

         Ryan: *"if you're in a chat with a customer and you click over to read the other chat,
         you have 90 seconds before the bot quietly takes that first customer back."* The 90s
         itself is correct and stays as it is — it exists because a portal page nav is a full
         reload and the socket really does drop. The bug was calling this a disconnect.

         So the socket STAYS on the held conversation and the one being opened is previewed over
         HTTP. ⚠️ That preview is a snapshot plus the inbox-driven refresh rather than a live
         feed — the cost of not owning a second socket, and it only applies while you are holding
         something else. The takeover itself is now released by exactly one thing: ten minutes of
         admin silence, which is the safety net Ryan actually wants. */
      var _keepHeld = this.holding && this.roomSid && this.roomSid !== sid;
      if (_keepHeld) {
        this.heldAwaySid = this.roomSid;   // the chip's live dot reads this
        skipWatch = true;                  // do not tear the held socket down to watch this one
      } else if (this.roomSid !== sid) {
        this.closeRoom();
      }
      if (this.heldAwaySid === sid) this.heldAwaySid = null;   // back in it
      /* ⛔ WATCH THE ROOM WHILE PREVIEWING IT.
         Until 2026-08-17 the dock opened no socket until Take Over, so a rep
         reading a conversation to get up to speed saw a FROZEN transcript:
         everything the customer and the bot said while they sat there was
         missing, and it only appeared after they joined. Ryan hit this and it
         is the worst possible moment to lose messages — you take over already
         behind, without knowing you are.

         refreshThread()'s HTTP fallback could not cover it either: the inbox
         event that would trigger a re-fetch fires BEFORE the model runs, so the
         re-fetch reads a transcript that does not have the new exchange in it
         yet, and nothing fires afterwards to try again. */
      /* ⚠️ `!this.roomWs` was the whole guard, and with the held socket now surviving a switch
         it is no longer sufficient on its own — connectRoom() closes whatever is open before it
         dials, so watching the previewed conversation would tear down the takeover this change
         exists to protect. `skipWatch` is set above for exactly that case. */
      if (!this.roomWs && !skipWatch) this.connectRoom(sid, { watch: true });
      this.loadThread(sid);
      this.renderMeta();
      this.renderActions();
      this.announceView('thread');
    },

    /* Turn a storefront path into something a person would say. The homepage
       is `/`, which the old version printed bare — a single slash sitting at
       the top of the conversation, which is what Ryan saw. */
    prettyLocation(url) {
      var path = this.shortPath(url);
      if (!path || path === '/') return 'the homepage';
      var m = path.match(/^\/(products|collections|pages|blogs)\/([^/?#]+)/);
      if (m) {
        var name = decodeURIComponent(m[2]).replace(/[-_]+/g, ' ').trim();
        name = name.charAt(0).toUpperCase() + name.slice(1);
        return m[1] === 'products' ? name
          : m[1] === 'collections' ? name + ' collection'
          : name;
      }
      if (path === '/cart') return 'the cart';
      if (path.indexOf('/search') === 0) return 'search';
      return path;
    },

    renderMeta() {
      var el = document.getElementById('rqcdMeta');
      var chat = this.chats[this.activeSid];
      if (!el || !chat) return;
      var bits = [];
      if (chat.contactInfo && chat.contactInfo.phone) bits.push(chat.contactInfo.phone);
      if (chat.contactInfo && chat.contactInfo.email) bits.push(chat.contactInfo.email);
      // Always say where they are, and say it as a place rather than a path.
      bits.push('on ' + (chat.productTitle || this.prettyLocation(chat.pageUrl)));

      /* Which name the customer knows you by. The bot rotates through four per
         session, so the rep who signs off with their own name has just broken
         the persona the customer has been talking to for ten minutes.

         Embedded, this rides in the widget's header badge — ADMIN · Emma —
         instead of a "You are Emma" pill above the conversation. Same fact,
         one line higher, in the chrome that is already reserved for who you
         are rather than in the row that describes the customer. Standalone
         keeps the pill: there is no widget header to put it in. */
      var persona = this.botName || chat.botName || null;
      var lead = (persona && !this.embedded)
        ? '<span class="rqcd__persona">You are ' + escHtml(persona) + '</span>'
        : '';
      this.announcePersona(persona);

      el.hidden = false;
      el.innerHTML = lead + bits.map(escHtml).join(' &middot; ');
    },

    /* The widget owns the header; we own the conversation. It learns the
       persona the same way it learns everything else about us — an event. */
    announcePersona(name) {
      if (this._persona === name) return;   // renderMeta runs on every update
      this._persona = name;
      try {
        document.dispatchEvent(new CustomEvent('bsdock:persona', { detail: { name: name || null } }));
      } catch (e) {}
    },

    /* ⛔ A LIVE APPEND DURING THE FETCH WOULD BE ERASED BY IT.
       appendLocal writes straight to the DOM and does NOT touch this.thread, so
       renderThread() from a fetched transcript replaces everything. If a message
       arrived on the socket while this request was in flight, the transcript we
       are holding predates it and rendering would silently delete it from the
       screen — the same class of bug as the frozen preview, in a window a few
       hundred ms wide. One bounded re-fetch instead; the attempt counter keeps
       a busy conversation from looping. */
    loadThread(sid, attempt) {
      var self = this;
      var n = attempt || 0;
      var appendsAtStart = this._liveAppends || 0;
      var again = function (delay) {
        if (n >= 5) { self.setStatus('Could not load this conversation.'); return; }
        setTimeout(function () {
          if (self.activeSid === sid) self.loadThread(sid, n + 1);
        }, delay);
      };
      RepAPI.fetchExternal(CHAT_WORKER, '/conversations/' + encodeURIComponent(sid), { method: 'GET' })
        .then(function (data) {
          if (self.activeSid !== sid) return;   // rep moved on
          /* ⛔ A CONVERSATION THE INBOX KNOWS ABOUT MAY NOT BE SAVED YET.
             ConversationRoom notifies AdminInbox BEFORE the model runs, so a
             brand-new chat appears in the list a beat before it exists in KV.
             Bailing out here used to leave whatever was rendered before still
             on screen — which, for a rep clicking a new chat, was the last one
             they had open. Retry instead of giving up on the first miss. */
          if (!data || !data.conversation) { again(400 * (n + 1)); return; }
          if (!n && (self._liveAppends || 0) !== appendsAtStart) {
            // Messages landed while this was in flight; the copy we are holding
            // predates them. Re-fetch rather than render a stale transcript.
            again(800);
            return;
          }
          self.thread = data.conversation.messages || [];
          self.botName = data.conversation.botName || null;
          self.seenUpTo = data.conversation.seenUpTo || '';
          self.renderThread();
          self.renderMeta(); // the persona name arrives with the transcript
        })
        .catch(function () {
          if (self.activeSid === sid) again(400 * (n + 1));
        });
    },

    /* Only poll the thread over HTTP when we have no socket on this room. With
       a socket open, messages arrive as events and appending both ways would
       double every line. */
    refreshThread() {
      if (this.activeSid && !this.roomWs) this.loadThread(this.activeSid);
    },

    renderThread() {
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      var self = this;
      var html = '';
      var lastPlace = '';
      var lastSepTs = 0;
      this.thread.forEach(function (m) {
        /* A timestamp above the first message, then one after any real lull.
           System lines (page moves, the finalize seam) do not count as
           conversation, so they neither trigger a separator nor reset the
           clock — otherwise a customer wandering the site would litter the
           thread with stamps. */
        if (m.role !== 'system' && m.timestamp) {
          var t = new Date(m.timestamp).getTime();
          if (!isNaN(t)) {
            if (!lastSepTs || (t - lastSepTs) >= self.SEP_GAP_MS) {
              html += '<div class="rqcd__timesep">' + self.sepMarkup(m.timestamp) + '</div>';
            }
            lastSepTs = t;
          }
        }
        /* The customer's journey. Lifecycle events are already stored as
           system messages, and skipping them threw away the trail — where they
           went and in what order is real context for a rep about to reply.
           Rendered inline so the moves sit between the messages they happened
           between; identical consecutive pages are dropped so a reload or a
           bounced tab does not repeat itself. */
        if (m.role === 'system') {
          /* Where the conversation was filed: lead posted, transcript emailed.
             A chat that resumes stays ONE thread the rep can scroll through in
             full — this line marks where the half they were already emailed
             stops, so nothing below it is assumed to have been seen. */
          if (m.content === 'chat_finalized') {
            html += '<div class="rqcd__endline"><span>chat ended · transcript sent</span></div>';
            lastPlace = ''; // a page they return to after the seam is news again
            return;
          }
          if (m.content !== 'page_change' && m.content !== 'page_nav') return;
          var d = m.data || {};
          var place = d.page_title || self.prettyLocation(d.page_url);
          if (!place || place === lastPlace) return;
          lastPlace = place;
          html += '<div class="rqcd__sysline">moved to ' + escHtml(place) + '</div>';
          return;
        }
        var kind = m.role === 'user' ? 'user' : (m.interceptedBy ? 'human' : 'bot');
        var tick = '';
        /* Rebuild the ticks from the persisted receipt so they survive a page
           change. Anything a rep sent is at least delivered (it is in the
           transcript); read is whatever the customer acknowledged. */
        if (m.interceptedBy && m.timestamp) {
          var read = self.seenUpTo && m.timestamp <= self.seenUpTo;
          tick = '<span class="rqcd__tick' + (read ? ' rqcd__tick--read' : '') + '" title="' +
            (read ? 'Read by the customer' : 'Delivered to the customer') + '">&nbsp;' +
            (read ? '✓✓' : '✓') + '</span>';
        }
        html += '<div class="' + self.msgClass(kind) + '">' + escHtml(m.content || '') + tick + '</div>';
      });
      /* CUSTOM_CHATSCROLL_BY_CLAUDE — a refresh of the conversation already on screen keeps the
         reader where they are; only a newly opened one (nothing drawn yet) goes to the bottom. */
      var MSG_SEL = '.rqcd__msg, .bschat__msg';
      var keeper = this.scrollKeeper();
      var before = el.querySelectorAll(MSG_SEL);
      var prevCount = before.length;
      var oldDiv = document.getElementById('rqcdUnreadDivider');
      var divAt = -1;
      if (oldDiv && keeper && keeper.unseen()) {
        for (var bi = 0; bi < before.length; bi++) {
          if (oldDiv.compareDocumentPosition(before[bi]) & Node.DOCUMENT_POSITION_FOLLOWING) { divAt = bi; break; }
        }
      }
      var snap = keeper ? keeper.snapshot() : null;
      el.innerHTML = html;
      var after = el.querySelectorAll(MSG_SEL);
      if (!keeper || prevCount === 0) {
        if (keeper) keeper.reset();
        this.scrollThreadToBottom();
        return;
      }
      if (divAt >= 0 && after[divAt]) this.insertUnreadDivider(after[divAt], 'New');
      var added = Math.max(0, after.length - prevCount);
      keeper.settle(snap, { added: added, firstNew: added ? after[prevCount] : null });
    },

    /* CUSTOM_CHATSCROLL_BY_CLAUDE — the thread's scroll keeper (see rqChatScrollFor). The NEW
       divider is the dock's own unread divider, so a scrolled-up reader and a reopened
       minimised takeover see the same line, retired the same way once it has been seen. */
    scrollKeeper() {
      var el = document.getElementById('rqcdThread');
      if (!el) return null;
      var self = this;
      return rqChatScrollFor(el, {
        divider: function () { return document.getElementById('rqcdUnreadDivider'); },
        placeDivider: function (first) { self.insertUnreadDivider(first, 'New'); },
        onSeen: function () { self.retireUnreadDivider(); },
      });
    },

    /* Setting scrollTop while the panel is still hidden does nothing — an
       element with no layout has scrollHeight 0 — so after a page change the
       restored thread appeared scrolled to the very top. Defer past layout,
       then check once more after paint for anything that changed height. */
    scrollThreadToBottom() {
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      var pin = function () { el.scrollTop = el.scrollHeight; };
      pin();
      requestAnimationFrame(function () { pin(); requestAnimationFrame(pin); });
      setTimeout(pin, 120);
    },

    /* Customer-side typing dots, pinned to the bottom of the thread. Always
       removed and re-appended so it stays below the newest message. */
    showCustomerTyping(active) {
      /* ⛔ NEVER PAINT THE PARKED CONVERSATION INTO THE ONE ON SCREEN. The room socket
         survives a tab-switch now, so its messages keep arriving while the rep reads somebody
         else — and appending them here would put one customer's words in another's transcript.
         The message is not lost: it is counted (missedHeld), it chimes, and the switcher chip
         shows it, then loadThread paints it in full when the rep switches back. */
      if (this.roomOffScreen()) return;
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      var dots = document.getElementById('rqcdCustTyping');
      if (!active) { if (dots) dots.remove(); return; }
      if (!dots) {
        dots = document.createElement('div');
        dots.id = 'rqcdCustTyping';
      }
      /* Same rule as the bubbles: embedded, we wear the widget's own typing
         indicator rather than a lookalike. The panel skin's version is a
         bordered surface pill — correct next to panel bubbles, but inside the
         chat window it reads as a small grey chip stuck under the conversation,
         because the widget's dots are bare and brand-coloured with no bubble at
         all. Re-applied on every call, not just on create: the node survives
         mount/unmount, so a set of dots built in one skin can be shown in the
         other. */
      if (this.embedded) {
        dots.className = 'bschat__typing';
        dots.innerHTML = '<span class="bschat__typing-dot"></span>'
          + '<span class="bschat__typing-dot"></span><span class="bschat__typing-dot"></span>';
      } else {
        dots.className = 'rqcd__typing';
        dots.innerHTML = '<span></span><span></span><span></span>';
      }
      var tk = this.scrollKeeper(), tsnap = tk ? tk.snapshot() : null; // CUSTOM_CHATSCROLL_BY_CLAUDE
      el.appendChild(dots); // re-append: keep it last
      if (tk) tk.settle(tsnap, {}); else el.scrollTop = el.scrollHeight;
      // Safety net: if the stop signal is lost, don't leave the dots forever.
      clearTimeout(this._custTypingTimer);
      var self = this;
      this._custTypingTimer = setTimeout(function () { self.showCustomerTyping(false); }, 8000);
    },

    /** The "filed here" seam. Idempotent — finalize can fire more than once
     *  (idle, then the cron sweep) and one ending must not draw two rules. */
    appendEndLine() {
      /* ⛔ NEVER PAINT THE PARKED CONVERSATION INTO THE ONE ON SCREEN. The room socket
         survives a tab-switch now, so its messages keep arriving while the rep reads somebody
         else — and appending them here would put one customer's words in another's transcript.
         The message is not lost: it is counted (missedHeld), it chimes, and the switcher chip
         shows it, then loadThread paints it in full when the rep switches back. */
      if (this.roomOffScreen()) return;
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      var last = el.lastElementChild;
      if (last && last.classList.contains('rqcd__endline')) return;
      var div = document.createElement('div');
      div.className = 'rqcd__endline';
      div.innerHTML = '<span>chat ended &middot; transcript sent</span>';
      var ek = this.scrollKeeper(), esnap = ek ? ek.snapshot() : null; // CUSTOM_CHATSCROLL_BY_CLAUDE
      el.appendChild(div);
      if (ek) ek.settle(esnap, {}); else el.scrollTop = el.scrollHeight;
    },

    /** A centred, muted note in the thread — not a message from anyone. */
    appendSystemLine(text) {
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      // Each move gets its own line — the trail IS the useful part. Only an
      // identical repeat is dropped, so a bounced tab doesn't stutter.
      var last = el.lastElementChild;
      if (last && last.classList.contains('rqcd__sysline') && last.textContent === text) return;
      var div = document.createElement('div');
      div.className = 'rqcd__sysline';
      div.textContent = text;
      var sk = this.scrollKeeper(), ssnap = sk ? sk.snapshot() : null; // CUSTOM_CHATSCROLL_BY_CLAUDE
      el.appendChild(div);
      if (sk) sk.settle(ssnap, {}); else el.scrollTop = el.scrollHeight;
    },

    appendLocal(content, kind, clientId) {
      /* ⛔ NEVER PAINT THE PARKED CONVERSATION INTO THE ONE ON SCREEN. The room socket
         survives a tab-switch now, so its messages keep arriving while the rep reads somebody
         else — and appending them here would put one customer's words in another's transcript.
         The message is not lost: it is counted (missedHeld), it chimes, and the switcher chip
         shows it, then loadThread paints it in full when the rep switches back. */
      if (this.roomOffScreen()) return;
      // Counted so loadThread can tell whether its transcript is already stale
      // by the time it lands. See the note there.
      this._liveAppends = (this._liveAppends || 0) + 1;
      var el = document.getElementById('rqcdThread');
      if (!el) return null;
      var div = document.createElement('div');
      div.className = this.msgClass(kind);
      div.textContent = content;
      /* CUSTOM_CHATSCROLL_BY_CLAUDE — the rep's own send (it carries a clientId) always goes to
         the bottom; anything else follows only a reader already at the bottom, and is counted on
         the "new messages" pill otherwise. */
      var ak = this.scrollKeeper(), asnap = ak ? ak.snapshot() : null;
      el.appendChild(div);
      if (ak) ak.settle(asnap, { added: 1, firstNew: div, own: !!clientId });
      else el.scrollTop = el.scrollHeight;
      if (clientId) this.pendingSends[clientId] = div;
      return div;
    },

    /* ---------- takeover ---------- */

    takeOver() {
      if (!this.activeSid) return;
      /* Already watching this room: claim it over the socket we have rather
         than tearing it down and reconnecting. Dropping and re-opening would
         lose anything said in the gap — the exact bug this preview socket
         exists to fix. */
      if (this.roomWs && this.roomSid === this.activeSid && this.roomWs.readyState === WebSocket.OPEN) {
        this.roomWatch = false;
        try {
          this.roomWs.send(JSON.stringify({
            type: 'admin_join',
            adminName: this.session.name || this.session.code,
          }));
          this.setStatus('Joining…');
        } catch (e) {
          this.connectRoom(this.activeSid);
        }
        return;
      }
      this.connectRoom(this.activeSid);
    },

    /* opts.watch — connect READ-ONLY. The socket is tagged 'admin' by
       state.acceptWebSocket the moment it connects, so it receives every
       customer_message / bot_reply broadcast straight away; it is `admin_join`
       that claims the conversation, not the connection. Withholding that one
       message is the whole difference between watching and taking over. */
    connectRoom(sid, opts) {
      var self = this;
      this.closeRoom();
      this.roomSid = sid;
      this.roomWatch = !!(opts && opts.watch);
      // Same signed ticket as the inbox socket. Identity is server-derived from
      // it, so repCode/repName no longer ride in the URL at all.
      this.wsTicket().then(function (ticket) {
        // The rep may have switched conversations while the ticket was minting.
        if (self.roomSid !== sid) return;
        if (!ticket) { self.setStatus('Could not authenticate for that conversation.'); return; }
        self.openRoom(sid, ticket);
      });
    },

    openRoom(sid, ticket) {
      var self = this;
      var url = CHAT_WORKER.replace('https://', 'wss://') + '/ws/admin' +
        '?session=' + encodeURIComponent(sid) +
        '&ticket=' + encodeURIComponent(ticket);
      try {
        this.roomWs = new WebSocket(url);
      } catch (e) {
        this.setStatus('Could not connect to that conversation.');
        return;
      }

      this.roomWs.onopen = function () {
        /* admin_ack stops the SMS cascade. Correct to send while merely
           watching: a rep reading the transcript HAS seen it, and texting them
           about a chat they are looking at is the nagging this whole area is
           trying to stop. */
        self.roomWs.send(JSON.stringify({ type: 'admin_ack' }));
        if (self.roomWatch) {
          // Read-only. No admin_join, so the bot keeps the conversation and the
          // customer is told nothing.
          self.setStatus('');
        } else {
          self.roomWs.send(JSON.stringify({ type: 'admin_join', adminName: self.session.name || self.session.code }));
          self.setStatus('Joining…');
        }
      };

      this.roomWs.onmessage = function (evt) {
        if (evt.data === 'pong') return;
        var d;
        try { d = JSON.parse(evt.data); } catch (e) { return; }
        self.onRoomEvent(d);
      };

      this.roomWs.onclose = function () {
        var wasWatch = self.roomWatch;
        self.roomWs = null;
        // The worker holds our takeover for 90s. Reconnect rather than
        // silently handing a live customer back to the bot.
        if (self.holding && self.roomSid) {
          self.setStatus('Reconnecting…');
          setTimeout(function () {
            if (self.holding && self.roomSid && !self.roomWs) self.connectRoom(self.roomSid);
          }, 1500);
        } else if (wasWatch && self.activeSid === sid) {
          /* A dropped PREVIEW socket is silent by nature — the transcript just
             quietly stops updating, which is the same "chunks missing" failure
             in a different costume. Reconnect while the rep is still on this
             conversation. No status text: they never knew it dropped. */
          setTimeout(function () {
            if (!self.roomWs && self.activeSid === sid) self.connectRoom(sid, { watch: true });
          }, 1500);
        }
      };
    },

    /* Survives the page change; cleared only when the takeover genuinely ends.
       Note closeRoom() does NOT clear it — closing the socket is what a page
       nav does, and that is precisely when we want to come back holding. */
    /* Is the rep actually looking at the conversation thread right now?
       offsetParent goes null when the element OR ANY ANCESTOR is display:none,
       so this answers the question the same way whether the dock owns its panel
       (standalone) or is mounted inside the chat widget (embedded, where the
       widget hides its window and the dock is never told). Also false when the
       tab itself is not being looked at — a popup open behind another window is
       not being read either. */
    /* \u26d4 "AM I WATCHING THIS CONVERSATION" \u2014 NOT "am I watching A conversation".
       (Ryan 2026-08-25.) This used to ask only whether SOME thread was on
       screen, so the moment a second chat existed every held-chat protection
       inverted: holding chat A while reading chat B counted as watching A, so
       A's incoming messages skipped their chime, skipped the missed-message
       counter and skipped the unread divider. The customer waiting on a HUMAN
       was the one that went quiet. Callers that mean a specific conversation
       pass its sid; the default is the room we hold. */
    watchingThread(sid) {
      try {
        var panel = document.getElementById('rqcdPanel');
        if (!panel || panel.hidden || panel.offsetParent === null) return false;
        var wrap = document.getElementById('rqcdThreadWrap');
        if (!wrap || wrap.hidden) return false;          // showing the list, not the chat
        var want = sid || this.roomSid;
        if (want && this.activeSid !== want) return false;   // open, but on another chat
        return !RepChatAlert.away();
      } catch (e) { return false; }
    },

    /* Persisted for the same reason `announced` is: a portal page change is a
       FULL RELOAD, and an in-memory tally means the count a rep is looking at
       vanishes the moment they click through to another tool. Ryan, 2026-08-17:
       minimised at "3", clicked a new page, the dot dropped to a plain red and
       the next message restarted at 1 instead of going to 4.

       Keyed by session id so a stale count can never attach itself to a
       different conversation, and only restored while `rqcd_holding` says the
       takeover is still ours. sessionStorage is per-tab and dies with it, which
       is the right lifetime. */
    MISSED_KEY: 'rqcd_missed',

    setMissedHeld(n) {
      this.missedHeld = n;
      try {
        var sid = this.roomSid || this.activeSid;
        if (!sid || !n) sessionStorage.removeItem(this.MISSED_KEY);
        else sessionStorage.setItem(this.MISSED_KEY, JSON.stringify({ sid: sid, n: n }));
      } catch (e) {}
    },

    loadMissedHeld() {
      try {
        if (sessionStorage.getItem('rqcd_holding') !== '1') return 0;
        var sid = sessionStorage.getItem('rqcd_sid');
        var raw = JSON.parse(sessionStorage.getItem(this.MISSED_KEY) || 'null');
        return (raw && sid && raw.sid === sid) ? (raw.n || 0) : 0;
      } catch (e) { return 0; }
    },

    clearMissedHeld() {
      if (!this.missedHeld) return;
      this.setMissedHeld(0);
      this.render();
      this.retireUnreadDivider();
    },

    /* Marks where the rep stopped watching, so reopening a minimised takeover
       shows at a glance what arrived without them. Mirrors the customer widget's
       divider deliberately: same look, same 10s-then-fade retirement. */
    /* ---- time separators ----
       Same idea and the same 15-minute lull threshold as the customer widget
       (see fmtSepTime there), so both sides of a conversation are punctuated
       the same way. The dock has it easier: every stored message carries a
       timestamp, so this is decided at render time from the data. */
    SEP_GAP_MS: 15 * 60 * 1000,

    fmtSepTime(ts) {
      try {
        var d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        var now = new Date();
        var time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        if (d.toDateString() === now.toDateString()) return time;
        var sameYear = d.getFullYear() === now.getFullYear();
        var date = d.toLocaleDateString([], sameYear
          ? { month: 'short', day: 'numeric' }
          : { year: 'numeric', month: 'short', day: 'numeric' });
        return date + ', ' + time;
      } catch (e) { return ''; }
    },

    sepMarkup(ts) {
      return '<span class="rqcd__timesep-label">' + escHtml(this.fmtSepTime(ts)) + '</span>';
    },

    insertUnreadDivider(beforeEl, label) {
      /* ⛔ NEVER PAINT THE PARKED CONVERSATION INTO THE ONE ON SCREEN. The room socket
         survives a tab-switch now, so its messages keep arriving while the rep reads somebody
         else — and appending them here would put one customer's words in another's transcript.
         The message is not lost: it is counted (missedHeld), it chimes, and the switcher chip
         shows it, then loadThread paints it in full when the rep switches back. */
      if (this.roomOffScreen()) return;
      var el = document.getElementById('rqcdThread');
      if (!el) return;
      /* Drop any previous one first, including one mid-fade. Bailing out when a
         divider already exists would leave it stranded above messages the rep
         has already read, marking the wrong place. */
      var old = document.getElementById('rqcdUnreadDivider');
      if (old && old.parentNode) old.remove();
      var div = document.createElement('div');
      div.className = 'rqcd__unread-divider';
      div.id = 'rqcdUnreadDivider';
      div.setAttribute('data-ts', String(Date.now()));
      /* CUSTOM_CHATSCROLL_BY_CLAUDE — "New" (Ryan's word for it); placed above the first
         message the reader has not seen when the thread hands one over, else at the end as before. */
      div.innerHTML = '<span class="rqcd__unread-label">' + (label || 'New') + '</span>';
      if (beforeEl && beforeEl.parentNode === el) el.insertBefore(div, beforeEl);
      else el.appendChild(div);
    },

    retireUnreadDivider() {
      /* 10s to read from, then a 600ms fade while the box collapses so the
         messages below slide up instead of snapping. Removed only after the
         transition finishes, so the end is not a jump either. */
      var self = this;
      setTimeout(function () {
        var div = document.getElementById('rqcdUnreadDivider');
        if (!div) return;
        /* CUSTOM_CHATSCROLL_BY_CLAUDE — still marking messages the reader has not reached; the
           keeper calls this again once they have. */
        var keep = self.scrollKeeper();
        if (keep && keep.unseen()) return;
        var ts = Number(div.getAttribute('data-ts')) || Date.now();
        div.classList.add('rqcd__unread-divider--leaving');
        setTimeout(function () {
          if (!div || !div.parentNode) return;
          div.className = 'rqcd__timesep';
          div.style.opacity = '0';
          div.innerHTML = self.sepMarkup(ts);
          div.removeAttribute('id');
          void div.offsetHeight;
          div.style.opacity = '';
        }, 420);
      }, 10000);
    },

    setHoldingFlag(on) {
      try {
        if (on) sessionStorage.setItem('rqcd_holding', '1');
        else sessionStorage.removeItem('rqcd_holding');
      } catch (e) {}
    },

    closeRoom() {
      if (this.roomWs) {
        try { this.roomWs.close(); } catch (e) {}
        this.roomWs = null;
      }
      this.roomSid = null;
      this.roomWatch = false;
      this.holding = false;
      /* The takeover is over, so the server starts counting this conversation's
         unread again. Keeping the local tally would double-count it. */
      this.setMissedHeld(0);
      this.pendingSends = {};
      this.sentBubbles = {};
    },

    onRoomEvent(d) {
      switch (d.type) {
        case 'room_state':
          this.holding = !!(d.intercept && d.intercept.mine);
          // Reconnect mid-takeover (page change, dropped socket) still counts
          // as having typed in this one.
          if (this.holding) this.markHeld(this.roomSid);
          this.roomStatus(this.holding ? 'Live — bot paused'
            : (d.intercept ? 'Held by ' + (d.intercept.adminName || d.intercept.adminCode) : ''));
          break;
        case 'intercept_started':
          this.holding = true;
          this.markHeld(this.roomSid);
          this.setHoldingFlag(true);
          this.roomStatus('Live — bot paused');
          // Tell the widget to open on the conversation, not the menu — this is
          // what carries a takeover across a portal page change.
          try { document.dispatchEvent(new CustomEvent('bsdock:resumed')); } catch (e) {}
          break;
        case 'intercept_denied':
          this.holding = false;
          this.setHoldingFlag(false);
          this.roomStatus('Already being handled by ' + (d.heldByName || d.heldBy));
          break;
        case 'intercept_suspended':
          this.roomStatus('Connection dropped — reconnecting, you still hold this chat.');
          break;
        case 'intercept_ended':
          this.holding = false;
          this.heldAwaySid = null;   // no longer on the hook for it
          this.setHoldingFlag(false);
          this.roomStatus(d.reason === 'idle_timeout' ? 'Handed back to bot (no admin activity for 10 min).'
            : d.reason === 'admin_disconnected' ? 'Handed back to bot (connection did not return).'
            /* End Chat. Deliberately does not claim the transcript has gone —
               it has not, and saying so is what made a rep think the record was
               closed while the visitor was still typing. */
            : d.reason === 'admin_ended' ? 'You are done with this chat. The bot has it back — the lead and transcript go out once the visitor is finished.'
            : 'Handed back to bot.');
          /* An idle timeout or a dead connection ends the takeover just as
             surely as End Chat does, and used to take the learning step with
             it. 'admin_ended' is excluded because endChat() already opened the
             wrap-up before this event arrived. */
          if (d.reason !== 'admin_ended') this.offerWrapUp(this.roomSid);
          // Server resumes counting this chat's unread from here; drop ours.
          this.setMissedHeld(0);
          break;
        /* Now fired only by finalizeConversation, i.e. the customer actually
           stopped — never by the End Chat button. */
        case 'chat_ended':
          this.holding = false;
          this.heldAwaySid = null;   // the visitor finished; nothing to go back to
          this.setHoldingFlag(false);
          this.roomStatus('Visitor finished. Lead posted and transcript sent.');
          // Draw the seam live too. The stored copy renders on the next load;
          // a rep watching when it happens should not have to reload to see it.
          this.appendEndLine();
          /* ⛔ THE case this was all missing. The customer finishing first is
             the ordinary end to a good takeover, and it used to be the one
             ending that silently skipped the learning step. */
          this.offerWrapUp(this.roomSid);
          /* ⛔ AND STOP IT BEING WHAT A RELOAD RESTORES.
             `rqcd_sid` was only ever cleared by showList(), but a finished
             chat deliberately STAYS on screen so the rep can read it — so the
             dead conversation remained the stored one, and every page load
             (hard refresh included) walked straight back into it while new
             chats piled up unanswered in the list. Ryan, 2026-08-17: "it's
             still taking the admin into the old chat when they try to join...
             even after hard refresh".
             The thread stays visible; only its claim on the next page load
             goes. */
          try { sessionStorage.removeItem('rqcd_sid'); } catch (e) {}
          break;
        case 'customer_message':
          this.showCustomerTyping(false); // the message is here; stop the dots
          /* Decided BEFORE the append, because the divider has to land above
             the first message they missed, not below it. */
          if (this.holding && !this.watchingThread() && !(this.missedHeld || 0)) {
            this.insertUnreadDivider();
          }
          if (d.content) this.appendLocal(d.content, 'user');
          if (d.reply) this.appendLocal(d.reply, 'bot');
          /* ⛔ The case nothing else covers. AdminInbox does not raise `unread`
             for a chat you hold, so the inbox-driven alert never fires here and
             a live takeover is completely silent the moment you look away.
             Once you have picked up, EVERY message announces itself — a missed
             one now means a customer waiting on a human, not on the bot. The
             only silence is while your cursor is in the reply box.

             Gated on actually HOLDING it: this socket is now also open while
             merely previewing a conversation, and an unclaimed chat is still
             the bot's, so it keeps the quieter inbox rule (chime once, then
             tab title only). Without this gate, opening a preview would make
             every message beep again. */
          if (this.holding) {
            RepChatAlert.heldMessage();
            /* Minimised takeover: count what they are missing so the indicator
               carries a number rather than sitting on a green LED. */
            if (!this.watchingThread()) {
              this.setMissedHeld((this.missedHeld || 0) + 1);
              this.render();
            }
          }
          break;

        /* Where they went, as a quiet line in the thread rather than a bubble.
           Useful context mid-takeover — "they just opened the cart" changes
           what you say next — but it is not something the customer said. */
        case 'customer_nav':
          this.appendSystemLine('moved to ' + (d.productTitle || this.prettyLocation(d.pageUrl)));
          break;

        case 'customer_exit':
          this.appendSystemLine('left the site');
          break;
        case 'bot_reply':
          if (d.reply) this.appendLocal(d.reply, 'bot');
          break;
        case 'admin_message_echo':
          this.appendLocal(d.content, 'human');
          break;
        case 'admin_message_delivered':
          this.onSendAck(d);
          break;
        case 'customer_seen':
          this.markSeen(d.upTo);
          break;
        case 'customer_typing':
          this.showCustomerTyping(d.active);
          break;
        case 'error':
          this.roomStatus(d.message || 'Chat error');
          break;
      }
      this.renderActions();
      /* `holding` changes on several of these (intercept_started/ended,
         chat_ended, room_state) and the bubble indicator is now driven off it,
         so re-render unconditionally rather than remembering to do it in each
         branch. render() is a few class toggles and one event dispatch. */
      this.render();
    },

    onSendAck(d) {
      var div = this.pendingSends[d.clientId];
      if (!div) return;
      delete this.pendingSends[d.clientId];
      if (!d.ok) {
        div.classList.add('rqcd__msg--failed');
        div.title = d.reason === 'not_intercepting'
          ? 'Not sent — you are not holding this conversation.'
          : 'Not sent.';
        return;
      }
      /* ⛔ `delivered:false` IS STILL DELIVERED. It means the customer's socket
         was not open at that instant, not that the message might not arrive:
         the worker persists BEFORE it pushes ("never lost — only late", see
         admin_message in conversation-room.js), and the widget renders it from
         the transcript the moment they open the chat.

         This used to render ⋯ with "their connection dropped", which is what a
         rep sees for the single most ordinary case there is — a customer who
         simply closed the chat window. It read as a failure and it was not one.
         Ryan, 2026-08-17: it should be a single check.

         One tick = we have it and they will get it. Two = they have read it.
         There is no state between those worth a third symbol, because the
         message cannot be lost. `ok:false` above is the only real failure. */
      if (d.sentAt) this.sentBubbles[d.sentAt] = div;
      this.setTick(div, 'delivered');
    },

    setTick(div, state) {
      var tick = div.querySelector('.rqcd__tick');
      if (!tick) {
        tick = document.createElement('span');
        tick.className = 'rqcd__tick';
        div.appendChild(tick);
      }
      /* Two states only, deliberately. There WAS a 'queued' ⋯ for
         `delivered:false`, but that is not a distinct outcome: the worker
         persists the message before pushing it, so it always reaches the
         customer — a socket that happened to be shut only makes it late.
         Showing ⋯ there made the most ordinary case (the customer closed the
         chat window) look like a failure. Do not reintroduce a third symbol
         unless there is genuinely a way for a message to be lost. */
      var read = state === 'read';
      /* The leading non-breaking space is the separator, not the margin. These
         bubbles borrow widget classes whose rules were flattening margin-left
         on inline children, so the ticks sat flush against the last word. A
         character in the content cannot be overridden by a stylesheet. */
      tick.textContent = read ? ' ✓✓' : ' ✓';
      tick.classList.toggle('rqcd__tick--read', read);
      tick.title = read ? 'Read by the customer' : 'Delivered to the customer';
    },

    /** Everything sent at or before `upTo` has now been seen. */
    markSeen(upTo) {
      if (!upTo) return;
      if (!this.seenUpTo || this.seenUpTo < upTo) this.seenUpTo = upTo;
      for (var ts in this.sentBubbles) {
        if (ts <= upTo) this.setTick(this.sentBubbles[ts], 'read');
      }
      // Bubbles rendered from the transcript aren't in sentBubbles — mark those too.
      var el = document.getElementById('rqcdThread');
      if (el) {
        var stale = el.querySelectorAll('.rqcd__tick:not(.rqcd__tick--read)');
        Array.prototype.forEach.call(stale, function (t) {
          t.classList.add('rqcd__tick--read');
          t.textContent = '✓✓';
          t.title = 'Read by the customer';
        });
      }
    },

    send() {
      var input = document.getElementById('rqcdInput');
      if (!input) return;
      var text = input.value.trim();
      /* ⛔ holdingActive(), NOT holding. The room socket can now be parked on a conversation
         the rep is NOT looking at (see selectChat), and `this.holding` would still be true —
         so this would send what they typed for THIS customer into the OTHER one's chat. The
         composer is hidden in that state, but a guard on the send itself is the one that has
         to be right. */
      if (!text || !this.holdingActive()) return;
      if (!this.roomWs || this.roomWs.readyState !== WebSocket.OPEN) {
        this.setStatus('Not connected — message not sent. Reconnecting.');
        return;
      }
      var clientId = 'd' + (++this.sendSeq) + '_' + Date.now();
      this.roomWs.send(JSON.stringify({
        type: 'admin_message',
        content: text,
        clientId: clientId,
        botName: this.botName || null,
      }));
      this.appendLocal(text, 'human', clientId);
      input.value = '';
      this.autoGrow(input);
      this.notifyTyping(false); // message sent — stop the customer's dots
      input.focus();
    },

    autoGrow(el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    },

    /* Drives the customer's typing dots from real keystrokes. Throttled on the
       way up so we send at most one signal every 2s, and auto-stopped after a
       pause so the dots don't hang there when the admin walks away mid-sentence. */
    notifyTyping(active) {
      // Same reason as send(): typing dots must not appear for a customer the rep is not writing to.
      if (!this.holdingActive() || !this.roomWs || this.roomWs.readyState !== WebSocket.OPEN) return;
      var on = active !== false;
      var self = this;
      clearTimeout(this._typingStop);

      if (on) {
        var now = Date.now();
        if (!this._lastTyping || now - this._lastTyping > 2000) {
          this._lastTyping = now;
          try { this.roomWs.send(JSON.stringify({ type: 'admin_typing', active: true })); } catch (e) {}
        }
        this._typingStop = setTimeout(function () { self.notifyTyping(false); }, 4000);
      } else {
        this._lastTyping = 0;
        try { this.roomWs.send(JSON.stringify({ type: 'admin_typing', active: false })); } catch (e) {}
      }
    },

    handBack() {
      if (!this.roomWs || !this.holdingActive()) return;
      try { this.roomWs.send(JSON.stringify({ type: 'admin_leave' })); } catch (e) {}
      this.holding = false;
      this.setHoldingFlag(false);
      this.renderActions();
      this.render();   // drop the red "live takeover" light immediately
    },

    /* Ends YOUR part, not the conversation. The visitor's widget is still open
       and they can keep talking to the bot — so the lead and transcript stay on
       the customer-silence clock. The old copy here promised they went out now,
       which was both true and wrong: the transcript really did send, mid-chat,
       missing everything said afterwards. */
    endChat() {
      if (!this.roomWs || !this.holding) return;
      if (!window.confirm('Done with this chat?\n\nThe bot takes it back. If the visitor asks anything else you will see it here — the lead and transcript go out once they are actually finished.')) return;
      var sid = this.activeSid;
      try { this.roomWs.send(JSON.stringify({ type: 'admin_end_chat' })); } catch (e) {}
      // Stand down first, then offer the review. The rep is out of the
      // conversation either way — whether they teach the bot anything is a
      // separate decision, and skipping it must cost nothing.
      this.openWrapUp(sid);
    },

    /* ---------- the learning loop ----------
       Four corrections went through Claude Code by hand on 2026-08-11, hours
       after the fact. This is the same thing, in the rep's hands, while the
       conversation is still on screen.

       The panel itself now lives in RepChatLearn, above, because it stopped
       being the dock's alone: the Review tab on the chatbot admin page opens
       the same thing on any past conversation. What stays here is WHEN the
       dock offers it. */

    closeWrapUp() { RepChatLearn.close(); },

    openWrapUp(sid) {
      if (!sid) return;
      var self = this;
      this.wrapOffered[sid] = true;
      RepChatLearn.open(sid, document.getElementById('rqcdPanel'), {
        onStatus: function (t) { self.setStatus(t); },
      });
    },

    /* ⛔ THE WRAP-UP MUST NOT DEPEND ON HOW THE CONVERSATION ENDED.
       It used to hang off endChat() alone, which early-returns unless you are
       still holding the chat. So when the customer finished before the rep did
       — the ordinary way a good conversation ends — chat_ended cleared
       `holding`, End Chat hid itself on that same flag (renderActions), and the
       only door to the learning step was bolted shut at exactly the moment the
       conversation was most worth learning from. An idle timeout or a dropped
       connection lost it the same way, just as silently. Ryan lost a takeover
       he had specifically wanted to teach the bot from, 2026-08-17.

       `heldSids` is deliberately NOT `holding`. "I typed in this conversation
       at some point" and "I am holding it right now" are different questions,
       and answering the first with the second is the whole bug. */
    markHeld(sid) {
      if (sid) this.heldSids[sid] = true;
    },

    offerWrapUp(sid) {
      if (!sid || !this.heldSids[sid] || this.wrapOffered[sid]) return;
      if (RepChatLearn.sid) return;  // one is already open; do not stomp it
      this.openWrapUp(sid);
    },

    inboxSend(obj) {
      if (!this.inboxWs || this.inboxWs.readyState !== WebSocket.OPEN) return;
      try { this.inboxWs.send(JSON.stringify(obj)); } catch (e) {}
    },
  };

  function escHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escAttr(s) {
    return String(s == null ? '' : s).replace(/"/g, '&quot;');
  }
  /* END CUSTOM_CHATBOT_BY_CLAUDE */

  /* ------------------------------------------
     Expose to global scope for page-specific JS
     ------------------------------------------ */
  /* ------------------------------------------
     RepPortal.Contacts — the "+ Person" contact model (Ryan 2026-08-25)
     ------------------------------------------
     ⛔ TWIN OF `src/lib/contacts.js` IN THE rfq-worker. Behaviour must stay
     identical; `tests/contacts-theme-parity.test.mjs` in the worker repo extracts
     the block between the CONTACTS-TWIN markers below and runs the worker's own
     assertions against it, so a drift fails a test rather than a customer email.
     That guard exists because two chip implementations already drifted once and
     the lossy one shipped (2026-08-25 PM⁵, the red-pill bug).

     THE RULE (Ryan): the first contact is the primary and gets the To; the 2nd+
     are secondary and get a CC.

     `customer.contacts[]` holds the ADDITIONAL people only — the primary stays in
     customer.name/.title/.email/.phone. Every field is optional. `customer.email`
     stays the derived comma-joined mirror of every address, which is what keeps
     every existing consumer working untouched. When `contacts` is absent, tokens
     2..n of customer.email ARE the secondaries, so legacy deals backfill on read.
     ------------------------------------------ */
  /* CONTACTS-TWIN:BEGIN */
  var MAX_CONTACTS = 10;
  var _CT_CAP = { name: 200, title: 200, email: 200, phone: 30 };

  function splitAddrs(v) {
    return String(v == null ? '' : v).split(/[,;\s]+/)
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
  }

  function primaryEmail(customer) {
    return splitAddrs(customer && customer.email)[0] || '';
  }

  function _ctClamp(v, n) {
    return String(v == null ? '' : v).trim().slice(0, n);
  }

  function normalizeContact(c) {
    var o = c || {};
    return {
      name: _ctClamp(o.name, _CT_CAP.name),
      title: _ctClamp(o.title, _CT_CAP.title),
      email: _ctClamp(o.email, _CT_CAP.email).toLowerCase(),
      phone: _ctClamp(o.phone, _CT_CAP.phone),
      role: o.role === 'to' ? 'to' : 'cc'
    };
  }

  function isEmptyContact(c) {
    var n = normalizeContact(c);
    return !n.name && !n.title && !n.email && !n.phone;
  }

  function boundedContacts(raw) {
    return (Array.isArray(raw) ? raw : [])
      .slice(0, MAX_CONTACTS * 4)
      .map(normalizeContact)
      .filter(function (c) { return !isEmptyContact(c); })
      .slice(0, MAX_CONTACTS);
  }

  function readContacts(customer) {
    if (!customer || typeof customer !== 'object') return [];
    var p = normalizeContact({
      name: customer.name, title: customer.title,
      email: primaryEmail(customer), phone: customer.phone, role: 'to'
    });
    p.isPrimary = true;
    var secondaries;
    if (Array.isArray(customer.contacts) && customer.contacts.length) {
      secondaries = customer.contacts.slice(0, MAX_CONTACTS).map(normalizeContact);
    } else {
      secondaries = splitAddrs(customer.email).slice(1, MAX_CONTACTS + 1)
        .map(function (email) { return normalizeContact({ email: email }); });
    }
    return [p].concat(secondaries
      .filter(function (c) { return !isEmptyContact(c); })
      .map(function (c) {
        return { name: c.name, title: c.title, email: c.email, phone: c.phone, role: c.role, isPrimary: false };
      }));
  }

  function contactEmails(customer) {
    var seen = {}, out = [];
    readContacts(customer).forEach(function (c) {
      var k = c.email.toLowerCase();
      if (!k || seen[k]) return;
      seen[k] = 1; out.push(c.email);
    });
    return out;
  }

  function recipients(customer, extraCc) {
    var all = contactEmails(customer);
    var to = all[0] || '';
    var seen = {}, cc = [];
    if (to) seen[to.toLowerCase()] = 1;
    var push = function (a) {
      var k = String(a == null ? '' : a).trim().toLowerCase();
      if (!k || seen[k]) return;
      seen[k] = 1; cc.push(String(a).trim());
    };
    all.slice(1).forEach(push);
    (Array.isArray(extraCc) ? extraCc : splitAddrs(extraCc)).forEach(push);
    return { to: to, cc: cc };
  }

  /* CUSTOM_DEALPEOPLE_BY_CLAUDE (Ryan 2026-10-27) - To / CC per person. Twins of dealAudience,
     firstNameOfContact, multiToGreeting, setContactName and parseAddressList in the worker. */
  function dealAudience(customer, opts) {
    opts = opts || {};
    var seen = {}, to = [], cc = [];
    var add = function (list, name, email) {
      var e = _ctClamp(email, _CT_CAP.email).toLowerCase();
      if (!e || seen[e]) return;
      seen[e] = 1;
      list.push({ name: _ctClamp(name, _CT_CAP.name), email: e });
    };
    var base = Array.isArray(opts.base) ? opts.base : splitAddrs(opts.base);
    var people = readContacts(customer);
    var nameFor = function (e) {
      var k = String(e || '').trim().toLowerCase();
      for (var i = 0; i < people.length; i++) if (people[i].email && people[i].email === k) return people[i].name;
      return '';
    };
    base.forEach(function (e) { add(to, nameFor(e), e); });
    people.forEach(function (c) { if (c.role === 'to' && !(c.isPrimary && base.length)) add(to, c.name, c.email); });
    people.forEach(function (c) { if (c.role !== 'to') add(cc, c.name, c.email); });
    if (!to.length && cc.length) to.push(cc.shift());
    return { to: to, cc: cc };
  }

  function firstNameOfContact(name) {
    return _ctClamp(name, _CT_CAP.name).split(/\s+/)[0] || '';
  }

  function multiToGreeting(toPeople) {
    var to = Array.isArray(toPeople) ? toPeople : [];
    if (to.length < 2) return '';
    if (to.length >= 3) return 'everyone';
    var names = to.map(function (p) { return firstNameOfContact(p && p.name); }).filter(Boolean);
    return names.length === 2 ? names[0] + ' and ' + names[1] : (names[0] || '');
  }

  function setContactName(customer, email, name) {
    if (!customer || typeof customer !== 'object') return false;
    var e = String(email == null ? '' : email).trim().toLowerCase();
    var n = _ctClamp(name, _CT_CAP.name);
    if (!e || !n) return false;
    if (primaryEmail(customer).toLowerCase() === e) {
      if (_ctClamp(customer.name, _CT_CAP.name)) return false;
      customer.name = n;
      return true;
    }
    if (!Array.isArray(customer.contacts)) return false;
    var hit = null;
    for (var i = 0; i < customer.contacts.length; i++) {
      var c = customer.contacts[i];
      if (c && String(c.email || '').trim().toLowerCase() === e) { hit = c; break; }
    }
    if (!hit || _ctClamp(hit.name, _CT_CAP.name)) return false;
    hit.name = n;
    return true;
  }

  function parseAddressList(text) {
    var s = String(text == null ? '' : text);
    var out = [];
    var rx = /(?:"([^"]*)"|([^,;<>"]*?))\s*<([^<>\s]+@[^<>\s]+)>|([^\s,;<>"]+@[^\s,;<>"]+)/g;
    var m;
    while ((m = rx.exec(s))) {
      if (m[3]) {
        var raw = (m[1] != null ? m[1] : m[2] || '').trim();
        var name = /,/.test(raw) && m[1] != null ? raw.split(',').map(function (t) { return t.trim(); }).reverse().join(' ') : raw;
        out.push({ name: _ctClamp(name, _CT_CAP.name), email: _ctClamp(m[3], _CT_CAP.email).toLowerCase() });
      } else {
        out.push({ name: '', email: _ctClamp(m[4], _CT_CAP.email).toLowerCase() });
      }
    }
    return out;
  }

  function emailMirror(contacts) {
    var seen = {}, out = [];
    (contacts || []).forEach(function (c) {
      var e = _ctClamp(c && c.email, _CT_CAP.email).toLowerCase();
      if (!e || seen[e]) return;
      seen[e] = 1; out.push(e);
    });
    return out.join(', ');
  }

  function writeContacts(customer, primary, secondaries) {
    var cust = customer || {};
    var p = normalizeContact(primary);
    var rest = boundedContacts(secondaries);
    cust.name = p.name;
    cust.phone = p.phone;
    if (p.title || cust.title !== undefined) cust.title = p.title;
    if (rest.length) cust.contacts = rest; else delete cust.contacts;
    cust.email = emailMirror([p].concat(rest));
    return cust;
  }

  function applyContactList(customer, list) {
    var rows = Array.isArray(list) ? list : [];
    return writeContacts(customer || {}, rows[0], rows.slice(1));
  }

  function attachContacts(customer, rawContacts) {
    if (!customer || typeof customer !== 'object') return customer;
    /* Absent = "not mentioned", and must not clear. An explicit [] = the rep
       removed the last one, and must. */
    if (rawContacts == null) return customer;
    var rest = boundedContacts(rawContacts);
    var p = normalizeContact({
      name: customer.name, title: customer.title,
      email: primaryEmail(customer), phone: customer.phone
    });
    if (rest.length) customer.contacts = rest; else delete customer.contacts;
    customer.email = emailMirror([p].concat(rest));
    return customer;
  }

  var RepContacts = {
    MAX_CONTACTS: MAX_CONTACTS,
    splitAddrs: splitAddrs,
    primaryEmail: primaryEmail,
    normalizeContact: normalizeContact,
    isEmptyContact: isEmptyContact,
    readContacts: readContacts,
    contactEmails: contactEmails,
    recipients: recipients,
    dealAudience: dealAudience,
    firstNameOfContact: firstNameOfContact,
    multiToGreeting: multiToGreeting,
    setContactName: setContactName,
    parseAddressList: parseAddressList,
    emailMirror: emailMirror,
    writeContacts: writeContacts,
    applyContactList: applyContactList,
    attachContacts: attachContacts
  };
  /* CONTACTS-TWIN:END */

  /* ── Backdrop dismissal that survives a drag ──────────────────────────────────────────────────
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — every modal in the portal closes on a backdrop
     click, and every one of them was written as `if (e.target === backdrop) close()`. That is wrong
     for a gesture that STARTS inside the dialog: a `click` is dispatched on the nearest common
     ancestor of where the pointer went down and where it came up, so pressing inside the modal,
     dragging past its edge and releasing on the backdrop fires a click whose target IS the
     backdrop. The modal closes.

     Ryan hits this selecting a quantity in the Adjust Order panel — drag across the number, slip
     outside the panel, release, and the whole half-built adjustment is gone. Text selection makes
     it routine rather than rare, because dragging past the edge is how you select to the end of a
     field.

     The fix is to require BOTH ends of the gesture to be on the backdrop. `pointerdown` is recorded
     once, globally, in the CAPTURE phase so nothing can stop it from reaching us, and it covers
     mouse, pen and touch in one listener (Ryan's laptop is a touchscreen).

     ⚠️ Deliberately NOT a global click suppressor. Retargeting to a common ancestor is ordinary DOM
     behaviour — a button whose label is a <span> relies on it — so blanket-cancelling every click
     whose target differs from the pointerdown target would break real clicks. This only ever
     answers the narrow question a backdrop asks: "did this whole gesture happen on me?" */
  var _rpGestureStart = null;
  document.addEventListener('pointerdown', function (e) { _rpGestureStart = e.target; }, true);
  /* A cancelled gesture (the browser taking over for a scroll or a system drag) must not leave a
     stale start element behind for the next click to match against. */
  document.addEventListener('pointercancel', function () { _rpGestureStart = null; }, true);

  /**
   * Did this click both start AND end on `el` itself, rather than being dragged out onto it?
   * @param e  the click event
   * @param el the backdrop / overlay element
   */
  function dismissedOnBackdrop(e, el) {
    if (!e || !el || e.target !== el) return false;
    /* No recorded start means no pointerdown was seen — a keyboard-activated click, or a synthetic
       one. Those never originate inside the dialog, so treat them as a genuine backdrop hit and
       keep the pre-2026-08-25 behaviour. */
    if (_rpGestureStart == null) return true;
    return _rpGestureStart === el;
  }

  /* CUSTOM_FREIGHTTYPE_BY_CLAUDE (Ryan 2026-08-27) — is this delivery going to a HOME?

     THE CASE THIS EXISTS FOR. RW-1339-A ships to a house. The rep wrote **"N/a - Residence"** into
     the company field precisely so that reads at a glance, and the freight request emailed the
     supplier a SHIP TO table saying **Type: Non-residence**.

     ⛔ THE OLD RULE WAS AN EXACT WHOLE-STRING MATCH AFTER STRIPPING PUNCTUATION. `"N/a - Residence"`
     lowercased, then `[^a-z0-9]` removed, is the single fused token **`naresidence`** — which is in
     no list, so it read as a real company name. Every value the rep might type to mean "this is a
     house" fails the same way the moment it contains more than one word: `n/a - residence`,
     `N/A (home)`, `no company - residential` all fuse into one unrecognisable string. Only the bare
     forms (`na`, `home`, `residence`) ever worked.

     ⛔ THIS IS THE SAME ROOT CAUSE AS THE 2026-08-26 `gatedOrg` BUG, in a second place. There, the
     identical placeholder became a live ACCOUNT KEY and cross-propagated ad credit between three
     unrelated households ($152.98 of candidate agency fee). The fix there was to tokenize and
     ignore one-character tokens; this had been sitting on the same landmine, answering a different
     question, and nothing connected the two.

     ⚠️ IT COSTS REAL MONEY THE OTHER WAY ROUND. This field tells the supplier's broker what to
     quote. Calling a house commercial gets a quote with no residential surcharge and no liftgate —
     and the re-bill lands AFTER we have already quoted the customer. That is precisely the
     surprise `handlers/address-classify.js` was built to stop.

     ⛔ THE TOKEN LIST IS RESIDENTIAL + PLACEHOLDER ONLY — deliberately NOT the worker's
     GENERIC_ORG_TOKENS, which looks superficially reusable and would be a disaster here. That set
     exists to answer "is this a distinctive ACCOUNT KEY" and contains `gym`, `fitness`, `ymca`,
     `school`, `college`, `club`. Borrowing it would classify the YMCA of Denver as somebody's
     house. Same-looking question, opposite list.

     THE RULE: drop one-character tokens (the `n` and `a` of `n/a`), then it is a residence only if
     NOTHING distinctive is left. Requiring EVERY surviving token to be residential is what keeps
     real businesses out — `Residence Inn` keeps `inn`, `Home Depot` keeps `depot`, `Private School`
     keeps `school`. It fails toward Non-residence for anything carrying a real word. */
  /* FREIGHT-DESTTYPE-TWIN:BEGIN
     CUSTOM_FREIGHTTWIN_BY_CLAUDE (Ryan 2026-09-03) — this label is printed in the freight email's
     SHIP TO card, so `src/lib/freight-request-inputs.js` in the rfq-worker carries a copy and
     tests/freight-compose-parity.test.mjs asserts the two agree. Both token lists are inside the
     span: they ARE the rule, and porting the method while retyping the words is how a residential
     surcharge goes missing on one surface and not the other. */
  var RESIDENTIAL_ORG_TOKENS = [
    // placeholders a rep types to mean "there is no company here"
    'na', 'none', 'nil', 'nan', 'unknown', 'tbd', 'no', 'not', 'applicable', 'blank', 'null',
    /* Generic org nouns, so "no company - residential" and "no business name" resolve. These are
       only safe BECAUSE every surviving token has to match: a real business name almost always
       carries a distinctive word alongside them ("Moving Company" keeps `moving`, "The Coffee
       Company" keeps `coffee` and `the`), so adding them here cannot tip a genuine company. */
    'company', 'business', 'org', 'organization', 'organisation', 'name',
    // words that positively say "a home"
    'residence', 'residences', 'residential', 'resident', 'home', 'homes', 'homeowner',
    'house', 'household', 'personal', 'private', 'individual', 'consumer', 'self',
    'apartment', 'apt', 'condo', 'townhouse', 'domestic',
  ];

  /* ⚠️ NEUTRAL, not residential and not distinctive — words this business sells to BOTH sides of.
     "Home Gym" is a house; "Gym" on its own is a customer we should quote commercially. Under a
     plain all-tokens-must-match rule those two cannot both be right: adding `gym` to the list above
     makes a bare "Gym" a residence, and leaving it out makes "Home Gym" commercial — which is the
     EXPENSIVE direction (no residential surcharge, no liftgate, re-billed after the customer was
     quoted). Treating them as neither lets the rest of the string decide. */
  var NEUTRAL_ORG_TOKENS = ['gym', 'gyms', 'fitness', 'studio', 'training'];

  /** @returns {'Residence'|'Non-residence'} — the label printed in the freight email's SHIP TO table. */
  function destinationTypeFromOrg(org) {
    var tokens = String(org == null ? '' : org)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .filter(function (t) { return t.length > 1; });
    if (!tokens.length) return 'Residence';          // no company at all
    var sawResidential = false;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (RESIDENTIAL_ORG_TOKENS.indexOf(t) !== -1) { sawResidential = true; continue; }
      if (NEUTRAL_ORG_TOKENS.indexOf(t) !== -1) continue;
      return 'Non-residence';                        // a real word — somebody's business
    }
    /* Only neutral words and nothing residential ("Gym", "Fitness Studio") is a business. */
    return sawResidential ? 'Residence' : 'Non-residence';
  }
  /* END CUSTOM_FREIGHTTYPE_BY_CLAUDE */
  /* What the parity test pulls out of this block — see the BEGIN marker. */
  var FreightDestTypeTwin = { destinationTypeFromOrg: destinationTypeFromOrg };
  /* FREIGHT-DESTTYPE-TWIN:END */

  window.RepPortal = {
    Auth: RepAuth,
    /* CUSTOM_FREIGHTTYPE_BY_CLAUDE (Ryan 2026-08-27) — shared because BOTH the Deal Dashboard's
       freight modal and the Quote Workstation print this Type, and they each had their own copy of
       the rule. Two answers to "is this a house" on one deal is how the QW and the DD can email a
       supplier different things about the same address. custom-rep-portal.js loads first (deferred,
       document order) on both pages, so it is reachable from either. */
    destinationTypeFromOrg: destinationTypeFromOrg,
    ChatDock: RepChatDock,
    ChatLearn: RepChatLearn,
    ChatAlert: RepChatAlert,
    API: RepAPI,
    Theme: RepTheme,
    Drawer: RepDrawer,
    formatPhone: formatPhone,
    bindPhoneInput: bindPhoneInput,
    checkPasswordStrength: checkPasswordStrength,
    installChipField: installChipField,
    Contacts: RepContacts,
    dismissedOnBackdrop: dismissedOnBackdrop
  };

  /* ------------------------------------------
     Fire init AFTER all const declarations (including RepDrawer) are
     initialized. See the note next to `function initAll()` above for why
     this lives at the bottom of the IIFE.
     ------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();

/* CUSTOM_PORTALHAPTICS_BY_CLAUDE (Ryan 2026-10-29) - a 50 ms tap of feedback on the portal's main menu on a
   phone: the ☰ that opens the left slide-out menu, and every page, heading and button inside that menu.
   The storefront header's recipe (custom-header-haptics.js): small touch screens only (a touchscreen laptop
   never buzzes), a click not a pointerdown (starting a scroll in the menu does not buzz), capture phase so a
   handler that stops the event still buzzes. iPhone browsers have no vibrate, so there it does nothing. */
(function () {
  if (!('vibrate' in navigator)) return;
  var mq = window.matchMedia('(max-width: 769px) and (pointer: coarse)');
  var SELECTOR = '.rep-portal-drawer-mobile-open, .rep-portal-drawer a, .rep-portal-drawer button, .rep-portal-drawer__item, .rep-portal-drawer__category-title';
  document.addEventListener('click', function (e) {
    if (!mq.matches || !e.target || !e.target.closest || !e.target.closest(SELECTOR)) return;
    try { navigator.vibrate(50); } catch (_) { /* no motor, no matter */ }
  }, true);
})();
